import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { flushSync } from "react-dom";
import Joyride, { Step, ACTIONS, EVENTS, CallBackProps } from "react-joyride";

import { Config } from "@eagerdog/config";
import {
  Constants,
  getJudgeEntryAvailability,
  getJudgeEntryLimitKey,
  getShowJudgesForEntryLimits,
  isCwagsFeoShowWide,
  isLotteryDrawOpen,
  isManualEntryHoldActive,
  showOffersDetective,
  getCkcScentDivision,
} from "@eagerdog/constants";
import { IStripePaymentMethod } from "@eagerdog/stripe";
import {
  IShow,
  IDog,
  IDogClass,
  IShowRunTimeSlot,
  EventDogShowsDto,
  ITransactionPaymentSummary,
  IUser,
  IShowElement,
  EventRegistrationCreateDto,
  IAddOn,
  EventAddOnDto,
  IAddress,
  ITransactionElementDetails,
  ITransactionShowDetails,
  IDogProposedChanges
} from "@eagerdog/interfaces";

import moment from "moment";
import momentTz from "moment-timezone";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { Helmet } from "react-helmet";
import { isPossiblePhoneNumber } from "react-phone-number-input";

import { apiService } from "src/services";
import { helperService } from "src/services/helper.service";
import { httpService } from "src/services/http.service";

import ShowTime from "./ShowTime/ShowTime";
import Checkout from "./Checkout/Checkout";
import { usePointsReminder } from "src/components/PointsReminder/usePointsReminder";
import { pointsReminderEnabled } from "src/components/PointsReminder/pointsReminder";
import UserAddress, { isValidAddress, isValidPhone, IAddressError } from "./UserAddress/UserAddress";

import Input from "src/components/Input/Input";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import Button from "src/components/Button/Button";
import DatePick from "src/components/DatePick/DatePick";
import Cart from "src/components/Cart/Cart";
import DogForm from "src/components/StepForm/DogForm/DogForm";
import FormBuilder from "src/components/FormBuilder/FormBuilder";
import MissingFieldsModal from "src/components/MissingFieldsModal/MissingFieldsModal";
import { RegistrationContext, RegistrationField, resolveMissingFields, resolveMissingFieldsForLevels } from "src/registration-requirements";
import { AKC_PREFERRED_OBEDIENCE_LEVELS } from "src/registration-requirements/levels";
import Checkbox from "src/components/Checkbox/Checkbox";
import { Modal, useModal } from "src/components/Modal/Modal";
import DogPortrait from "src/components/DogPortrait/DogPortrait";
import Confirm from "src/components/Confirm/Confirm";
import Phone from "src/components/Phone/Phone";

import { workingOrCourierOptions, getWorkingLegOptions } from "./utils/WaterDog";
import { dogWheelchairOptions } from "./utils/CwagsScent";

import { toast } from "src/components/Toast/ToastManager";
import MandateNotice from "src/components/MandateNotice/MandateNotice";
import AddUserPaymentMethod from "src/components/AddUserPaymentMethod";
import CardOnFile from "src/components/CardOnFile/CardOnFile";

import emailIcon from "src/assets/icons/email.svg";

import "./EventRegister.scss";

interface IProps {}

export interface IDogInfo extends IDog {
  start_minutes?: number,
  time_slot?: string,
  classes?: IDogClass[],
  plan_to_earn_title?: boolean,
  title?: string,
  asfa_first_trial_for_hound?: boolean,
  asfa_first_time_entry?: boolean,
  asfa_info_changed_since_last_trial?: boolean,
  asfa_info_changed_regarding?: string,
  asfa_dismissed_last_6_trials?: boolean,
  using_agent?: boolean,
  agent_name?: string,
  agent_number?: string,
  using_non_agent?: boolean,
  competing_owner_handler?: boolean,
  agent_phone?: string,
  agent_email?: string,
  agent_address?: IAddress,

  substitute_name?: string,
  substitute_email?: string,
  substitute_phone?: string,

  leg_info?: string,
  wheelchair_type?: string,

  needs_measurement?: boolean,

  splashes?: number
}

export interface IShowDogs extends IShow {
  dogs: IDogInfo[],
  registered_entries?: number,
  waitlist_count?: number,
  dogs_count?: number,
  current_mrv?: number,
  created_by?: string,
  stock_score_counts?: { section: string; count: number }[],
  stock_waitlist_counts?: { section: string; count: number }[],
  judge_entry_counts?: { judge_name?: string; judge_number?: string; manual: boolean; count: number }[],
  judge_waitlist_counts?: { judge_name?: string; judge_number?: string; count: number }[],
  detective_entry_counts?: { online: number; manual: number },
  club_id: string
}

type AgentPayload = {
  name?: string
  email?: string
  phone?: string
  number?: string
  address?: Partial<IAddress>
}

type SubstitutePayload = {
  name?: string
  email?: string
  phone?: string
}

export interface IAddonUI extends IAddOn {
  amount: number;
}

const EventRegister: React.FC<IProps> = (props) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  let { eid } = useParams();
  const { isShown, toggle } = useModal();
  const { isShown: isRandomDrawConfirmShown, toggle: toggleRandomDrawConfirm } = useModal();
  
  // Use ref for ban status to ensure it's immediately available in event handlers
  const isUserBannedRef = React.useRef<boolean>(false);

  // updatePaymentSummary fires a new registration-payment-summary request on every
  // `shows` change (e.g. each class selected/deselected). With several dogs/classes
  // being edited in quick succession, multiple requests can be in flight at once and
  // network timing does not guarantee they resolve in the order they were sent. This
  // counter lets us discard a response that arrives after a newer request has already
  // been fired, so a stale (smaller) total can never overwrite the current one.
  //
  // IMPORTANT: this is keyed per payment method (card / bank_account / ...). Card and
  // bank totals are fetched independently/concurrently (e.g. one silent, one not, when
  // the payment step loads, or when the user toggles payment method). A single shared
  // counter would mark whichever request was fired *first* as permanently stale the
  // instant the second one fires (its id is bumped synchronously before either can
  // resolve), so that method's total would never get cached — leaving its displayed
  // amount to silently fall back to whatever `paymentSummary.total_amount` happens to
  // be, which can belong to a *different* payment method and make both options show
  // the same (wrong) price.
  const paymentSummaryRequestIdRef = React.useRef<Record<string, number>>({});

  // Source of truth for WHICH transaction / PaymentIntent belongs to WHICH payment
  // method. Each method (card / bank_account) gets its own pending transaction and
  // PI from initRegistrationPaymentIntent; a payment-summary request for method M
  // must only ever carry M's ids. Reading `transactionId` / `paymentIntentId` from
  // React state inside updatePaymentSummary was the stale-closure bug that let a
  // bank_account-priced request be persisted onto the card transaction (and its
  // PI) — the root cause of the Sep 2026 club payout shortfalls/overages. A ref is
  // updated synchronously, so it is always current regardless of render timing.
  const methodIdsRef = React.useRef<
    Record<string, { transactionId?: string; paymentIntentId?: string }>
  >({});

  // Monotonic sequence sent with every summary request that targets a
  // transaction. The server refuses to persist a request whose sequence is
  // older than the last one applied, so out-of-order responses can never leave
  // the transaction / PI holding an older total.
  const summarySequenceRef = React.useRef<number>(0);

  // Always-current payment choice, for use inside async callbacks whose closure
  // may have captured an older `paymentChoice`. Kept in sync below.
  const paymentChoiceRef = React.useRef<string>('card');

  const user:IUser = helperService.getUser();

  const steps:string[] = ["Select Dogs", "Dog Details", "Sanctioning Club Waiver", "Event Waiver", "Add-ons", "Payment"];

  const [joyrideSteps, setJoyrideSteps] = useState<Step[]>([]);
  const [joyrideRun, setJoyrideRun] = useState<boolean>(false);
  const [joyrideStepIndex, setJoyrideStepIndex] = useState<number>(0);

  const [onBehalf, setOnBehalf] = useState<string>();
  const [onBehalfEmail, setOnBehalfEmail] = useState<string>();
  const [onBehalfJunior, setOnBehalfJunior] = useState<string>();
  const [isClaimed, setIsClaimed] = useState<boolean>(false);

  const [loadingBehalf, setLoadingBehalf] = useState<boolean>(false);

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [shows, setShows] = useState<IShowDogs[]>([]);
  const [event, setEvent] = useState<any>();
  const lotteryDrawOpen = isLotteryDrawOpen(event);
  const [currentShow, setCurrentShow] = useState<number>(-1);
  const [currentShowTime, setCurrentShowTime] = useState<number>(0);
  const [expandedShowTypes, setExpandedShowTypes] = useState<Set<number>>(new Set());
  const [expandedShowTimes, setExpandedShowTimes] = useState<Set<number>>(new Set());
  const [showTypes, setShowTypes] = useState<string[]>([]);
  const orderedShowTypes = useMemo(
    () => [...showTypes].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
    [showTypes]
  );
  const [dogs, setDogs] = useState<IDog[]>([]);
  const freshDogRef = useRef<IDog | undefined>(undefined);
  const selectDogRef = useRef<(showIndex: number, dog: IDogInfo) => boolean | void>(() => false);
  const [missingFieldsRequest, setMissingFieldsRequest] = useState<{
    context: RegistrationContext;
    fields: RegistrationField[];
    levels?: string[];
    onSaved: (dog: IDog) => void;
  } | null>(null);
  const [agreeToWaiver, setAgreeToWaiver] = useState<boolean>(false);
  const [agreeToEventWaiver, setAgreeToEventWaiver] = useState<boolean>(false);
  const [regPayload, setRegPayload] = useState<EventDogShowsDto[]>();
  
  const [isVolunteer, setIsVolunteer] = useState<boolean>(false);
  const [volunteerLateRegAllowed, setVolunteerLateRegAllowed] = useState<boolean>(false);
  const [volunteerEarlyRegAllowed, setVolunteerEarlyRegAllowed] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(user.user_type === Constants.user_type.club_manager); // Whether current user is admin/manager for event's club (initialized for club_managers, refined in useEffect for owners)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_isUserBanned, setIsUserBanned] = useState<boolean>(false); // State kept for potential future use, ref is used for immediate access
  
  const [showStepHint, setShowStepHint] = useState<boolean>(false);

  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null>>();
  const [stripeOptions, setStripeOptions] = useState<{ clientSecret: string }>();

  const [paymentIntentId, setPaymentIntentId] = useState<string>();
  const [transactionId, setTransactionId] = useState<string>();
  const [paymentChoice, setPaymentChoice] = useState<'card' | 'bank_account' | 'pay_club_directly'>('card');
  useEffect(() => { paymentChoiceRef.current = paymentChoice; }, [paymentChoice]);
  const [isPayClubDirectlyTransaction, setIsPayClubDirectlyTransaction] = useState<boolean>(false);
  const [payClubDirectlyLoading, setPayClubDirectlyLoading] = useState<boolean>(false);
  // Saved Stripe state so we can restore it when switching back to Credit Card
  const [savedStripeOptions, setSavedStripeOptions] = useState<{ clientSecret: string } | undefined>();
  const [savedTransactionId, setSavedTransactionId] = useState<string | undefined>();
  const [savedPaymentIntentId, setSavedPaymentIntentId] = useState<string | undefined>();
  // Cache for bank account transaction/PI so switching back doesn't create a new transaction
  const [savedBankStripeOptions, setSavedBankStripeOptions] = useState<{ clientSecret: string } | undefined>();
  const [savedBankTransactionId, setSavedBankTransactionId] = useState<string | undefined>();

  const [stripeCustomerId, setStripeCustomerId] = useState<string | undefined>(user.stripe_customer_id);
  const [cardOnFileMethods, setCardOnFileMethods] = useState<IStripePaymentMethod[] | null>(null);
  const standaloneStripePromise = useMemo(() => loadStripe(Config.web.REACT_APP_STRIPE_PUBLIC_KEY ?? ""), []);

  const [modalContent, setModalContent] = useState<string>("");
  const [twoDogMsg, setTwoDogsMsg] = useState<string>("");
  
  // UKC Conformation: Track if user wants to use same selection for all shows (joined behavior)
  const [useJoinedConformation, setUseJoinedConformation] = useState<Record<string, boolean>>({});

  // Helper function to determine if we're on the payment step
  const isPaymentStep = useCallback(() => {
    // Payment step is always step 5, whether add-ons/waiver are skipped or not
    return currentStep === 5;
  }, [currentStep]);

  // Helper function to build breadcrumb URL preserving admin context
  const getBreadcrumbUrl = useCallback(() => {
    const adminParam = searchParams.get('admin');
    const baseUrl = `/e/${eid}`;
    
    if (adminParam) {
      return `${baseUrl}?admin=${adminParam}`;
    }
    
    return baseUrl;
  }, [eid, searchParams]);

  // Helper function to construct redirect URLs with admin context
  const getRedirectUrl = useCallback((baseUrl: string, additionalParams: string = '') => {
    const adminParam = searchParams.get('admin');
    const separator = baseUrl.includes('?') ? '&' : '?';
    let url = baseUrl;
    
    if (additionalParams) {
      url += separator + additionalParams;
    }
    
    if (adminParam) {
      const paramSeparator = url.includes('?') ? '&' : '?';
      url += paramSeparator + `admin=${adminParam}`;
    }
    
    return url;
  }, [searchParams]);

  const [addons, setAddons] = useState<IAddonUI[]>([]);

  // NOTE: for AKC Team / Brace / Pairs levels purposes (extra dogs picker)
  const [usingTeamDogs, setUsingTeamDogs] = useState<boolean>(false);

  const [dogDetailsError, setDogDetailsError] = useState<string>();

  const defaultPaymentSummary:ITransactionPaymentSummary = useMemo(() => {
    return {
      account_number: "",
      base_amount: 0,
      discount: 0,
      currency: "USD",
      calculated_application_fee: 0,
      calculated_processing_fee: 0,
      calculated_processing_tax: 0,
      fees: 0,
      total_amount: 0,
      details: []
    };
  }, []);

  const [userAddress, setUserAddress] = useState<IAddress>();
  const [userPhone, setUserPhone] = useState<string>();

  const isPayClubDirectlyEligible = useMemo(() => {
    return (
      event?.sanctioning_club === Constants.sanctioning_club.UKC &&
      (event?.club as any)?.has_pay_club_directly_access === true &&
      shows.some((s) => s.pay_club_directly_enabled === true) &&
      onBehalf === undefined &&
      onBehalfEmail === undefined
    );
  }, [event, shows, onBehalf, onBehalfEmail]);

  // Compute the effective user for checkout - use dog owner_info if available
  const effectiveCheckoutUser = useMemo(() => {
    // If any selected dog in shows has owner_info, use that instead of user info
    let dogWithOwnerInfo: IDogInfo | undefined;
    for (const show of shows) {
      dogWithOwnerInfo = show.dogs.find(d => d.owner_info);
      if (dogWithOwnerInfo) break;
    }
    
    if (dogWithOwnerInfo?.owner_info) {
      const ownerInfo = dogWithOwnerInfo.owner_info;
      // Create a user-like object from dog owner_info
      return {
        ...user,
        first_name: ownerInfo.first_name,
        last_name: ownerInfo.last_name,
        display_name: `${ownerInfo.first_name} ${ownerInfo.last_name}`,
        address: ownerInfo.address,
        phone: ownerInfo.phone,
        email: ownerInfo.email,
      };
    }
    return user;
  }, [shows, user]);

  const [paymentSummary, setPaymentSummary] = useState<ITransactionPaymentSummary>(defaultPaymentSummary);
  const [previewPaymentSummary, setPreviewPaymentSummary] = useState<ITransactionPaymentSummary | null>(null);
  const [perMethodTotals, setPerMethodTotals] = useState<Record<string, number>>({});
  const [currentDiscountCode, setCurrentDiscountCode] = useState<string | undefined>(undefined);
  const [waitlistsPaymentElements, setWaitlistsPaymentElements] = useState<ITransactionElementDetails[]>([]);

  // Price override state — club manager / on-behalf only
  const [feeOverrides, setFeeOverrides] = useState<Record<string, number>>({});
  const [originalPrices, setOriginalPrices] = useState<Record<string, number>>({});
  const [overrideNote, setOverrideNote] = useState<string>("");

  //const [clubPaymentMethods, setClubPaymentMethods] = useState<IRadioListItem[]>([]);
  const [cardToDelete, setCardToDelete] = useState<any>();
  //const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<number>(0);

  // DO - this will change...
  const onBehalfPaymentMethods: IOption[] = [
    { value: "Cheque", id: Constants.payment_method_type.cheque },
    { value: "Cash", id: Constants.payment_method_type.cash },
    { value: "E-Transfer", id: Constants.payment_method_type.etransfer },
    { value: "Paypal", id: Constants.payment_method_type.paypal },
    { value: "Other", id: Constants.payment_method_type.other }
  ];

  // Helper to check if dog has legacy registration data only
  const hasLegacyRegistrationOnly = (dog: IDog, sanctioningClub: string) => {
    // Check if they have new club registration for this sanctioning club
    const hasNewRegistration = dog.club_registrations?.some(cr => 
      cr.sanctioning_club === sanctioningClub && cr.registration_number && cr.registration_type
    );
    
    if (hasNewRegistration) return false;
    
    // Check if they have legacy registration data
    const hasLegacyData = dog.registration_numbers?.some(rn => {
      if (sanctioningClub === Constants.sanctioning_club.PWDCA) {
        return rn.name === Constants.sanctioning_club.CKC || rn.name === Constants.sanctioning_club.AKC;
      }
      return rn.name === sanctioningClub;
    });
    
    return hasLegacyData;
  };

  // Helper to check if any dogs need migration for this event
  const hasAnyLegacyDogs = () => {
    if (!event) return false;
    return dogs.some(dog => hasLegacyRegistrationOnly(dog, event.sanctioning_club));
  };

  // Component for top-level migration banner
  const RegistrationUpdateBanner: React.FC = () => (
    <div className="registrationUpdateBanner">
      <div className="bannerContent">
        <div className="bannerIcon">⚠️</div>
        <div className="bannerText">
          <h4>Registration Information Update Required</h4>
          <p>
            We've improved how dog registration information is managed. 
            Some of your dogs need their registration details updated before they can be registered for this event.
            Look for the <span className="indicatorExample">⚠️</span> indicator and click "Edit" to update their information.
          </p>
        </div>
      </div>
    </div>
  );

  const akcTitles: IOption[] = [
    { value: "FCAT", id: Constants.akc_fastcat_titles.fcat},
    { value: "DCAT - 500 pts", id: Constants.akc_fastcat_titles.dcat},
    { value: "BCAT - 150 pts", id: Constants.akc_fastcat_titles.bcat},
  ];

  const akcCatTitles: IOption[] = [
    { value: Constants.akc_cat_titles.ca, id: Constants.akc_cat_titles.ca },
    { value: Constants.akc_cat_titles.caa, id: Constants.akc_cat_titles.caa },
    { value: Constants.akc_cat_titles.cax, id: Constants.akc_cat_titles.cax },
    { value: Constants.akc_cat_titles.cax2, id: Constants.akc_cat_titles.cax2 },
  ];

  const iswsFastDashTitles: IOption[] = [
    { value: "FAST - 1000 pts", id: Constants.isws_fast_dash_titles.fast},
    { value: "IFAST - 500 pts", id: Constants.isws_fast_dash_titles.ifast},
    { value: "BFAST - 150 pts", id: Constants.isws_fast_dash_titles.bfast},
  ];

  const [onBehalfPaymentMethod, setOnBehalfPaymentMethod] = useState<IOption>({ value: "Cheque", id: Constants.payment_method_type.cheque });
  const [chequeNumber, setChequeNumber] = useState<string | undefined>(undefined);

  // Add state to trigger timeslot refresh
  const [timeslotRefreshTrigger, setTimeslotRefreshTrigger] = useState<number>(0);

  // Track which shows have timeslots configured (show_id → slot count), populated by FormBuilder callbacks
  const [showTimeslotMap, setShowTimeslotMap] = useState<Record<string, number>>({});
  // Full timeslot data per show — used for per-class level validation (OSNG / Rally)
  const [showTimeslotsData, setShowTimeslotsData] = useState<Record<string, IShowRunTimeSlot[]>>({});
  
  // Add state to trigger FormBuilder refresh when dog is edited
  const [dogUpdateTrigger, setDogUpdateTrigger] = useState<number>(0);

  // Date Received for offline admin entries (defaults to current date)
  const [dateReceived, setDateReceived] = useState<Date>(new Date());

  const getDogs = useCallback((id?: string, clubId?: string) => {
    apiService.getUserDogs(id ? id : user._id).then((dogResponse) => {
      if (clubId) {
        apiService.getUserDogs(id ? id : user._id, clubId).then((clubResponse) => {
          setDogs([...dogResponse, ...clubResponse]);
        });
      } else {
        if (dogResponse.length > 0) {
          setDogs(dogResponse);
        }
      }
    });
  }, [user._id]);

  // Helper function to get club-specific registration information
  const getClubRegistrationInfo = (dog: IDog, sanctioningClub: string, existingChanges?: IDogProposedChanges) => {
    // Proposed changes take priority — check them first
    if (existingChanges && existingChanges.club_registrations?.length) {
      const clubReg = existingChanges.club_registrations.find((reg) => reg.new.sanctioning_club === sanctioningClub)?.new;
      if (clubReg) {
        // For any field that is empty in the proposed change, fall back to the current dog value
        const currentClubReg = dog.club_registrations?.find((r) => r.sanctioning_club === sanctioningClub);
        return {
          breed: clubReg.breed || currentClubReg?.breed || "",
          variety: clubReg.variety || currentClubReg?.variety || "",
          variation: clubReg.variation || currentClubReg?.variation || "",
          call_name: clubReg.call_name || currentClubReg?.call_name || "",
          registered_name: clubReg.registered_name || currentClubReg?.registered_name || "",
          prefix_title: clubReg.prefix_title || currentClubReg?.prefix_title || "",
          suffix_title: clubReg.suffix_title || currentClubReg?.suffix_title || "",
          registration_type: clubReg.registration_type || currentClubReg?.registration_type || "",
          registration_number: clubReg.registration_number || currentClubReg?.registration_number || "",
          needs_measurement: clubReg.needs_measurement ?? currentClubReg?.needs_measurement ?? undefined,
          akc_obedience_jump_height: clubReg.akc_obedience_jump_height ?? currentClubReg?.akc_obedience_jump_height ?? undefined,
          akc_obedience_preferred_jump_height: clubReg.akc_obedience_preferred_jump_height ?? currentClubReg?.akc_obedience_preferred_jump_height ?? undefined,
          akc_rally_jump_height: clubReg.akc_rally_jump_height ?? currentClubReg?.akc_rally_jump_height ?? undefined,
          ckc_obedience_jump_height: clubReg.ckc_obedience_jump_height ?? currentClubReg?.ckc_obedience_jump_height ?? undefined,
          ckc_rally_jump_height: clubReg.ckc_rally_jump_height ?? currentClubReg?.ckc_rally_jump_height ?? undefined,
        };
      }
    }

    // Check current club_registrations
    if (dog.club_registrations?.length) {
      const clubReg = dog.club_registrations.find((reg) => reg.sanctioning_club === sanctioningClub);
      if (clubReg) {
        return {
          breed: clubReg.breed || "",
          variety: clubReg.variety || "",
          variation: clubReg.variation || "",
          call_name: clubReg.call_name || "",
          registered_name: clubReg.registered_name || "",
          prefix_title: clubReg.prefix_title || "",
          suffix_title: clubReg.suffix_title || "",
          registration_type: clubReg.registration_type || "",
          registration_number: clubReg.registration_number || "",
          needs_measurement: clubReg.needs_measurement ?? undefined,
          akc_obedience_jump_height: clubReg.akc_obedience_jump_height ?? undefined,
          akc_obedience_preferred_jump_height: clubReg.akc_obedience_preferred_jump_height ?? undefined,
          akc_rally_jump_height: clubReg.akc_rally_jump_height ?? undefined,
          ckc_obedience_jump_height: clubReg.ckc_obedience_jump_height ?? undefined,
          ckc_rally_jump_height: clubReg.ckc_rally_jump_height ?? undefined,
        };
      }
    }

    // Fall back to legacy top-level fields, applying any proposed changes to those fields too
    const effectiveBreed = existingChanges?.breed?.new || dog.breed || "";
    const effectiveVariety = existingChanges?.variety?.new || dog.variety || "";
    const effectiveCallName = existingChanges?.call_name?.new || dog.call_name || "";
    const effectiveRegisteredName = existingChanges?.registered_name?.new || dog.registered_name || "";
    const effectivePrefixTitle = existingChanges?.prefix_title?.new || dog.prefix_title || "";
    const effectiveSuffixTitle = existingChanges?.suffix_title?.new || dog.suffix_title || "";

    return {
      breed: effectiveBreed,
      variety: effectiveVariety,
      variation: "",
      call_name: effectiveCallName,
      registered_name: effectiveRegisteredName,
      prefix_title: effectivePrefixTitle,
      suffix_title: effectiveSuffixTitle,
      registration_type: "",
      registration_number: "",
      akc_obedience_jump_height: undefined,
      akc_obedience_preferred_jump_height: undefined,
      akc_rally_jump_height: undefined,
      ckc_obedience_jump_height: undefined,
      ckc_rally_jump_height: undefined,
    };
  };

  const getExistingChangesForEvent = (
    dog: IDog,
    eventId?: string
  ): IDogProposedChanges | undefined => {
    if (!eventId) {
      return undefined;
    }

    return (dog.proposed_changes || []).find(
      (pc) => pc?.event_id?.toString() === eventId.toString()
    );
  };

  const renderDogInfoByShowType = (
    showType: string,
    dog: IDogInfo,
    clubRegInfo: any,
    existingChanges?: IDogProposedChanges
  ) => {
    if (showType === Constants.show_type.conformation) {
      return (
        <>
          <div className="jumpHeight">Breed: <span>{clubRegInfo?.breed || "Not set"}</span></div>
          <div className="jumpHeight">Breeder: <span>{existingChanges?.breeder?.new || dog?.breeder || "Not set"}</span></div>
          <div className="jumpHeight">Sire: <span>{existingChanges?.sire?.new || dog?.sire || "Not set"}</span></div>
          <div className="jumpHeight">Dam: <span>{existingChanges?.dam?.new || dog?.dam || "Not set"}</span></div>
        </>
      );
    }

    if (event?.sanctioning_club === Constants.sanctioning_club.CKC && showType === Constants.show_type.obedience) {
      return (
        <>
          <div className="jumpHeight">Breed: <span>{clubRegInfo?.breed || "Not set"}</span></div>
          <div className="jumpHeight">Obedience Jump Height: <span>{clubRegInfo?.ckc_obedience_jump_height ? `${clubRegInfo.ckc_obedience_jump_height}"` : "Not set"}</span></div>
        </>
      );
    }

    if (event?.sanctioning_club === Constants.sanctioning_club.CKC && showType === Constants.show_type.rally_obedience) {
      return (
        <>
          <div className="jumpHeight">Breed: <span>{clubRegInfo?.breed || "Not set"}</span></div>
          <div className="jumpHeight">Rally Jump Height: <span>{clubRegInfo?.ckc_rally_jump_height ? `${clubRegInfo.ckc_rally_jump_height}"` : "Not set"}</span></div>
        </>
      );
    }

    if (event?.sanctioning_club === Constants.sanctioning_club.CKC && showType === Constants.show_type.scent_detection) {
      const heightAtWithers = existingChanges?.height_at_withers?.new ?? dog.height_at_withers;
      const division = getCkcScentDivision(heightAtWithers);
      return (
        <>
          <div className="jumpHeight">Breed: <span>{clubRegInfo?.breed || "Not set"}</span></div>
          <div className="jumpHeight">Call Name: <span>{clubRegInfo?.call_name || dog.call_name || "Not set"}</span></div>
          <div className="jumpHeight">Height at Withers: <span>{heightAtWithers ? `${heightAtWithers}" (${division})` : "Not set"}</span></div>
        </>
      );
    }

    if (showType === Constants.show_type.scentwork) {
      return (
        <>
          <div className="jumpHeight">DOB: <span>{dog.dob ? new Date(dog.dob).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Not set"}</span></div>
          <div className="jumpHeight">Breed: <span>{dog.breed || "Not set"}</span></div>
        </>
      );
    }

    return (
      <>
        <div className="jumpHeight">Height at Withers: <span>{(existingChanges?.height_at_withers?.new ?? dog.height_at_withers) ? `${existingChanges?.height_at_withers?.new ?? dog.height_at_withers}"` : "Not set"}</span></div>
        <div className="jumpHeight">Obedience Jump Height: <span>{clubRegInfo.akc_obedience_jump_height ? `${clubRegInfo.akc_obedience_jump_height}"` : "Not set"}</span></div>
        <div className="jumpHeight">Obedience Preferred Jump Height: <span>{clubRegInfo.akc_obedience_preferred_jump_height ? `${clubRegInfo.akc_obedience_preferred_jump_height}"` : "Not set"}</span></div>
        <div className="jumpHeight">Rally Jump Height: <span>{clubRegInfo.akc_rally_jump_height ? `${clubRegInfo.akc_rally_jump_height}"` : "Not set"}</span></div>
      </>
    );
  };

  // Helper function to get legacy registration info for messaging
  // const getLegacyRegistrationInfo = (dog: IDog, sanctioningClub: string) => {
  //   if (sanctioningClub === Constants.sanctioning_club.PWDCA) {
  //     const akcReg = dog.registration_numbers?.find(rn => rn.name === Constants.sanctioning_club.AKC);
  //     const ckcReg = dog.registration_numbers?.find(rn => rn.name === Constants.sanctioning_club.CKC);
  //     return akcReg || ckcReg;
  //   }
  //   return dog.registration_numbers?.find(rn => rn.name === sanctioningClub);
  // };

  const clubPointsRef = useRef(0);
  const clubPointsLoadedRef = useRef(false);
  const chargeResultRef = useRef<{ charged: number; pointsUsed: number; pointsRemaining?: number; currency: string }>({ charged: 0, pointsUsed: 0, currency: "USD" });

  const updatePaymentSummary = useCallback((discount?: string, addons?: IAddonUI[], pointsToRedeem?: number, overridePaymentMethodType?: string, silent?: boolean) => {
    // Store the discount code in state
    setCurrentDiscountCode(discount);

    let _shows:IShowDogs[] = JSON.parse(JSON.stringify(shows));
    let hasClasses:boolean = false;

    /*const getDockJumpingClasses = (_classes: any) => {
      let _dockJumpingClasses = JSON.parse(JSON.stringify(_classes));

      for (let c in _dockJumpingClasses) {
        _dockJumpingClasses[c].show_element = _dockJumpingClasses[c].level;
        delete _dockJumpingClasses[c].level;
      }

      return _dockJumpingClasses;
    }*/

    setDogDetailsError(undefined);

    let _payload:EventDogShowsDto[] = [];

    if (_shows.length > 0) {
      for (let s in _shows) {
        if (_shows[s].dogs.length > 0) {
          let _showDogs:any = {
            show_id: _shows[s]._id,
            dogs: []
          }

          // TODO: temp hotfix; level value override for ABI Conformation: "Altered Conformation" -> "Altered" to pass BE DogClass schema enum validation
          for (let d in _shows[s].dogs) {
            const dogClasses = _shows[s].dogs[d].classes || []; // Ensure it's always an array
            const dogClassesMutated = dogClasses.map((dc) => {
              let mutated: any = { ...dc };
              if (
                Constants.sanctioning_club.ABI === event.sanctioning_club &&
                _shows[s].show_type === Constants.show_type.conformation &&
                dc.level === Constants.dog_class_element.conformation_altered
              ) {
                mutated.level = "Altered";
              }
              // Inject fee_override for club manager / on-behalf entries
              const overrideKey = `${_shows[s]._id}::${_shows[s].dogs[d]._id}::${dc.show_element}::${dc.level}`;
              if (feeOverrides[overrideKey] !== undefined) {
                mutated.fee_override = feeOverrides[overrideKey]; // dollars, matches backend unit
              }
              return mutated;
            });

            const asfaMetadataClass = dogClassesMutated.find((dc: any) =>
              dc.asfa_first_trial_for_hound !== undefined ||
              dc.asfa_first_time_entry !== undefined ||
              dc.asfa_info_changed_since_last_trial !== undefined ||
              dc.asfa_info_changed_regarding !== undefined ||
              dc.asfa_dismissed_last_6_trials !== undefined
            ) as any;

            // Only include jump_height_type for show types that actually need it
            const showTypesNeedingJumpHeight = [
              Constants.show_type.obedience,
              Constants.show_type.rally_obedience,
              Constants.show_type.agility
            ];

            let _dog:any = {
              dog_id: _shows[s].dogs[d]._id,
              dog_classes: /*_shows[s].show_type === Constants.show_type.dock_jumping ? getDockJumpingClasses(_shows[s].dogs[d].classes) : */ dogClassesMutated,
              plan_to_earn_title: _shows[s].dogs[d].plan_to_earn_title !== undefined ? _shows[s].dogs[d].plan_to_earn_title : false,
              title: _shows[s].dogs[d].title,
              asfa_first_trial_for_hound: asfaMetadataClass?.asfa_first_trial_for_hound,
              asfa_first_time_entry: asfaMetadataClass?.asfa_first_time_entry,
              asfa_info_changed_since_last_trial: asfaMetadataClass?.asfa_info_changed_since_last_trial,
              asfa_info_changed_regarding: asfaMetadataClass?.asfa_info_changed_regarding,
              asfa_dismissed_last_6_trials: asfaMetadataClass?.asfa_dismissed_last_6_trials,
              using_agent: _shows[s].dogs[d].using_agent,
              agent_name: _shows[s].dogs[d].agent_name,
              agent_number: _shows[s].dogs[d].agent_number,
              using_non_agent: _shows[s].dogs[d].using_non_agent,
              competing_owner_handler: _shows[s].dogs[d].competing_owner_handler,
              agent_phone: _shows[s].dogs[d].agent_phone,
              agent_email: _shows[s].dogs[d].agent_email,
              agent_address: _shows[s].dogs[d].using_agent ? _shows[s].dogs[d].agent_address : undefined,
              substitute_name: _shows[s].dogs[d].substitute_name,
              substitute_email: _shows[s].dogs[d].substitute_email,
              substitute_phone: _shows[s].dogs[d].substitute_phone,
              // NOTE: "validate" leg info value; for junior/apprentice should be blank
              leg_info: [Constants.dog_class_element.working,
                Constants.dog_class_element.courier,
                Constants.dog_class_element.master
              ].includes(dogClassesMutated?.[0]?.level ?? "") ? _shows[s].dogs[d].leg_info : undefined,
              wheelchair_type: _shows[s].dogs[d].wheelchair_type,
              needs_measurement: _shows[s].dogs[d].needs_measurement,
              splashes: _shows[s].dogs[d].splashes
            };
            
            // Only add jump_height_type if the show type needs it and it's not "None"
            if (showTypesNeedingJumpHeight.includes(_shows[s].show_type) && _shows[s].dogs[d].jump_height_type && _shows[s].dogs[d].jump_height_type !== "None") {
              _dog.jump_height_type = _shows[s].dogs[d].jump_height_type;
            }

            let hasTimeSlot:any = _shows[s]?.dogs[d]?.classes?.filter((i: any) => { return i.time_slot !== undefined; });

            if (hasTimeSlot && hasTimeSlot.length > 0) {
              _dog.time_slot = hasTimeSlot[0].time_slot;
            }

            _showDogs.dogs.push(_dog);

            const _classLength = _shows[s]?.dogs[d]?.classes?.length;

            if (_classLength) {
              hasClasses = true;
            }
          }

          _payload.push(_showDogs);
        }
      }

      if (event && hasClasses) {
        const effectivePmt = overridePaymentMethodType || paymentChoice;
        const isOnBehalf = onBehalf !== undefined && onBehalfEmail !== undefined;
        let payload:EventRegistrationCreateDto = {
          registration_kind: isOnBehalf ? Constants.registration_kind.club : Constants.registration_kind.owner,
          payment_method_type: isOnBehalf
            ? onBehalfPaymentMethod.id.toLowerCase()
            : effectivePmt === 'bank_account'
              ? Constants.payment_method_type.bank_account
              : Constants.payment_method_type.card,
          owner_id: onBehalf || user._id,
          "dog_show_registrations": _payload,
          need_check_waitlists: true,
        }

        // Add date_received for club manager entries
        if (user.user_type === Constants.user_type.club_manager) {
          payload.date_received = dateReceived;
        }

        if (discount) {
          payload.discount_code = discount;
        }

        // Attach ONLY the transaction / PaymentIntent that belongs to the payment
        // method this request is priced for (see methodIdsRef). If this method has
        // no transaction yet (e.g. the silent bank_account pre-fetch before the
        // user ever picks Bank Account), the request is a pure quote and the
        // server persists nothing. Never fall back to another method's ids.
        if (!isOnBehalf) {
          const ids = methodIdsRef.current[effectivePmt];
          if (ids?.transactionId) {
            payload.transaction_id = ids.transactionId;
            if (ids.paymentIntentId) {
              payload.payment_intent_id = ids.paymentIntentId;
            }
            payload.summary_sequence = ++summarySequenceRef.current;
          }
        }

        if (addons) {
          let hasAddons = addons.filter((a: any) => a.amount > 0);

          if (hasAddons.length > 0) {
            payload.add_ons = hasAddons.map((a: IAddonUI) => {
              return {
                add_on_id: a._id,
                quantity: a.amount
              };
            });
          }
        }

        if (pointsToRedeem && pointsToRedeem > 0) {
          payload.club_points_to_redeem = pointsToRedeem;
        }

        // Tag this request (scoped to this payment method) so we can tell, once it
        // resolves, whether it's still the most recent one for that method — see
        // paymentSummaryRequestIdRef declaration for why this must be per-method.
        const requestId = (paymentSummaryRequestIdRef.current[effectivePmt] =
          (paymentSummaryRequestIdRef.current[effectivePmt] || 0) + 1);

        return apiService.getRegistrationPaymentSummary(event._id, payload).then((response) => {
          if (requestId !== paymentSummaryRequestIdRef.current[effectivePmt]) {
            // A newer request for this same payment method has since been fired
            // (e.g. another class was selected while this one was in flight) — this
            // response is stale, ignore it so it can't overwrite a more up-to-date
            // total with an incomplete one. Requests for other payment methods are
            // tracked independently and never invalidate this one.
            return undefined;
          }

          if (response) {
            // Always cache this method's total for the toggle display
            setPerMethodTotals(prev => ({ ...prev, [effectivePmt]: response.total_amount }));

            if (silent) return undefined;

            // The user may have switched payment method while this request was in
            // flight. Never show one method's total under another method's label —
            // the active summary must always be priced for the selected method.
            if (!isOnBehalf && effectivePmt !== paymentChoiceRef.current) return undefined;

            setRegPayload(_payload);
            setPaymentSummary(response);
            chargeResultRef.current = {
              charged: response.total_amount || 0,
              pointsUsed: response.club_points_redeemed || 0,
              currency: (response.currency || "USD").toUpperCase(),
              pointsRemaining: clubPointsLoadedRef.current
                ? Math.max(0, clubPointsRef.current - (response.club_points_redeemed || 0))
                : undefined,
            };

            const waitlists = []
            const registrants = []

            if (response.details) {
              for (let d in response.details) {
                for (let dog in (response.details[d] as ITransactionShowDetails).dogs) {
                  let _dog:any = (response.details[d] as ITransactionShowDetails).dogs[dog];
                  for (let _se in _dog.show_elements) {
                    if (_dog.show_elements[_se].waitlist && _dog.show_elements[_se].waitlist === true) {
                      waitlists.push(_dog.show_elements[_se])
                    } else {
                      registrants.push(_dog.show_elements[_se])
                    }
                  }
                }
              }

              setWaitlistsPaymentElements(waitlists);
            }

            if (!lotteryDrawOpen) {
              if (onBehalf === undefined && onBehalfEmail === undefined) {
                setStripePromise(loadStripe(Config.web.REACT_APP_STRIPE_PUBLIC_KEY || "", { stripeAccount: response.account_number }));
              } else {
                setStripePromise(loadStripe(Config.web.REACT_APP_STRIPE_PUBLIC_KEY || ""));
              }
            }

            if (discount && (response.discount > 0 || response.bulk_discount_applied)) {
              toast.show({
                title: "Discount Applied",
                content: response.bulk_discount_applied ? "Bulk discount code applied - Eager Dog fees waived" : "A discount has been applied",
                duration: 10000,
                type: "success"
              });
            } else if (discount) {
              toast.show({
                title: "Discount Invalid",
                content: "The discount code you entered is invalid",
                duration: 10000,
                type: "fail"
              });
            }
            return response.total_amount;
          }
          return undefined;
        }).catch((e: any) => {
          if (requestId !== paymentSummaryRequestIdRef.current[effectivePmt]) {
            // Stale request (for this payment method) failed after a newer one was
            // fired for the same method — ignore it.
            return undefined;
          }

          if (e.response?.data?.message) {
            setDogDetailsError(e.response?.data?.message);
          }

          toast.show({
            title: "Event Register",
            content: e.response?.data?.message ? e.response?.data?.message : "Something went wrong while getting the payment summary",
            duration: 10000,
            errorDetails: e,
            type: "fail"
          });
          return undefined;
        });
      } else {
        setPaymentSummary(defaultPaymentSummary);
        setWaitlistsPaymentElements([]);
      }
      return Promise.resolve(undefined);
    }
    return Promise.resolve(undefined);
  }, [defaultPaymentSummary, event, shows, onBehalf, onBehalfEmail, onBehalfPaymentMethod.id, user._id, dateReceived, user.user_type, feeOverrides, paymentChoice]);

  const handleRedeemPoints = useCallback((points: number) => {
    // Update payment summary with point redemption
    updatePaymentSummary(currentDiscountCode, addons, points);
  }, [currentDiscountCode, addons, updatePaymentSummary]);

  const pointsReminder = usePointsReminder(event?.club_id, pointsReminderEnabled(user, onBehalf, onBehalfEmail));
  const payRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!event?.club_id || !helperService.isLoggedIn() || onBehalf || onBehalfEmail || user.user_type !== Constants.user_type.owner) {
      clubPointsRef.current = 0;
      clubPointsLoadedRef.current = false;
      return;
    }

    let cancelled = false;
    httpService.get(`${Config.web.REACT_APP_API_URL}/api/wallets/me/clubs/${event.club_id}/points`).then((response) => {
      if (cancelled) return;
      clubPointsRef.current = response.data?.points || 0;
      clubPointsLoadedRef.current = true;
      chargeResultRef.current = {
        ...chargeResultRef.current,
        pointsUsed: paymentSummary.club_points_redeemed || 0,
        pointsRemaining: Math.max(0, clubPointsRef.current - (paymentSummary.club_points_redeemed || 0)),
      };
    }).catch(() => {
      if (!cancelled) clubPointsLoadedRef.current = false;
    });

    return () => {
      cancelled = true;
    };
  }, [event?.club_id, onBehalf, onBehalfEmail, user.user_type, paymentSummary.club_points_redeemed]);

  const chargeParams = () => {
    if (lotteryDrawOpen || onBehalf || onBehalfEmail) return "";
    const summary = chargeResultRef.current;
    const parts = [
      `charged=${encodeURIComponent(summary.charged)}`,
      `charge_currency=${encodeURIComponent(summary.currency || "USD")}`,
      `points_used=${encodeURIComponent(summary.pointsUsed || 0)}`,
    ];
    if (summary.pointsRemaining !== undefined) {
      parts.push(`points_remaining=${encodeURIComponent(summary.pointsRemaining)}`);
    }
    return parts.join("&");
  };
  const askAboutPoints = (redeemableAmount: number, pointsApplied: boolean, onContinue: () => void, onFree?: () => void) => {
    if (!pointsReminder.shouldAsk(redeemableAmount, pointsApplied)) {
      onContinue();
      return;
    }
    pointsReminder.ask({
      redeemableAmount,
      onApply: (points) => updatePaymentSummary(currentDiscountCode, addons, points),
      onContinue,
      onFree,
    });
  };

  const classesWithoutUnresolvedPreferred = (
    dogRecord: IDog,
    show: IShow | undefined,
    classList: IDogClass[],
    retry: (updated: IDog) => void,
  ): IDogClass[] => {
    if (!event || !show || event.sanctioning_club !== Constants.sanctioning_club.AKC || show.show_type !== Constants.show_type.obedience) {
      return classList;
    }

    const preferred = classList.filter((dogClass) => AKC_PREFERRED_OBEDIENCE_LEVELS.includes(dogClass.level));
    if (preferred.length === 0) {
      if (freshDogRef.current?._id === dogRecord._id) {
        freshDogRef.current = undefined;
      }
      return classList;
    }

    const dogForCheck = freshDogRef.current?._id === dogRecord._id ? freshDogRef.current : dogRecord;
    const proposedChanges = getExistingChangesForEvent(dogForCheck, event._id);
    const levels = preferred.map((dogClass) => dogClass.level);
    const missing = resolveMissingFieldsForLevels({
      phase: "classSelect",
      dog: dogForCheck,
      proposedChanges,
      sanctioningClub: event.sanctioning_club,
      show,
    }, levels);

    if (missing.length === 0) {
      if (freshDogRef.current?._id === dogRecord._id) {
        freshDogRef.current = undefined;
      }
      return classList;
    }

    setMissingFieldsRequest({
      context: {
        phase: "classSelect",
        dog: dogForCheck,
        proposedChanges,
        sanctioningClub: event.sanctioning_club,
        show,
        level: levels[0],
      },
      fields: missing,
      levels,
      onSaved: (updated) => {
        freshDogRef.current = updated;
        setMissingFieldsRequest(null);
        handleDogUpdated();
        retry(updated);
      },
    });

    return classList.filter((dogClass) => !AKC_PREFERRED_OBEDIENCE_LEVELS.includes(dogClass.level));
  };

  const updateDogInfo = (showIndex: number, dogIndex: number, classes: any[]) => {
    let _shows:IShowDogs[] = JSON.parse(JSON.stringify(shows));
    
    const dog = _shows[showIndex].dogs[dogIndex];
    const show = _shows[showIndex];
    const dogInfo = (freshDogRef.current?._id === dog?._id ? freshDogRef.current : dogs.find(d => d._id === dog?._id)) as IDog | undefined;
    if (dogInfo) {
      const requestedClasses = classes;
      classes = classesWithoutUnresolvedPreferred(dogInfo, show, classes, () => {
        updateDogInfo(showIndex, dogIndex, requestedClasses);
      });
    }
    
    // Check if classes actually changed
    const oldClassesJson = JSON.stringify(_shows[showIndex].dogs[dogIndex].classes || []);
    const newClassesJson = JSON.stringify(classes || []);
    
    if (oldClassesJson === newClassesJson) {
      return;
    }
    
    _shows[showIndex].dogs[dogIndex].classes = classes;

    // UKC Precision Coursing: max 2 PC runs per dog per calendar day PER TYPE
    // Types: "Regular" (novice/advanced/superior/master/elite), "Dash", "Steeplechase"
    // Non-competitive levels (fun_run, puppy_dash) are exempt.
    if (
      event.sanctioning_club === Constants.sanctioning_club.UKC &&
      _shows[showIndex].show_type === Constants.show_type.precision_coursing
    ) {
      const PC_REGULAR_LEVELS = new Set([
        Constants.dog_class_element_level.novice,
        Constants.dog_class_element_level.advanced,
        Constants.dog_class_element_level.superior,
        Constants.dog_class_element_level.master,
        Constants.dog_class_element_level.elite,
      ]);
      const getPCRunType = (level: string): string | null => {
        if (PC_REGULAR_LEVELS.has(level)) return "Regular";
        if (level === Constants.dog_class_element_level.dash) return "Dash";
        if (level === Constants.dog_class_element_level.steeplechase) return "Steeplechase";
        return null; // fun_run, puppy_dash — exempt
      };

      const dogId = _shows[showIndex].dogs[dogIndex]._id;
      // { "YYYY-MM-DD|RunType" -> count }
      const runsByDateType: Record<string, number> = {};

      for (const pcShow of _shows) {
        if (pcShow.show_type !== Constants.show_type.precision_coursing) continue;
        const dateKey = moment(pcShow.show_date).format("YYYY-MM-DD");
        const dogEntry = pcShow.dogs.find((d: IDogInfo) => d._id === dogId);
        for (const cls of dogEntry?.classes ?? []) {
          const runType = getPCRunType(cls.level ?? "");
          if (!runType) continue;
          const key = `${dateKey}|${runType}`;
          runsByDateType[key] = (runsByDateType[key] ?? 0) + 1;
        }
      }

      const exceededEntry = Object.entries(runsByDateType).find(([, count]) => count > 2);
      if (exceededEntry) {
        const [key] = exceededEntry;
        const [dateKey, runType] = key.split("|");
        toast.show({
          title: "Registration",
          content: `A maximum of 2 Precision Coursing ${runType} runs are allowed per day (UKC rule). Please reduce your selections on ${moment(dateKey).format("MMMM D, YYYY")}.`,
          duration: 10000,
          type: "fail"
        });
        // Revert to old class selection
        _shows[showIndex].dogs[dogIndex].classes = JSON.parse(oldClassesJson);
        setShows(_shows);
        return;
      }
    }

    setShows(_shows);
  }

  const updateDogInfoAllShowTypes = useCallback((showType: string, classes: IDogClass[], dog: IDog) => {
    const show = shows.find((item) => item.show_type === showType);
    const dogForCheck = freshDogRef.current?._id === dog._id ? freshDogRef.current : dog;
    const requestedClasses = classes;
    classes = classesWithoutUnresolvedPreferred(dogForCheck, show, classes, (updated) => {
      updateDogInfoAllShowTypes(showType, requestedClasses, updated);
    });

    let _shows:IShowDogs[] = JSON.parse(JSON.stringify(shows));
    let hasChanges = false;

    for (let s in _shows) {
      if (_shows[s].show_type === showType) {
        let _dog:IDogInfo[] = _shows[s].dogs.filter((d: IDogInfo, index: number) => d._id === dog._id);

        if (_dog[0]) {
          // For UKC Conformation, filter classes to only those offered by the target show.
          // This prevents "Use same selections for all shows" from applying a class (e.g. Veteran)
          // to a show that doesn't offer it in its show_elements.
          let classesToApply = classes;
          if (event.sanctioning_club === Constants.sanctioning_club.UKC && showType === Constants.show_type.conformation) {
            const targetElements: IShowElement[] = _shows[s].show_elements || [];
            if (targetElements.length > 0) {
              classesToApply = classes.filter((dc: IDogClass) => {
                // Non-licensed classes (Veteran, Novice Puppy, etc.) need special handling
                // because show_elements can store them in two formats:
                //   OLD: { show_element: "Veteran" }  (level as the show_element, no level field)
                //   NEW: { show_element: "Non-Licensed Classes", level: "Veteran" }
                if (dc.show_element === Constants.dog_class_element.conformation_non_licensed) {
                  return targetElements.some((se: IShowElement) =>
                    (se.show_element === dc.show_element && se.level === dc.level) || // NEW format
                    se.show_element === dc.level ||                                    // OLD format
                    se.level === dc.level                                              // fallback
                  );
                }
                // Regular conformation / altered conformation — just needs the section to exist
                return targetElements.some((se: IShowElement) =>
                  se.show_element === dc.show_element && (!se.level || se.level === dc.level)
                );
              });
            }
          }

          // C-WAGS FEO: don't copy FEO runs onto a trial that doesn't offer FEO
          // (Scent / Games: show.feo_available; Obedience / Rally: FEO section row for that level).
          if (event.sanctioning_club === Constants.sanctioning_club.C_WAGS) {
            const targetShow: IShowDogs = _shows[s];
            const targetElements: IShowElement[] = targetShow.show_elements || [];
            classesToApply = classesToApply.filter((dc: IDogClass) => {
              if (dc.section !== Constants.dog_class_section.feo) {
                return true;
              }
              if (isCwagsFeoShowWide(event.sanctioning_club, showType)) {
                return !!targetShow.feo_available;
              }
              return targetElements.some((se: IShowElement) =>
                se.level === dc.level && se.section === Constants.dog_class_section.feo
              );
            });
          }

          // Check if classes actually changed
          const oldClassesJson = JSON.stringify(_dog[0].classes || []);
          const newClassesJson = JSON.stringify(classesToApply || []);
          
          if (oldClassesJson !== newClassesJson) {
            _dog[0].classes = classesToApply;
            hasChanges = true;
          }
        }
      }
    }

    if (hasChanges) {
      setShows(_shows);
    }
  }, [shows, event, dogs]);

  const handleDogUpdated = () => {
    const queryParameters = new URLSearchParams(window.location.search);
    const uid = queryParameters.get("uid");

    // Save current class selections before refreshing dogs
    const currentClassSelections = new Map<string, IDogClass[]>();
    shows.forEach(show => {
      show.dogs.forEach(dog => {
        if (dog.classes && dog.classes.length > 0) {
          currentClassSelections.set(`${show._id}-${dog._id}`, dog.classes);
        }
      });
    });

    // Refresh dogs from API
    const refreshDogs = (updatedDogs: IDog[]) => {
      // Update states synchronously to ensure proper re-render
      flushSync(() => {
        // Update dogs state
        setDogs(updatedDogs);
      });
      
      flushSync(() => {
        // Update shows to use new dog data while preserving class selections
        setShows(prevShows => {
          const _shows = JSON.parse(JSON.stringify(prevShows));
          _shows.forEach((show: IShowDogs) => {
            show.dogs = show.dogs.map((oldDog: IDogInfo) => {
              // Find the updated dog from API
              const updatedDog = updatedDogs.find(d => d._id === oldDog._id);
              if (updatedDog) {
                // Use the updated dog data
                const newDog = { ...updatedDog } as IDogInfo;
                // Keep classes already on this show. A selection can land while
                // this refresh is in flight, and the snapshot above would drop it.
                const key = `${show._id}-${oldDog._id}`;
                if (oldDog.classes !== undefined) {
                  newDog.classes = oldDog.classes;
                } else {
                  const savedClasses = currentClassSelections.get(key);
                  if (savedClasses) {
                    newDog.classes = savedClasses;
                  }
                }
                // Preserve any other show-specific properties from oldDog that aren't in updatedDog
                if (oldDog.using_agent !== undefined) newDog.using_agent = oldDog.using_agent;
                if (oldDog.using_non_agent !== undefined) newDog.using_non_agent = oldDog.using_non_agent;
                if (oldDog.competing_owner_handler !== undefined) newDog.competing_owner_handler = oldDog.competing_owner_handler;
                if (oldDog.plan_to_earn_title !== undefined) newDog.plan_to_earn_title = oldDog.plan_to_earn_title;
                return newDog;
              }
              return oldDog; // Keep old dog if not found in updated list
            });
          });
          return _shows;
        });
      });
      
      flushSync(() => {
        // Trigger FormBuilder re-render
        setDogUpdateTrigger(prev => prev + 1);
      });
    };

    if (onBehalf && onBehalfEmail && event && uid) {
      apiService.getUserDogs(uid).then((dogResponse) => {
        if (event.club_id) {
          apiService.getUserDogs(uid, event.club_id).then((clubResponse) => {
            refreshDogs([...dogResponse, ...clubResponse]);
          });
        } else {
          refreshDogs(dogResponse);
        }
      });
    } else if (event) {
      apiService.getUserDogs(user._id).then((dogResponse) => {
        if (event.club_id) {
          apiService.getUserDogs(user._id, event.club_id).then((clubResponse) => {
            refreshDogs([...dogResponse, ...clubResponse]);
          });
        } else {
          refreshDogs(dogResponse);
        }
      });
    }
  }

  /*const hasAddress = (validateDog: any) => {
    if (validateDog.coowners && validateDog.coowners.length > 0) {
      if ((!validateDog.coowners[0].address.city || validateDog.coowners[0].address.city.length === 0) ||
          (!validateDog.coowners[0].address.country || validateDog.coowners[0].address.country.length === 0) ||
          (!validateDog.coowners[0].address.state || validateDog.coowners[0].address.state.length === 0) ||
          (!validateDog.coowners[0].address.postal_code || validateDog.coowners[0].address.postal_code.length === 0) ||
          (!validateDog.coowners[0].address.line1 || validateDog.coowners[0].address.line1.length === 0)) {
        return validateDog.call_name + " is missing owner address information (Country, State, City, ZIP, and Address). You can add owner information by editing your dog information.";
      } else {
        return "";
      }
    }

    return "";
  }*/

  const selectDog = (showIndex:number, dog:IDogInfo) => {
    // Check if user is banned from this club (use ref for immediate access)
    if (isUserBannedRef.current) {
      const isOnBehalfReg = onBehalf !== undefined && onBehalfEmail !== undefined;
      toast.show({
        title: "Registration",
        content: isOnBehalfReg 
          ? "This user is not currently able to register for events hosted by this club."
          : "This account is not currently able to register for events hosted by this club. If you believe this is an error, please contact the event secretary.",
        duration: 10000,
        type: "fail"
      });
      return false;
    }

    // The dog tiles can lag behind a save made from another trial, so check the
    // newest copy of this dog we hold (it carries the latest proposed changes).
    dog = [freshDogRef.current, ...shows.flatMap((s) => s.dogs)]
      .reduce<IDogInfo>((latest, candidate) => {
        if (!candidate || candidate._id !== dog._id || !candidate.updated_at) return latest;
        return !latest.updated_at || moment(candidate.updated_at).isAfter(moment(latest.updated_at)) ? { ...latest, ...candidate } as IDogInfo : latest;
      }, dog);

    let _shows:IShowDogs[] = JSON.parse(JSON.stringify(shows));
    let _foundDog:boolean = false;
    const proposedChanges = dog.proposed_changes ?? [];
    // Check for proposed changes if user is admin for this club (club_manager user_type OR
    // an "owner" user with admin capacity for the club - see isAdmin state) OR doing an
    // on-behalf registration. Using isAdmin here (rather than just user_type === club_manager)
    // ensures secretaries who are "owner" users with admin capacity also get breed/other
    // overrides they proposed applied during registration, instead of validation still using
    // the dog's un-corrected info.
    const shouldCheckProposedChanges = isAdmin || (onBehalf && onBehalfEmail);
    const existingChanges = shouldCheckProposedChanges ? proposedChanges.find(pc => pc.event_id?.toString() === _shows[showIndex].event_id?.toString()) : undefined;
    const clubRegInfo = getClubRegistrationInfo(dog, event.sanctioning_club, existingChanges);

    const show = _shows[showIndex];
    const breedMode = show.breed_restriction_mode ?? Constants.breed_restriction_mode.exclude;
    const dogBreed = clubRegInfo.breed;
    const dogName = clubRegInfo.call_name.length > 0 ? clubRegInfo.call_name : dog.call_name;
    const now = new Date();

    let breedBlocked = false;
    let breedBlockReason = "";

    if (breedMode === Constants.breed_restriction_mode.allow) {
      const allowedEndDate = (show as any).allowed_breeds_end_date;
      const allowedExpired = allowedEndDate && new Date(allowedEndDate) < now;
      if (!allowedExpired) {
        const allowedBreeds = show.allowed_breeds ?? [];
        if (dogBreed.length > 0 && allowedBreeds.length > 0 && !allowedBreeds.includes(dogBreed)) {
          breedBlocked = true;
          breedBlockReason = `${dogName}'s breed (${dogBreed}) is not in the allowed breeds list for this show`;
        }
      }
    } else {
      const excludedEndDate = (show as any).excluded_breeds_end_date;
      const excludedExpired = excludedEndDate && new Date(excludedEndDate) < now;
      if (!excludedExpired && dogBreed.length > 0 && (show.excluded_breeds ?? []).includes(dogBreed)) {
        breedBlocked = true;
        breedBlockReason = `${dogName}'s breed (${dogBreed}) is not allowed for this show`;
      }
    }

    if (breedBlocked) {
      // Remove dog from selection if already selected
      for (let d in _shows[showIndex].dogs) {
        if (_shows[showIndex].dogs[d]._id === dog._id) {
          _shows[showIndex].dogs.splice(parseInt(d), 1);
          setShows(_shows);
          break;
        }
      }

      toast.show({
        title: "Select Dog",
        content: breedBlockReason,
        duration: 10000,
        type: "fail"
      });

      return false;
    }

    const selectedShow = _shows[showIndex];
    const dogCallName = clubRegInfo.call_name.length > 0 ? clubRegInfo.call_name : dog.call_name;

    const effectiveDob = existingChanges?.dob?.new || dog.dob;
    if (effectiveDob && event.sanctioning_club === Constants.sanctioning_club.AKC && selectedShow.show_type === Constants.show_type.fast_cat) {
      const ageInMonths = moment(selectedShow.show_date).diff(moment(effectiveDob), "months");
      if (ageInMonths < 12) {
        toast.show({
          title: "Select Dog",
          content: dogCallName + " must be at least 12 months old to compete in AKC Fast CAT",
          duration: 10000,
          type: "fail",
        });
        return false;
      }
    }

    if (effectiveDob && event.sanctioning_club === Constants.sanctioning_club.AKC && selectedShow.show_type === Constants.show_type.trick_dog) {
      const referenceDate = selectedShow.show_date || new Date();
      const ageInMonths = moment(referenceDate).diff(moment(effectiveDob), "months");
      if (ageInMonths < 4) {
        toast.show({
          title: "Registration",
          content: "Dogs must be 4 months or older to compete in Trick Dog",
          duration: 10000,
          type: "fail",
        });
        return false;
      }
    }

    if (selectedShow.max_dogs !== undefined && (selectedShow.max_dogs || 0) - (selectedShow.registered_entries || 0) === 0) {
      toast.show({
        title: "Dog Registration",
        content: "There aren't any available spots for that show",
        duration: 10000,
        type: "fail",
      });
      return false;
    }

    const missingFields = resolveMissingFields({
      phase: "dogSelect",
      dog,
      proposedChanges: existingChanges,
      sanctioningClub: event.sanctioning_club,
      show: selectedShow,
    });
    if (missingFields.length > 0) {
      setMissingFieldsRequest({
        context: {
          phase: "dogSelect",
          dog,
          proposedChanges: existingChanges,
          sanctioningClub: event.sanctioning_club,
          show: selectedShow,
        },
        fields: missingFields,
        onSaved: (updated) => {
          freshDogRef.current = updated;
          setMissingFieldsRequest(null);
          handleDogUpdated();
          selectDogRef.current(showIndex, updated as IDogInfo);
        },
      });
      return false;
    }

    // If we get here, all validations passed - select the dog
    {
      let _dog = JSON.parse(JSON.stringify(dog));
      _dog.classes = [];

      for (let d in _shows[showIndex].dogs) {
        if (_shows[showIndex].dogs[d]._id === dog._id) {
          _shows[showIndex].dogs.splice(parseInt(d), 1);
          _foundDog = true;
          break;
        }
      }

      if (!_foundDog) {
        _shows[showIndex].dogs.push(_dog);
      }

      setShows(_shows);
      // Trigger timeslot refresh when dogs are added or removed
      setTimeslotRefreshTrigger(prev => prev + 1);
    }
  }

  selectDogRef.current = selectDog;

  const showTypeHasDogs = (showType: string) => {
    for (let s in shows) {
      if (shows[s].show_type === showType) {
        if (shows[s].dogs.length > 0) {
          return shows[s].dogs.length;
        }
      }
    }

    return 0;
  }

  const allDogsHaveClasses = () => {
    for (let s in shows) {
      for (let d in shows[s].dogs) {
        let _classes = shows[s].dogs[d].classes;

        if (!helperService.timeSlotShows.includes(shows[s].show_type)) {
          if (!_classes || _classes.length === 0) {
            return false;
          }
        }
      }
    }

    return true;
  }

  const dogsAgentsValidate = () => {
    const errors = []
    for (let s in shows) {
      for (let d in shows[s].dogs) {
        if (shows[s].dogs[d].using_agent) {
          if (!shows[s].dogs[d].agent_name) errors.push('name');
          // Only require agent number for non-UKC, non-BHA and non-CKC events
          if (![Constants.sanctioning_club.UKC, Constants.sanctioning_club.BHA, Constants.sanctioning_club.CKC].includes(event?.sanctioning_club) && !shows[s].dogs[d].agent_number) {
            errors.push('number');
          }
          // NOTE: check agent's phone presence on Water dog event show
          if (shows[s].show_type === Constants.show_type.water_dog) {
            if (!helperService.isValidEmail(shows[s].dogs[d].agent_email ?? '')) errors.push('email');
            if (!isPossiblePhoneNumber(shows[s].dogs[d].agent_phone ?? '')) errors.push('phone');
          }
        }
      }
    }

    return errors;
  }

  // NOTE: PWDCA Water dog specific check
  const dogsSubstituteValidate = () => {
    const errors: Record<string, string[]>  = {};

    for (let s in shows) {
      if (shows[s].show_type === Constants.show_type.water_dog) {
        for (let d in shows[s].dogs) {
          const dog = shows[s].dogs[d];
          errors[dog.call_name] = [];

          // level single select
          if (dog.classes?.[0] && [Constants.dog_class_element.courier, Constants.dog_class_element.master].includes(dog.classes[0].level)) {
            if (!dog.substitute_name) errors[dog.call_name].push('name');
            if (!helperService.isValidEmail(dog.substitute_email ?? '')) errors[dog.call_name].push('email');
            if (!isPossiblePhoneNumber(dog.substitute_phone ?? '')) errors[dog.call_name].push('phone');
          }
        }
      }
    }

    return errors;
  }

  const next = () => {
    let valid:boolean = true;

    let dogMsg:string = "";

    if (dogDetailsError !== undefined) {
      toast.show({
        title: "Event Registration",
        content: dogDetailsError,
        duration: 10000,
        type: "fail"
      });

      valid = false;
    }

    switch (currentStep) {
      case 0:
        let _hasDogs: boolean = false;

        for (let s in showTypes) {
          let hasDogs:number = showTypeHasDogs(showTypes[s]);

          if (hasDogs > 0) {
            _hasDogs = true;
          }

          // Only show warning for Conformation shows with multiple dogs
          if (hasDogs > 1 && showTypes[s] === Constants.show_type.conformation) {
            if (dogMsg.length > 0) {
              dogMsg = "Looks like you've placed a few dogs onto a single show, multiple times. Was this intentional?";
            } else {
              dogMsg = `Looks like you've placed ${hasDogs} dogs onto a single ${showTypes[s]} show. Was this intentional?`;
            }

            valid = false;
          }
        }

        if (dogMsg) {
          setTwoDogsMsg(dogMsg);
          setModalContent("twoDogs");
        }

        if (!_hasDogs) {
          toast.show({
            title: "Event Registration",
            content: "Please add at least one dog to a show",
            duration: 10000,
            type: "fail"
          });

          valid = false;
        }
      break;
      case 1:
        if (!allDogsHaveClasses()) {
          toast.show({
            title: "Event Registration",
            content: "Each registered dog must have at least one class chosen per show",
            duration: 10000,
            type: "fail"
          });

          valid = false;
        }

        const agentsErrors = dogsAgentsValidate();
        if (valid && agentsErrors.length) {
          toast.show({
            title: "Event Registration",
            content: `Missing an agent/handler ${agentsErrors.join(', ')}`,
            duration: 10000,
            type: "fail"
          });

          valid = false;
        }

        const substituteErrors = dogsSubstituteValidate();
        for (const dogCallName in substituteErrors) {
          if (valid && substituteErrors[dogCallName].length) {
            substituteErrors[dogCallName].forEach((error) => {
              if (['email', 'phone'].includes(error)) {
                toast.show({
                  title: "Event Registration",
                  content: `Please enter a valid substitute steward ${error} for ${dogCallName}`,
                  duration: 10000,
                  type: "fail"
                });
              } else {
                toast.show({
                  title: "Event Registration",
                  content: `Missing a substitute steward ${error} for ${dogCallName}`,
                  duration: 10000,
                  type: "fail"
                });
              }
            })

            valid = false;
          }
        }

        let hasNoseworkOrRatRace = shows.filter((s: IShow) => s.show_type === Constants.show_type.nosework || s.show_type === Constants.show_type.urban_rat_race);

        if (hasNoseworkOrRatRace.length > 0) {
          for (let s in hasNoseworkOrRatRace) {
            for (let d in hasNoseworkOrRatRace[s].dogs) {
              // Check for any falsy section value (undefined, null, empty string) OR explicit "None"
              // For Urban Rat Race, Games classes don't require sections
              let hasInvalidSection = hasNoseworkOrRatRace[s].dogs[d].classes?.filter((cl: IDogClass) => {
                // Skip validation for Urban Rat Race Games classes (they don't have sections)
                if (hasNoseworkOrRatRace[s].show_type === Constants.show_type.urban_rat_race && 
                    cl.show_element === Constants.dog_class_element.games) {
                  return false;
                }
                
                return !cl.section || cl.section === "None" || cl.section.trim() === "";
              });

              if (hasInvalidSection && hasInvalidSection.length > 0) {
                toast.show({
                  title: "Event Registration",
                  content: "Please select a Section for all the elements you've selected in your "+ hasNoseworkOrRatRace[s].show_type +" show(s)",
                  duration: 10000,
                  type: "fail"
                });

                valid = false;
                return;
              }
            }
          }
        }

        let hasTimeSlotShows = shows.filter((s: IShow) => helperService.timeSlotShows.includes(s.show_type));

        // ABI Scent Dog Games: max 2 dogs per game (level) per registrant
        const scentDogGamesShows = shows.filter((s: IShow) => s.show_type === Constants.show_type.scent_dog_games);
        for (const sdgShow of scentDogGamesShows) {
        // ABI Scent Dog Games: max 2 unique dogs per game (level) per registrant
        // A single dog entered in multiple sections of the same game (via + row)
        // counts as 1 dog, not one per section.
        const levelDogSets: Record<string, Set<string>> = {};
        for (const dog of (sdgShow.dogs || [])) {
          const dogId = (dog._id || '').toString();
          for (const cls of (dog.classes || [])) {
            const lvl = cls.level;
            if (lvl) {
              if (!levelDogSets[lvl]) levelDogSets[lvl] = new Set();
              levelDogSets[lvl].add(dogId);
            }
          }
        }
        for (const [lvl, dogSet] of Object.entries(levelDogSets)) {
          if (dogSet.size > 2) {
            toast.show({
              title: "Event Registration",
              content: `You can enter a maximum of 2 dogs per game. "${lvl}" has ${dogSet.size} dogs entered.`,
              duration: 10000,
              type: "fail"
            });
            valid = false;
            return;
          }
        }
        }

        if (hasTimeSlotShows.length > 0) {
          for (let s in hasTimeSlotShows) {
            const show = hasTimeSlotShows[s];
            // Only enforce timeslot selection if this show actually has timeslots configured
            const showHasTimeslots = (showTimeslotMap[show._id] ?? 0) > 0;
            if (!showHasTimeslots) continue;

            const isPerClassTimeslotShow =
              show.show_type === Constants.show_type.obedience_show_n_go ||
              show.show_type === Constants.show_type.general_rally;
            const slotsData: IShowRunTimeSlot[] = showTimeslotsData[show._id] ?? [];
            const hasPerClassSlots = isPerClassTimeslotShow && slotsData.some((t: IShowRunTimeSlot) => !!t.level);

            for (let d in show.dogs) {
              const dogClasses: any[] = show?.dogs[d]?.classes ?? [];

              if (hasPerClassSlots) {
                // Per-class validation: each selected class whose level has timeslots must have a time_slot chosen
                for (const dc of dogClasses) {
                  const levelHasSlots = slotsData.some((t: IShowRunTimeSlot) => t.level === dc.level);
                  if (levelHasSlots && !dc.time_slot) {
                    toast.show({
                      title: "Event Registration",
                      content: `Please select a timeslot for "${dc.level}" in your ${show.show_type} show`,
                      duration: 10000,
                      type: "fail"
                    });
                    valid = false;
                    return;
                  }
                }
              } else {
                // Legacy: at least one class must have a time_slot
                let hasDogWithTimeSlot = dogClasses.filter((c: any) => c.time_slot !== undefined);
                if (!hasDogWithTimeSlot || hasDogWithTimeSlot.length === 0) {
                  toast.show({
                    title: "Event Registration",
                    content: "Please select a timeslot for all the elements you've selected in your " + show.show_type + " show(s)",
                    duration: 10000,
                    type: "fail"
                  });
                  valid = false;
                  return;
                }
              }
            }
          }
        }

        if (event?.sanctioning_club === Constants.sanctioning_club.ASFA) {
          const requiresCertLevels = [
            Constants.dog_class_element_level.asfa_open,
            Constants.dog_class_element_level.asfa_veteran,
            Constants.dog_class_element_level.asfa_provisional,
          ];

          const asfaShows = shows.filter((s: IShow) => s.show_type === Constants.show_type.field_trial);
          for (let s in asfaShows) {
            for (let d in asfaShows[s].dogs) {
              const dogClasses = asfaShows[s].dogs[d]?.classes || [];
              if (!dogClasses.length) continue;

              const hasRequiresCertLevel = dogClasses.some((cl: any) => requiresCertLevels.includes(cl.level));
              const asfaMetadataClass = dogClasses.find((cl: any) =>
                cl.asfa_first_trial_for_hound !== undefined ||
                cl.asfa_first_time_entry !== undefined ||
                cl.asfa_info_changed_since_last_trial !== undefined ||
                cl.asfa_dismissed_last_6_trials !== undefined
              ) as any;

              if (hasRequiresCertLevel && asfaMetadataClass?.asfa_first_trial_for_hound === undefined) {
                toast.show({
                  title: "Event Registration",
                  content: "Please answer whether this is the first ASFA trial for your hound.",
                  duration: 10000,
                  type: "fail"
                });
                valid = false;
                return;
              }

              if (asfaMetadataClass?.asfa_first_time_entry === undefined) {
                toast.show({
                  title: "Event Registration",
                  content: "Please answer whether this is a first-time ASFA entry.",
                  duration: 10000,
                  type: "fail"
                });
                valid = false;
                return;
              }

              if (asfaMetadataClass?.asfa_info_changed_since_last_trial === undefined) {
                toast.show({
                  title: "Event Registration",
                  content: "Please answer whether information changed since the last ASFA trial entry.",
                  duration: 10000,
                  type: "fail"
                });
                valid = false;
                return;
              }

              if (
                asfaMetadataClass?.asfa_info_changed_since_last_trial === true &&
                (!asfaMetadataClass?.asfa_info_changed_regarding || asfaMetadataClass.asfa_info_changed_regarding.trim().length === 0)
              ) {
                toast.show({
                  title: "Event Registration",
                  content: "Please fill out the ASFA 'Regarding' field when information has changed.",
                  duration: 10000,
                  type: "fail"
                });
                valid = false;
                return;
              }

              if (asfaMetadataClass?.asfa_dismissed_last_6_trials === undefined) {
                toast.show({
                  title: "Event Registration",
                  content: "Please answer whether the hound was dismissed within the last 6 ASFA trials.",
                  duration: 10000,
                  type: "fail"
                });
                valid = false;
                return;
              }
            }
          }
        }

        let hasObedienceOrConformation = shows.filter((s: IShow) => [Constants.show_type.obedience, Constants.show_type.rally_obedience, Constants.show_type.conformation].includes(s.show_type));
        if (hasObedienceOrConformation.length) {
          for (let s in hasObedienceOrConformation) {
            for (let d in hasObedienceOrConformation[s].dogs) {
              let teamsLevel = hasObedienceOrConformation[s].dogs[d]?.classes?.filter((cl: IDogClass) => cl.level === Constants.dog_class_element_level.team);
              let braceLevels = hasObedienceOrConformation[s].dogs[d]?.classes?.filter((cl: IDogClass) => cl.level === Constants.dog_class_element_level.brace);
              let pairsLevels = hasObedienceOrConformation[s].dogs[d]?.classes?.filter((cl: IDogClass) => [
                Constants.dog_class_element_level.pairs,
                Constants.dog_class_element_level.novice_pairs,
                Constants.dog_class_element_level.advanced_pairs,
              ].includes(cl.level));

              if (teamsLevel?.length) {
                for (const level of teamsLevel) {
                  if (level?.extra_arr?.length !== 3) {
                    toast.show({
                      title: "Event Registration",
                      content: "Please select 3 teammates for your dog on " + hasObedienceOrConformation[s].show_type + " show' Team level",
                      duration: 10000,
                      type: "fail"
                    });

                    valid = false;
                  }
                }
              }

              if (braceLevels?.length) {
                for (const level of braceLevels) {
                  if (!level?.extra) {
                    toast.show({
                      title: "Event Registration",
                      content: "Please select a teammate for your dog on "+ hasObedienceOrConformation[s].show_type +" show' Brace level",
                      duration: 10000,
                      type: "fail"
                    });

                    valid = false;
                  }
                }
              }

              if (pairsLevels?.length) {
                for (const level of pairsLevels) {
                  if (!level?.extra) {
                    toast.show({
                      title: "Event Registration",
                      content: "Please select a teammate for your dog on "+ hasObedienceOrConformation[s].show_type +" show' Pairs level",
                      duration: 10000,
                      type: "fail"
                    });

                    valid = false;
                  }
                }
              }

              if (!valid) return;
            }
          }
        }

        // C-WAGS Games: Pairs requires the partner dog to be named on the entry
        // (rules: "It also needs to indicate who the other dog is"). Team may be blank.
        if (event?.sanctioning_club === Constants.sanctioning_club.C_WAGS) {
          const gamesShows = shows.filter((s: IShow) => s.show_type === Constants.show_type.cwags_games);
          for (const gs of gamesShows) {
            for (const d of gs.dogs || []) {
              const pairsMissingPartner = d?.classes?.some((cl: IDogClass) =>
                cl.show_element === Constants.dog_class_element.cwags_games_pairs && !(cl.extra && String(cl.extra).trim())
              );
              if (pairsMissingPartner) {
                toast.show({
                  title: "Event Registration",
                  content: "Please enter the partner dog for the Pairs game on " + gs.show_type + " " + gs.show_name,
                  duration: 10000,
                  type: "fail"
                });
                valid = false;
                return;
              }
            }
          }
        }

        // let hasDockJumping = shows.filter((s: IShow) => (s.show_type === Constants.show_type.dock_jumping));

        // if (hasDockJumping.length > 0) {
        //   for (let s in hasDockJumping) {
        //     for (let d in hasDockJumping[s].dogs) {
        //       let hasDogWithAir = hasDockJumping[s]?.dogs[d]?.classes?.filter((c: any) => { return c.level === Constants.dog_class_element_level.dock_jumping_air; });

        //       if (hasDogWithAir && hasDogWithAir.length > 0) {
        //         for (let dd in hasDogWithAir) {
        //           if (!hasDogWithAir[dd].multiplier) {
        //             toast.show({
        //               title: "Event Registration",
        //               content: "Please enter a number of splashes for each dog in dock jumping air",
        //               duration: 10000,
        //               type: "fail"
        //             });

        //             valid = false;
        //             return;
        //           } else if (Number(hasDogWithAir[dd].multiplier) <= 0 || Number(hasDogWithAir[dd].multiplier) > 6) {
        //             toast.show({
        //               title: "Event Registration",
        //               content: "Please enter a valid number of splashes for each dog in dock jumping air (between 1 and 6)",
        //               duration: 10000,
        //               type: "fail"
        //             });

        //             valid = false;
        //             return;
        //           }
        //         }
        //       }
        //     }
        //   }
        // }
      break;
      case 2:
        if (!agreeToWaiver) {
          valid = false;

          toast.show({
            title: "Event Registration",
            content: "You must agree to the Waiver to continue",
            duration: 10000,
            type: "fail"
          });
        }
      break;
      case 3:
        if (!agreeToEventWaiver) {
          valid = false;

          toast.show({
            title: "Event Registration",
            content: "You must agree to the Waiver to continue",
            duration: 10000,
            type: "fail"
          });
        }
      break;
    }

    if (valid) {
      let hasFastCat: any[] = shows.filter((s: any) => s.show_type === Constants.show_type.fast_cat);

      if (hasFastCat.length > 0 && currentStep === 1) {
        checkFastCatDiffTimeSlots(hasFastCat);
      } else {
        if (currentStep === 2) {
          const hasEventWaiver = !!(event.waiver && event.waiver !== ' ' && event.waiver !== '');
          if (hasEventWaiver) {
            setCurrentStep(currentStep + 1); // step 3 (event waiver)
          } else if (addons.length === 0) {
            setCurrentStep(currentStep + 3); // step 5 (payment), skip 3, 4
          } else {
            setCurrentStep(currentStep + 2); // step 4 (add-ons), skip 3
          }
        } else {
          setCurrentStep(currentStep + 1);
        }
      }

      if (currentStep === 3) {
        // After event waiver: skip add-ons if empty, go straight to payment
        if (addons.length === 0 || (lotteryDrawOpen && !onBehalf)) {
          setCurrentStep(currentStep + 2); // Skip add-ons step (4), go to payment step (5)
        }
        // else: default +1 from above navigates to step 4 (add-ons)
      }

      if (currentStep === 4) {
        // Add-ons step — default +1 goes to payment (5), no skip needed
      }
    }
  }

  const checkFastCatDiffTimeSlots = (hasFastCat: any[]) => {
    let dogMsg:string = "";
    let mergedTimeSlotsPerDog: any[] = [];

    for (let s in hasFastCat) {
      let _timeslot_name:string = "";

      for (let d in hasFastCat[s].dogs) {
        if (!mergedTimeSlotsPerDog[hasFastCat[s].dogs[d]._id]) {
          const dogClubRegInfo = getClubRegistrationInfo(hasFastCat[s].dogs[d], event.sanctioning_club);
          mergedTimeSlotsPerDog[hasFastCat[s].dogs[d]._id] = {
            name: dogClubRegInfo.registered_name,
            timeSlots: []
          };
        }

        for (let c in hasFastCat[s].dogs[d].classes) {
          mergedTimeSlotsPerDog[hasFastCat[s].dogs[d]._id].timeSlots.push(hasFastCat[s].dogs[d].classes[c].time_slot);

          if (_timeslot_name.length === 0) {
            _timeslot_name = hasFastCat[s].dogs[d].classes[c].time_slot;
          } else if (_timeslot_name !== hasFastCat[s].dogs[d].classes[c].time_slot) {
            dogMsg = "Looks like you have multiple dogs in a single trial with different timeslots. Was this intentional?";
            break;
          }
        }
      }
    }

    if (Object.keys(mergedTimeSlotsPerDog).length === 1) {
      for (let d in mergedTimeSlotsPerDog) {
        for (let t in mergedTimeSlotsPerDog[d].timeSlots) {
          if (mergedTimeSlotsPerDog[d].timeSlots[t] !== mergedTimeSlotsPerDog[d].timeSlots[0]) {
            dogMsg = "Looks like "+ mergedTimeSlotsPerDog[d].name +" is registered for multiple trials, but in different timeslots. Was this intentional?";
          }
        }
      }
    }

    if (dogMsg.length > 0) {
      setTwoDogsMsg(dogMsg);
      setModalContent("twoDogs");
    } else {
      if (currentStep === 2) {
        const hasEventWaiver = !!(event.waiver && event.waiver !== ' ' && event.waiver !== '');
        if (hasEventWaiver) {
          setCurrentStep(currentStep + 1); // step 3 (event waiver)
        } else if (addons.length === 0) {
          setCurrentStep(currentStep + 3); // step 5 (payment), skip 3 and 4
        } else {
          setCurrentStep(currentStep + 2); // step 4 (add-ons), skip 3
        }
      }
      else {
        setCurrentStep(currentStep + 1);
      }
    }
  }

  const previous = () => {
    const hasEventWaiver = !!(event.waiver && event.waiver !== ' ' && event.waiver !== '');
    if (currentStep === 4) { // Add-ons step
      if (hasEventWaiver) {
        setCurrentStep(currentStep - 1); // Go back to Event Waiver
      } else {
        setCurrentStep(currentStep - 2); // Skip Event Waiver, go back to Sanctioning Waiver
      }
    } else if (currentStep === 5) { // Payment step
      if (addons.length === 0 || (lotteryDrawOpen && !onBehalf)) {
        if (hasEventWaiver) {
          setCurrentStep(currentStep - 2); // Skip add-ons (empty), go back to Event Waiver
        } else {
          setCurrentStep(currentStep - 3); // Skip add-ons and Event Waiver
        }
      } else {
        setCurrentStep(currentStep - 1); // Go back to Add-ons
      }
    } else {
      setCurrentStep(currentStep - 1);
    }
  }

  const getDogIds = () => {
    let dogIds:string[] = [];

    for (let s in shows) {
      for (let d in shows[s].dogs) {
        if (!dogIds.includes(shows[s].dogs[d]._id)) {
          dogIds.push(shows[s].dogs[d]._id);
        }
      }
    }

    return dogIds.join();
  }

  const dogInShowType = (dog:IDog, showType: string) => {
    let _filteredShows = shows.filter((show:IShowDogs) => show.show_type === showType && show.dogs.filter((d: IDogInfo) => d._id === dog._id).length > 0);

    return _filteredShows.length > 0 ? true : false;
  }

  const getJoinedShow = (dog:IDogInfo, showType: string) => {
    let _filteredShows = shows.filter((show:IShowDogs) => show.show_type === showType && show.dogs.filter((d: IDogInfo) => d._id === dog._id).length > 0);
    let _show:IShowDogs = JSON.parse(JSON.stringify(_filteredShows[0]));

    for (let s in shows) {
      if (shows[s].show_type === showType && shows[s].dogs.filter((d: IDogInfo) => d._id === dog._id).length > 0) {
        _show.show_elements = _show.show_elements.concat(shows[s].show_elements);
      }
    }

    _show.show_elements = _show.show_elements.filter((v:IShowElement, i:number, a:IShowElement[]) => a.findIndex(v2 => (JSON.stringify(v2) === JSON.stringify(v))) === i);

    return _show;
  }

   /*const getClubCards = (clubId: string) => {
    apiService.getClubPaymentMethods(clubId).then((clubPaymentMethodsResponse) => {
      let _radioItems:IRadioListItem[] = [];

      for (let i in clubPaymentMethodsResponse) {
        _radioItems.push({
          id: clubPaymentMethodsResponse[i].id,
          label: clubPaymentMethodsResponse[i].card.display_brand.toUpperCase() + " ending in " + clubPaymentMethodsResponse[i].card.last4,
          action: {
            label: "(remove)",
            onClick: () => { setModalContent("confirmRemoveCard"); setCardToDelete({ label: clubPaymentMethodsResponse[i].card.display_brand.toUpperCase() + " ending in " + clubPaymentMethodsResponse[i].card.last4, id: clubPaymentMethodsResponse[i].id }); }
          }
        })
      }

      setClubPaymentMethods(_radioItems);
    });
   }*/

  // NOTE: The following helper functions are currently unused but kept for potential future features
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const toggleMeasurementSingle = (_shows: IShowDogs[], _dog: any, _showType: string) => {
    let __shows = JSON.parse(JSON.stringify(_shows));

    for (let s in __shows) {
      if (__shows[s].show_type === _showType) {
        for (let d in __shows[s].dogs) {
          if (__shows[s].dogs[d]._id === _dog._id) {

            if (__shows[s].dogs[d].needs_measurement === undefined) {
              __shows[s].dogs[d].needs_measurement = true;
            } else {
              __shows[s].dogs[d].needs_measurement = !__shows[s].dogs[d].needs_measurement;
            }

            break;
          }
        }
      }
    }

    setShows(__shows);
  }

  const updateDogWheelchairType = (_shows: IShowDogs[], _dog: any, _showType: string, value: string) => {
    let __shows = JSON.parse(JSON.stringify(_shows));

    for (let s in __shows) {
      if (__shows[s].show_type === _showType) {
        for (let d in __shows[s].dogs) {
          if (__shows[s].dogs[d]._id === _dog._id) {
            __shows[s].dogs[d].wheelchair_type = value;
            break;
          }
        }
      }
    }

    setShows(__shows);
  }

  const toggleUsingAgentJoined = (_shows: IShowDogs[], _dog: any, _showType: string) => {
   let __shows = JSON.parse(JSON.stringify(_shows));

   for (let s in __shows) {
     if (__shows[s].show_type === _showType) {
       for (let d in __shows[s].dogs) {
         if (__shows[s].dogs[d]._id === _dog._id) {
           if (__shows[s].dogs[d].using_agent === undefined) {
             __shows[s].dogs[d].using_agent = true;
           } else {
             __shows[s].dogs[d].using_agent = !__shows[s].dogs[d].using_agent;
           }

           // Clear competing_owner_handler when using agent
           if (__shows[s].dogs[d].using_agent) {
             __shows[s].dogs[d].competing_owner_handler = false;
           }

           break;
         }
       }
     }
   }

   setShows(__shows);
  }

  const toggleUsingAgentSingle = (_showIndex: number, _dogIndex: number) => {
     let __shows = JSON.parse(JSON.stringify(shows));

     if (__shows[_showIndex].dogs[_dogIndex].using_agent) {
       __shows[_showIndex].dogs[_dogIndex].using_agent = false;
     } else {
       __shows[_showIndex].dogs[_dogIndex].using_agent = true;
     }

     // Clear competing_owner_handler when using agent
     if (__shows[_showIndex].dogs[_dogIndex].using_agent) {
       __shows[_showIndex].dogs[_dogIndex].competing_owner_handler = false;
     }

     setShows(__shows);
  }

  const toggleUsingNonAgentJoined = (_shows: IShowDogs[], _dog: any, _showType: string) => {
   let __shows = JSON.parse(JSON.stringify(_shows));

   for (let s in __shows) {
     if (__shows[s].show_type === _showType) {
       for (let d in __shows[s].dogs) {
         if (__shows[s].dogs[d]._id === _dog._id) {
           if (__shows[s].dogs[d].using_non_agent === undefined) {
             __shows[s].dogs[d].using_non_agent = true;
           } else {
             __shows[s].dogs[d].using_non_agent = !__shows[s].dogs[d].using_non_agent;
           }

           // Clear competing_owner_handler when using non-agent handler
           if (__shows[s].dogs[d].using_non_agent) {
             __shows[s].dogs[d].competing_owner_handler = false;
           }

           break;
         }
       }
     }
   }

   setShows(__shows);
  }

  const updateAgentJoined = (_shows: IShowDogs[], _dog: any, _showType: string, agent: AgentPayload) => {
    const { name, email, phone, number, address } = agent;
    let __shows = JSON.parse(JSON.stringify(_shows));

    for (let s in __shows) {
      if (__shows[s].show_type === _showType) {
        for (let d in __shows[s].dogs) {
          if (__shows[s].dogs[d]._id === _dog._id) {
            if (name !== undefined) { __shows[s].dogs[d].agent_name = name; }
            if (email !== undefined) { __shows[s].dogs[d].agent_email = email; }
            if (phone !== undefined) { __shows[s].dogs[d].agent_phone = phone; }
            if (number !== undefined) { __shows[s].dogs[d].agent_phone = number; }
            if (address !== undefined) {
              __shows[s].dogs[d].agent_address = { ...(__shows[s].dogs[d].agent_address ?? {}), ...address };
            }
            break;
          }
        }
      }
    }

    setShows(__shows);
   }

  // NOTE: PWDCA Water dog specific information
  const updateSubstituteJoined = (_shows: IShowDogs[], _dog: any, _showType: string, substitute: SubstitutePayload) => {
    const { name, phone, email } = substitute
    let __shows = JSON.parse(JSON.stringify(_shows));

    for (let s in __shows) {
      if (__shows[s].show_type === _showType) {
        for (let d in __shows[s].dogs) {
          if (__shows[s].dogs[d]._id === _dog._id) {
            if (name !== undefined) { __shows[s].dogs[d].substitute_name = name; }
            if (email !== undefined) { __shows[s].dogs[d].substitute_email = email; }
            if (phone !== undefined) { __shows[s].dogs[d].substitute_phone = phone; }
            break;
          }
        }
      }
    }

    setShows(__shows);
  }

  // NOTE: PWDCA Water dog specific information
  const updateDogLegInfo = (_shows: IShowDogs[], _dog: any, _showType: string, leg: string) => {
    let __shows = JSON.parse(JSON.stringify(_shows));

    for (let s in __shows) {
      if (__shows[s].show_type === _showType) {
        for (let d in __shows[s].dogs) {
          if (__shows[s].dogs[d]._id === _dog._id) {
            __shows[s].dogs[d].leg_info = leg;
            break;
          }
        }
      }
    }

    setShows(__shows);
  }

   const updateAgentSingle = (_showIndex: number, _dogIndex: number, agent: AgentPayload) => {
      const { name, number, address } = agent;
      let __shows = JSON.parse(JSON.stringify(shows));

      if (name !== undefined) __shows[_showIndex].dogs[_dogIndex].agent_name = name;
      if (number !== undefined) __shows[_showIndex].dogs[_dogIndex].agent_number = number;
      if (address !== undefined) {
        __shows[_showIndex].dogs[_dogIndex].agent_address = { ...(__shows[_showIndex].dogs[_dogIndex].agent_address ?? {}), ...address };
      }

      setShows(__shows);
   }

  const toggleUsingNonAgentSingle = (_showIndex: number, _dogIndex: number) => {
    let __shows = JSON.parse(JSON.stringify(shows));

    if (__shows[_showIndex].dogs[_dogIndex].using_non_agent) {
      __shows[_showIndex].dogs[_dogIndex].using_non_agent = false;
    } else {
      __shows[_showIndex].dogs[_dogIndex].using_non_agent = true;
    }

    // Clear competing_owner_handler when using non-agent handler
    if (__shows[_showIndex].dogs[_dogIndex].using_non_agent) {
      __shows[_showIndex].dogs[_dogIndex].competing_owner_handler = false;
    }

    setShows(__shows);
  }

  const toggleCompetingOwnerHandlerJoined = (_shows: IShowDogs[], _dog: any, _showType: string) => {
    let __shows = JSON.parse(JSON.stringify(_shows));

    for (let s in __shows) {
      if (__shows[s].show_type === _showType) {
        for (let d in __shows[s].dogs) {
          if (__shows[s].dogs[d]._id === _dog._id) {
            if (__shows[s].dogs[d].competing_owner_handler === undefined) {
              __shows[s].dogs[d].competing_owner_handler = true;
            } else {
              __shows[s].dogs[d].competing_owner_handler = !__shows[s].dogs[d].competing_owner_handler;
            }

            break;
          }
        }
      }
    }

    setShows(__shows);
  }

  const toggleCompetingOwnerHandlerSingle = (_showIndex: number, _dogIndex: number) => {
    let __shows = JSON.parse(JSON.stringify(shows));

    if (__shows[_showIndex].dogs[_dogIndex].competing_owner_handler) {
      __shows[_showIndex].dogs[_dogIndex].competing_owner_handler = false;
    } else {
      __shows[_showIndex].dogs[_dogIndex].competing_owner_handler = true;
    }

    setShows(__shows);
  }

   const toggleEarnTitle = (_shows: IShowDogs[], _dogIndex: number, _showIndex?: number, allShows?: true) => {
    let __shows = JSON.parse(JSON.stringify(_shows));

    if (_showIndex !== undefined) {
      let _dog = __shows[_showIndex].dogs[_dogIndex];

      if (_dog.plan_to_earn_title) {
        _dog.plan_to_earn_title = false;
      } else {
        _dog.plan_to_earn_title = true;
      }
    } else if (allShows) {
      for (let s in __shows) {
        let _dog = __shows[s].dogs[_dogIndex];

        if (_dog.plan_to_earn_title) {
          _dog.plan_to_earn_title = false;
        } else {
          _dog.plan_to_earn_title = true;
        }
      }
    }

    setShows(__shows);
   }

   const getTitleOptions = (showType: string): IOption[] => {
     if (showType === Constants.show_type.fast_dash) return iswsFastDashTitles;
     if (showType === Constants.show_type.cat) return akcCatTitles;
     return akcTitles;
   };

   const getDefaultTitle = (showType: string): IOption => {
     if (showType === Constants.show_type.fast_dash) {
       return { value: "BFAST - 150 pts", id: Constants.isws_fast_dash_titles.bfast };
     }
     if (showType === Constants.show_type.cat) {
       return { value: Constants.akc_cat_titles.ca, id: Constants.akc_cat_titles.ca };
     }
     return { value: "BCAT - 150 pts", id: Constants.akc_fastcat_titles.bcat };
   };
   
   // Get title from dog or use default for current show type
   const getTitle = (dog: IDogInfo, currentShowType: string): IOption => {
     if (dog.title) {
       // FCAT titles are stored as "FCAT1", "FCAT5", etc. — match the base FCAT option
       if (dog.title.toUpperCase().startsWith(Constants.akc_fastcat_titles.fcat)) {
         return { value: "FCAT", id: Constants.akc_fastcat_titles.fcat };
       }
       const titleList = getTitleOptions(currentShowType);
       // Support both historical value-based storage and id-based storage.
       const found = titleList.find((t) => t.value === dog.title || t.id === dog.title);
       if (found) return found;
     }
     return getDefaultTitle(currentShowType);
   };

   // Extract the numeric suffix from an FCAT title string (e.g. "FCAT5" → 5)
   const getFcatNumber = (dog: IDogInfo): number => {
     if (!dog.title) return 1;
     const suffix = dog.title.toUpperCase().replace(Constants.akc_fastcat_titles.fcat, "");
     const parsed = parseInt(suffix, 10);
     return isNaN(parsed) || parsed < 1 ? 1 : parsed;
   };

   const updateTitle = (_shows: IShowDogs[], _dogIndex: number, title: IOption, _showIndex?: number, allShows?: true) => {
    let __shows = JSON.parse(JSON.stringify(_shows));
    // When FCAT is selected from the dropdown, default to "FCAT1" so a number is always stored
    const storedTitle = title.id === Constants.akc_fastcat_titles.fcat ? `${Constants.akc_fastcat_titles.fcat}1` : title.id;

    if (_showIndex !== undefined) {
      let _dog = __shows[_showIndex].dogs[_dogIndex];

      _dog.title = storedTitle;
    } else if (allShows) {
      for (let s in __shows) {
        let _dog = __shows[s].dogs[_dogIndex];

        _dog.title = storedTitle;
      }
    }

    setShows(__shows);
   }

   const updateFcatNumber = (_shows: IShowDogs[], _dogIndex: number, num: number, _showIndex?: number) => {
    let __shows = JSON.parse(JSON.stringify(_shows));
    const clamped = Math.max(1, Math.min(100, num || 1));

    if (_showIndex !== undefined) {
      __shows[_showIndex].dogs[_dogIndex].title = `${Constants.akc_fastcat_titles.fcat}${clamped}`;
    }

    setShows(__shows);
   }
  /* eslint-enable @typescript-eslint/no-unused-vars */

  // On Page Load, Redirect Club Managers, Get Event Data, Shows
  useEffect(() => {
    if (!loaded && eid) {
      apiService.getEventSummaryForHandle(eid).then(async (response) => {
        setEvent(response);
        
        // Check admin status once at the start
        let userIsAdmin = false;
        if (user.user_type === Constants.user_type.club_manager) {
          // Club managers are always admins for their club's events
          // This enables proposed changes when registering on behalf of users
          userIsAdmin = true;
        } else if (user.user_type === Constants.user_type.owner) {
          try {
            const capacitiesResponse = await apiService.getUserCapacities();
            userIsAdmin = capacitiesResponse.capacities.some(capacity => 
              capacity.club_id === response.club._id && capacity.capacities.includes(Constants.capacity_type.admin)
            );
          } catch (error) {
            console.error('Failed to check admin capacity:', error);
          }
        }
        setIsAdmin(userIsAdmin);
        
        // Check volunteer status and settings
        let isVolunteerWithEarlyAccess = false;
        try {
          const [volunteerCheck, volunteerSettings] = await Promise.all([
            apiService.checkIfUserIsVolunteerForClub(response.club_id),
            apiService.getClubVolunteerSettings(response.club_id).catch(() => ({ allow_late_registration: false, allow_early_registration: false }))
          ]);
          setIsVolunteer(volunteerCheck.isVolunteer);
          setVolunteerLateRegAllowed(volunteerSettings.allow_late_registration);
          setVolunteerEarlyRegAllowed(volunteerSettings.allow_early_registration || false);
          isVolunteerWithEarlyAccess = volunteerCheck.isVolunteer && volunteerSettings.allow_early_registration;
        } catch (error) {
          console.error('Failed to check volunteer status:', error);
          setIsVolunteer(false);
          setVolunteerLateRegAllowed(false);
          setVolunteerEarlyRegAllowed(false);
        }
        
        // Check if draft event - allow creator, club managers, owners with admin capacity, and volunteers with early registration
        if (response.status === Constants.event_status.draft) {
          const hasAccess = user._id === response.created_by || userIsAdmin || isVolunteerWithEarlyAccess;
          
          if (!hasAccess) {
            toast.show({
              title: "Event",
              content: "This event hasn't been published yet",
              duration: 10000,
              type: "fail"
            });

            navigate("/");
            return false;
          }
        }

        // Check if user is banned from this club
        // Get on-behalf user info from URL params
        const queryParametersForBan = new URLSearchParams(window.location.search);
        const onBehalfUserId = queryParametersForBan.get("uid");
        
        if (userIsAdmin && onBehalfUserId) {
          // Admin doing on-behalf registration - check if the on-behalf user is banned
          try {
            const banCheck = await apiService.checkIfSpecificUserIsBanned(response.club_id, onBehalfUserId);
            isUserBannedRef.current = banCheck.is_banned;
            setIsUserBanned(banCheck.is_banned);
          } catch (error) {
            console.error('Failed to check ban status for on-behalf user:', error);
          }
        } else if (user.user_type === Constants.user_type.owner && !userIsAdmin) {
          // Regular user - check if they are banned
          try {
            const banCheck = await apiService.checkIfUserIsBanned(response.club_id);
            isUserBannedRef.current = banCheck.is_banned;
            setIsUserBanned(banCheck.is_banned);
          } catch (error) {
            console.error('Failed to check ban status:', error);
            // Continue if check fails - the backend will still enforce bans
          }
        }

        apiService.getEventAddOns(response._id).then((addOnsResponse) => {
          setAddons(addOnsResponse.map((a: any) => { return { ...a, amount: 0 }; }));
        });

        if (user.is_junior) {
          switch (response.sanctioning_club) {
            case Constants.sanctioning_club.UKC:
              if (user.junior_numbers?.filter((n: any) => { return n.name === Constants.sanctioning_club.UKC}).length === 0) {
                toast.show({
                  title: "Registration",
                  content: "You don't have a UKC Junior # attached to your account. Please add it on your account settings page.",
                  duration: 10000,
                  type: "fail"
                });

                navigate("/account");
              }
            break;
            case Constants.sanctioning_club.CKC:
              if (user.junior_numbers?.filter((n: any) => { return n.name === Constants.sanctioning_club.CKC}).length === 0) {
                toast.show({
                  title: "Registration",
                  content: "You don't have a CKC Junior # attached to your account. Please add it on your account settings page.",
                  duration: 10000,
                  type: "fail"
                });

                navigate("/account");
              }
            break;
            case Constants.sanctioning_club.AKC:
              if (user.junior_numbers?.filter((n: any) => { return n.name === Constants.sanctioning_club.AKC}).length === 0) {
                toast.show({
                  title: "Registration",
                  content: "You don't have an AKC Junior # attached to your account. Please add it on your account settings page.",
                  duration: 10000,
                  type: "fail"
                });

                navigate("/account?t=5");
              }
            break;
          }
        }

        const queryParameters = new URLSearchParams(window.location.search);
        const uid = queryParameters.get("uid");
        const uemail = queryParameters.get("uemail");
        const ujunior = queryParameters.get("junior");
        const claimed:boolean = queryParameters.get("claimed") === "true" ? true : false;
        setIsClaimed(claimed);

        // Handle "on behalf" registration for admins
        if (userIsAdmin && uid && uemail && ujunior) {
          setOnBehalf(uid);
          setOnBehalfEmail(uemail);
          setOnBehalfJunior(ujunior);
        } else if (user.user_type === Constants.user_type.club_manager && !(uid && uemail && ujunior)) {
          // Club managers must register on behalf (can't register for themselves)
          toast.show({
            title: "Event Registration",
            content: "Club owners aren't able to register to events",
            duration: 10000,
            type: "fail"
          });

          navigate("/");
        } else if (uid && uemail && ujunior && !userIsAdmin) {
          // Non-admin trying to register on behalf
          toast.show({
            title: "Event Registration",
            content: "You don't have permission to register users for this event",
            duration: 10000,
            type: "fail"
          });
          navigate("/");
        } else if (user.user_type === "") {
          toast.show({
            title: "Event Registration",
            content: "You must be logged in to register to an event",
            duration: 10000,
            type: "fail"
          });

          let _url = window.location.href.split("/e/")[1];

          if (_url) {
            navigate("/login?r=/e/" + _url);
          } else {
            navigate("/login");
          }

          return false;
        }

        if (response.club_id && response._id) {
          if (uid && uemail) {
            getDogs(uid, response.club_id);
          } else {
            getDogs(undefined, response.club_id);
          }

          apiService.getClubEventShows(response.club_id, response._id, {
            sort: [{
              attribute_name: "show_date",
              sort: "asc"
            }]
          }).then((eventResponse) => {
            let _showDogs:IShowDogs[] = [];

            for (let s in eventResponse) {
              _showDogs[s] = { ...eventResponse[s], dogs: [] };
            }

            setShows(_showDogs);

            let _showTypes:string[] = [];

            for (let t in eventResponse) {
              if (!_showTypes.includes(eventResponse[t].show_type)) {
                _showTypes.push(eventResponse[t].show_type);
              }
            }

            _showTypes.sort((a, b) => a.localeCompare(b));
            setShowTypes(_showTypes);
          });
        }
      });

      setJoyrideSteps([{
        disableBeacon: true,
        placement: "top",
        target: ".EventRegister .registerInner .stepWrap .step .stepInner.selectShows",
        content: "This is a list of show types that this event is running trials for. Clicking on a show type will expand a list of trials the event is running for the selected show type."
      }, {
        disableBeacon: true,
        placement: "top",
        target: ".ShowTime",
        content: "Click on each dog's image to add or remove them from each trial. To add a new dog to your account, click New Dog."
      }, {
        disableBeacon: true,
        placement: "top",
        target: ".EventRegister .registerInner .stepWrap .actions button.joy",
        content: "When you're finished selecting all dogs for all trials you wish to enter, click next to proceed to the next step.  Make sure you've included all your dogs so you only have to register once!"
      }]);
    }

    setLoaded(true);
  }, [loaded, eid, getDogs, navigate, user.user_type, user.email, onBehalf, onBehalfEmail, user._id, user.is_junior, user.junior_numbers]);

  const findFirstShowWithDogs = useCallback(() => {
    let hasDogs = shows.filter((s: any) => { return s.dogs.length > 0; });

    if (hasDogs.length > 0) {
      let showIndex = showTypes.indexOf(hasDogs[0].show_type);
      return showIndex;
    }

    return 0;
  }, [showTypes, shows]);

  const prevShows = helperService.usePrevious(shows);
  const prevStep = helperService.usePrevious(currentStep);

  // Scroll to top when step changes (e.g. clicking Next/Previous)
  const scrollToTopOnModalCloseRef = React.useRef(false);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Initialises the registration transaction (creates the payment intent or free transaction).
  // Extracted so it can be retried after a card is added for AKC waitlisted entries.
  const initRegistrationPaymentIntent = useCallback(async (overridePaymentMethodType?: string) => {
    if (!event || !regPayload || onBehalf !== undefined || onBehalfEmail !== undefined) return;

    // Use the override when provided (avoids stale closure on paymentChoice after a state set)
    const effectivePaymentChoice = overridePaymentMethodType || paymentChoice;

    const registrationPayload: any = {
      registration_kind: Constants.registration_kind.owner,
      payment_method_type: Constants.payment_method_type.card,
      owner_id: user._id,
      "dog_show_registrations": regPayload
    };

    // Pay Club Directly path — skip Stripe
    if (effectivePaymentChoice === 'pay_club_directly') {
      registrationPayload.payment_method_type = Constants.payment_method_type.pay_club_directly;
    }

    // Bank account path — uses ACSS (CA) or ACH (US) PaymentIntent
    if (effectivePaymentChoice === 'bank_account') {
      registrationPayload.payment_method_type = Constants.payment_method_type.bank_account;
    }

    if (currentDiscountCode) {
      registrationPayload.discount_code = currentDiscountCode;
    }

    // Create the transaction with the add-ons already in the cart. Add-ons are
    // picked on the payment step, so a transaction created when the user
    // toggles to Bank Account later would otherwise start without them.
    const selectedAddons = addons.filter((a: IAddonUI) => a.amount > 0);
    if (selectedAddons.length > 0) {
      registrationPayload.add_ons = selectedAddons.map((a: IAddonUI) => ({
        add_on_id: a._id,
        quantity: a.amount,
      }));
    }

    try {
      const response = await apiService.createRegistrationPaymentIntent(event._id, registrationPayload);

      // Record this method's ids synchronously (ref, not state) so any summary
      // request that fires right after — before React re-renders — already sees
      // them, and so a request for the OTHER method never picks them up.
      methodIdsRef.current[effectivePaymentChoice] = {
        transactionId: response?.transaction_id,
        paymentIntentId: response?.payment_intent_id,
      };

      // Now that this method has a transaction, re-run its summary WITH the ids so
      // the transaction + PaymentIntent are synced to the full current cart
      // (discount, add-ons). Any summary that fired for this method before the
      // transaction existed was quote-only and persisted nothing.
      if (
        response?.transaction_id &&
        (effectivePaymentChoice === 'card' || effectivePaymentChoice === 'bank_account')
      ) {
        updatePaymentSummary(currentDiscountCode, addons, undefined, effectivePaymentChoice);
      }

      if (response?.client_secret) {
        setStripeOptions({ clientSecret: response.client_secret });
        setPaymentIntentId(response.payment_intent_id);
        // Only persist the "saved" card options for card payments — bank account
        // has its own PI and must not overwrite the card's saved client secret.
        if (effectivePaymentChoice !== 'bank_account') {
          setSavedStripeOptions({ clientSecret: response.client_secret });
          setSavedPaymentIntentId(response.payment_intent_id);
        } else {
          setSavedBankStripeOptions({ clientSecret: response.client_secret });
        }
      }

      if (response?.transaction_id) {
        setTransactionId(response.transaction_id);
        if (effectivePaymentChoice !== 'bank_account') {
          setSavedTransactionId(response.transaction_id);
        } else {
          setSavedBankTransactionId(response.transaction_id);
        }
      }

      if (response?.is_pay_club_directly) {
        setIsPayClubDirectlyTransaction(true);
      }
    } catch (e: any) {
      const msg = e.response?.data?.message;
      // Suppress the payment-method-required error here — the frontend gate handles it
      if (e.response?.data?.code !== "ERROR.REGISTRATION.PAYMENT_METHOD_REQUIRED") {
        toast.show({
          title: "Event Register",
          content: msg ?? "Something went wrong when attempting to register this user",
          duration: 10000,
          errorDetails: e,
          type: "fail"
        });
      }
    }
  }, [event, regPayload, onBehalf, onBehalfEmail, user._id, currentDiscountCode, setStripeOptions, paymentChoice, addons, updatePaymentSummary]);

  // On Payment Step, Get Payment Intent, Setup Payment Options
  useEffect(() => {
    if (prevStep !== currentStep) {
      if (prevStep === 0 && currentStep === 1) {
        let newIndex = findFirstShowWithDogs();
        setCurrentShow(newIndex);
      }

      if (isPaymentStep() && event && regPayload) {
        if (onBehalf === undefined && onBehalfEmail === undefined) {
          // Reset pay_club_directly state on step change so the user can pick freshly
          setIsPayClubDirectlyTransaction(false);
          setPaymentChoice('card');
          // Reset bank account choice on step change
          setSavedStripeOptions(undefined);
          setSavedTransactionId(undefined);
          setSavedPaymentIntentId(undefined);
          setSavedBankStripeOptions(undefined);
          setSavedBankTransactionId(undefined);
          setPerMethodTotals({});
          // Fresh transactions are created for this visit to the payment step —
          // drop any ids from a previous visit so no summary request can target
          // a transaction the user has navigated away from.
          methodIdsRef.current = {};
          summarySequenceRef.current = 0;
          // Pass 'card' explicitly: setPaymentChoice('card') above has not
          // committed yet, so the closure's paymentChoice may still be the
          // previous visit's 'bank_account' and would create the wrong transaction.
          initRegistrationPaymentIntent('card');
          // Pre-fetch both payment method totals so each option always shows its own price.
          // Card is non-silent (sets the active paymentSummary + caches perMethodTotals['card']).
          // Bank is silent (only caches perMethodTotals['bank_account'] without touching paymentSummary).
          updatePaymentSummary(currentDiscountCode, addons, undefined, 'card');
          updatePaymentSummary(currentDiscountCode, addons, undefined, 'bank_account', true);
        } else {
          if (event && !lotteryDrawOpen && onBehalf !== undefined && onBehalfEmail !== undefined) {
            apiService.createClubPaymentMethod(event.club_id).then((response) => {
              if (response && response.client_secret) {
                setStripeOptions({
                  clientSecret: response.client_secret
                });
              }
            });
          }
        }
      }
    }
  }, [currentStep, prevStep, regPayload, event, setStripeOptions, onBehalf, onBehalfEmail, onBehalfPaymentMethod, findFirstShowWithDogs, user._id, currentDiscountCode, isPaymentStep, initRegistrationPaymentIntent]);

  useEffect(() => {
    if (isPaymentStep() && prevStep === 4 && stripeOptions === undefined /*&& clubPaymentMethods.length === 0*/) {
      if (event && !lotteryDrawOpen && onBehalf !== undefined && onBehalfEmail !== undefined) {
        apiService.createClubPaymentMethod(event.club_id).then((createResponse) => {
          if (createResponse && createResponse.client_secret) {
            setStripeOptions({
              clientSecret: createResponse.client_secret
            });
          }
        });
      }
    }
    // NOTE: reset the flag when picking dog for registration
    if (currentStep === 0) {
      setUsingTeamDogs(false);
    }
  }, [currentStep, prevStep, stripeOptions, setStripeOptions, setUsingTeamDogs, event, onBehalf, onBehalfEmail, isPaymentStep])

  // For AKC events: check whether the user has a payment method on file.
  // Depends on waitlistsPaymentElements so it re-runs once the payment summary
  // is loaded (which is when we know for sure there are waitlisted entries).
  useEffect(() => {
    if (!isPaymentStep() || !event || event.sanctioning_club !== Constants.sanctioning_club.AKC) return;
    if (onBehalf !== undefined) return;
    if (waitlistsPaymentElements.length === 0) return;

    (async () => {
      try {
        const { stripe_customer_id } = await apiService.ensureStripeCustomer();
        setStripeCustomerId(stripe_customer_id);
        const methods = await apiService.getSelfPaymentMethods();
        setCardOnFileMethods(methods);
      } catch (_err) {
        setCardOnFileMethods([]);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, event, waitlistsPaymentElements]);

  // If the user had Bank Account selected but entries are now waitlisted,
  // switch back to card — bank accounts can't be used for off-session future charges.
  useEffect(() => {
    if (waitlistsPaymentElements.length > 0 && paymentChoice === 'bank_account') {
      setPaymentChoice('card');
      setStripeOptions(savedStripeOptions);
      setTransactionId(savedTransactionId);
      setPaymentIntentId(savedPaymentIntentId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitlistsPaymentElements]);

  // When all entries are waitlisted, fetch a preview summary (need_check_waitlists: false)
  // so the cart can show the actual entry prices and fees instead of CA$0.00.
  useEffect(() => {
    if (!event || !regPayload || waitlistsPaymentElements.length === 0 || onBehalf !== undefined || onBehalfEmail !== undefined) {
      setPreviewPaymentSummary(null);
      return;
    }
    const previewPayload: any = {
      registration_kind: Constants.registration_kind.owner,
      payment_method_type: Constants.payment_method_type.card,
      owner_id: user._id,
      dog_show_registrations: regPayload,
      need_check_waitlists: false,
    };
    if (currentDiscountCode) previewPayload.discount_code = currentDiscountCode;
    apiService.getRegistrationPaymentSummary(event._id, previewPayload)
      .then((response) => { if (response) setPreviewPaymentSummary(response); })
      .catch(() => { setPreviewPaymentSummary(null); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitlistsPaymentElements, regPayload, event?._id]);

  // On Dog Details, Update Dollar Amount on Dog Details Change
  useEffect(() => {
    const showsChanged = JSON.stringify(prevShows) !== JSON.stringify(shows);
    if (showsChanged || addons.length > 0 || currentDiscountCode) {
      updatePaymentSummary(currentDiscountCode, addons);
    }
  }, [shows, prevShows, addons, currentDiscountCode, updatePaymentSummary]);

  // When fee overrides change (on-behalf entries), refresh the payment summary
  useEffect(() => {
    if (onBehalf !== undefined) {
      updatePaymentSummary(currentDiscountCode, addons);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feeOverrides]);

  const prevDogs = helperService.usePrevious(dogs);

  useEffect(() => {
    if (prevDogs !== dogs) {
      let _shows:IShowDogs[] = JSON.parse(JSON.stringify(shows));

      for (let s in _shows) {
        for (let d in _shows[s].dogs) {
          let findDog = dogs.filter((dog: IDogInfo) => dog._id === _shows[s].dogs[d]._id);

          if (findDog.length > 0) {
            _shows[s].dogs[d] = findDog[0];
          }
        }

        setShows(_shows);
      }
    }
  }, [prevDogs, dogs, setShows, shows]);

  // NOTE: init class for show types w/o any dog detail step inputs
  useEffect(() => {
    for (let s in shows) {
      const showType = shows[s].show_type;
      if (isNoQuestionsShow(showType)) {
        for (let d in shows[s].dogs) {
          const dog = shows[s].dogs[d];
          const classes: IDogClass[] = [{
            show_element: showType,
            level: showType,
          }];

          if (!dog.classes?.length) {
            updateDogInfoAllShowTypes(showType, classes, dog);
          }
        }
      }
    }
  }, [dogs, shows, showTypes, updateDogInfoAllShowTypes])

  // Expand all show types and show times by default when they're loaded
  useEffect(() => {
    if (showTypes.length > 0 && shows.length > 0) {
      // Expand all show types (by index)
      const allShowTypeIndices = new Set(orderedShowTypes.map((_, index) => index));
      setExpandedShowTypes(allShowTypeIndices);
      
      // Expand all show times/trials (by index)
      const allShowIndices = new Set(shows.map((_, index) => index));
      setExpandedShowTimes(allShowIndices);
    }
  }, [orderedShowTypes, shows.length, showTypes.length]);

  const getModalContent = () => {
    switch (modalContent) {
      case "dogForm":
        return <DogForm 
          clubId={event?.club_id} 
          onBehalf={onBehalf} 
          onBehalfEmail={onBehalfEmail}
          isAdmin={isAdmin}
          eventId={event?._id}
          onFinish={(dog: IDog) => {
          setModalContent("");
          toggle();
          handleDogUpdated();

          // NOTE: do not select dog for registration when adding team member for AKC
          if (!usingTeamDogs) {
            selectDog(currentShowTime, dog);
          }
        }}
      />;
    case "confirmRemoveCard":
      return <Confirm
        message={cardToDelete !== undefined ? <p>Are you sure you want to remove {cardToDelete.label}</p> : <></>}
        onConfirm={() => {
          if (event && cardToDelete) {
            apiService.deleteClubPaymentMethod(event.club_id, cardToDelete.id).then((response) => {
              apiService.getClubPaymentMethods(event.club_id).then((clubPaymentMethodsResponse) => {
                // setClubPaymentMethods(clubPaymentMethodsResponse);
                setStripeOptions(undefined);
              });

              setCardToDelete(undefined);
              toggle();
            });
          }
        }}
        onCancel={() => { toggle(); }}
      />
    }

    return (
      <div className="twoDogsInner">
        <div className="title">Registration</div>
        <div className="message">{twoDogMsg}</div>
        <div className="actions">
          <Button onClick={() => { setModalContent(""); toggle(); }}>Back</Button>
          <Button onClick={() => { scrollToTopOnModalCloseRef.current = true; setCurrentStep(currentStep + 1); setModalContent(""); toggle(); }}>Yes</Button>
        </div>
      </div>
    );
  }

  const validateChequeNumber = () => {
    if (onBehalfPaymentMethod.id === Constants.payment_method_type.cheque && !chequeNumber) {
      return { valid: false, message: "Please enter a cheque number" };
    }
    return { valid: true };
  }

  const registerOnBehalf = () => {
    if (event && regPayload && onBehalf && onBehalfEmail) {
      setLoadingBehalf(true);

      let validAddress:IAddressError = isValidAddress(userAddress);
      let validPhone:IAddressError = isValidPhone(userPhone);
      let validChequeNumber = validateChequeNumber();

      if (validAddress.valid && validPhone.valid && validChequeNumber.valid) {
        // Prepare add-ons
        let addOns: EventAddOnDto[] = [];
        if (addons) {
          let hasAddons = addons.filter((a: any) => a.amount > 0);

          if (hasAddons.length > 0) {
            addOns = hasAddons.map((a: IAddonUI) => {
              return {
                add_on_id: a._id,
                quantity: a.amount
              };
            });
          }
        }

        // Update user info based on user type and permissions
        const updateUserPromise = user.user_type === Constants.user_type.club_manager 
          ? apiService.updateUserByClubManager(onBehalf, {
              address: userAddress,
              phone: userPhone
            })
          : apiService.updateUserByAdmin(onBehalf, {
              address: userAddress,
              phone: userPhone
            }, event.club._id);

        updateUserPromise
          .then(() => {
            // Proceed with registration only if user update succeeded
            apiService.createRegistrationByClubManager(event._id, {
              registration_kind: Constants.registration_kind.club,
              //payment_method_id: clubPaymentMethods[selectedPaymentMethod].id,
              payment_method_type: onBehalfPaymentMethod.id,
              cheque_number: chequeNumber,
              date_received: dateReceived,
              "dog_show_registrations": regPayload,
              "owner_id": onBehalf,
              discount_code: currentDiscountCode || undefined,
              add_ons: addOns,
              club_points_to_redeem: paymentSummary?.club_points_redeemed || 0,
              override_note: overrideNote || undefined,
            }).then((response) => {
              navigate(getRedirectUrl(`/e/${eid}`, `dids=${getDogIds()}&redirect_status=succeeded&behalf=true`));
            }).catch((e: any) => {
              toast.show({
                title: "Event Register",
                content: e.response?.data?.message ? e.response?.data?.message : "Something went wrong when attempting to register this user",
                duration: 10000,
                errorDetails: e,
                type: "fail"
              });
            }).finally(() => {
              setLoadingBehalf(false);
            });
          })
          .catch((error) => {
            // Handle user update failure
            console.error('Failed to update user info during behalf registration:', error);
            toast.show({
              title: "Registration Failed",
              content: "Could not update user address and phone information. Please try again.",
              duration: 10000,
              errorDetails: error,
              type: "fail"
            });
            setLoadingBehalf(false);
          });
      } else {
        [validChequeNumber, validPhone, validAddress].filter((validator) => !validator.valid).forEach((validator) => {
          toast.show({
            title: "Event Register",
            content: validator.message ?? "",
            duration: 10000,
            type: "fail"
          });
        });
        setLoadingBehalf(false);
      }
    }
  }

  const pModalContent = helperService.usePrevious(modalContent);

  useEffect(() => {
    if (pModalContent !== modalContent) {
      if (modalContent !== "") {
        toggle();
      }
    } else {
      if (!isShown) {
        setModalContent("");
      }
    }
  }, [isShown, modalContent, pModalContent, setModalContent, toggle]);

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: paymentSummary.currency.toUpperCase() || "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  const getWaiverTitle = (sanctioningClub: string) => {
    switch (sanctioningClub) {
      case Constants.sanctioning_club.UKC:
        return "UKC Waiver";
      case Constants.sanctioning_club.ABI:
        return "ABI Waiver";
      case Constants.sanctioning_club.AKC:
        return "AKC Waiver";
      case Constants.sanctioning_club.PWDCA:
        return "Participation Agreement";
      case Constants.sanctioning_club.GENERAL:
        return "General Waiver";
      case Constants.sanctioning_club.C_WAGS:
        return "C-Wags Waiver";
      case Constants.sanctioning_club.BHA:
        return "Barn Hunt Waiver";
      case Constants.sanctioning_club.ASFA:
        return "ASFA Waiver";
      case Constants.sanctioning_club.FH:
        return "4-H Waiver";
      case Constants.sanctioning_club.CKC:
        return "CKC Entry Certification";
    }

    return "Waiver";
  }

  const getSignoffText = (sanctioningClub: string) =>
  {

    switch (sanctioningClub) {
      case Constants.sanctioning_club.AKC:
        if (onBehalf)
          return (event?.club_name || "The club") + " has received a signed copy of this waiver from the registrant."
        else
          return "I, "+ user.display_name +", have read and agree to the waiver on this page."
      case Constants.sanctioning_club.UKC:
        if (onBehalf)
          return (event?.club_name || "The club") + " has received a signed copy of this waiver from the registrant."
        else
          return "I, "+ user.display_name +", have read and agree to the waiver on this page. I swear that this dog is not being handled by a professional handler in Conformation."
      case Constants.sanctioning_club.ABI:
        if (onBehalf)
          return (event?.club_name || "The club") + " has received a signed copy of this waiver from the registrant."
        else
          return "I, "+ user.display_name +", have read and agree to the waiver on this page."
      case Constants.sanctioning_club.PWDCA:
        if (onBehalf)
          return (event?.club_name || "The club") + " has received a signed copy of this waiver from the registrant."
        else
          return "I, "+ user.display_name +", have read and agree to the waiver on this page."
      case Constants.sanctioning_club.ISWS:
        if (onBehalf)
          return (event?.club_name || "The club") + " has received a signed copy of this waiver from the registrant."
        else
          return "I, "+ user.display_name +", have read and agree to the waiver on this page."
      case Constants.sanctioning_club.CPE:
        if (onBehalf)
          return (event?.club_name || "The club") + " has received a signed copy of this waiver from the registrant."
        else
          return "I, "+ user.display_name +", have read and agree to the waiver on this page."
      case Constants.sanctioning_club.GCR:
        if (onBehalf)
          return (event?.club_name || "The club") + " has received a signed copy of this waiver from the registrant."
        else
          return "I, "+ user.display_name +", have read and agree to the waiver on this page."
      case Constants.sanctioning_club.GENERAL:
        if (onBehalf)
          return (event?.club_name || "The club") + " has received a signed copy of this waiver from the registrant."
        else
          return "I, "+ user.display_name +", have read and agree to the waiver on this page."
      case Constants.sanctioning_club.C_WAGS:
        if (onBehalf)
          return (event?.club_name || "The club") + " has received a signed copy of this waiver from the registrant."
        else
          return "I, "+ user.display_name +", have read and agree to the waiver on this page."
      case Constants.sanctioning_club.BHA:
        if (onBehalf)
          return (event?.club_name || "The club") + " has received a signed copy of this waiver from the registrant."
        else
          return "I, "+ user.display_name +", have read and agree to the waiver on this page."
      case Constants.sanctioning_club.ASFA:
        if (onBehalf)
          return (event?.club_name || "The club") + " has received a signed copy of this waiver from the registrant."
        else
          return "I, "+ user.display_name +", have read and agree to the waiver on this page."
      case Constants.sanctioning_club.FH:
        if (onBehalf)
          return (event?.club_name || "The club") + " has received a signed copy of this waiver from the registrant."
        else
          return "I, "+ user.display_name +", have read and agree to the waiver on this page."
      case Constants.sanctioning_club.CKC:
        if (onBehalf)
          return (event?.club_name || "The club") + " has received a signed copy of this certification from the registrant."
        else
          return "I, "+ user.display_name +", have read and agree to the certification on this page."
    }

    return "";
  }

  const getSignoffTextEventWaiver = () =>
  {
    if (onBehalf)
      return (event?.club_name || "The club") + " has received a signed copy of this waiver from the registrant."
    else
      return "I, "+ user.display_name +", have read and agree to the waiver on this page."
  }


  const getArbitrationAddress = () => {
    if (!event.arbitration_address) return "";

    const { city, state } = event.arbitration_address;
    return [city, state].join(", ");
  }

  // CKC show types that offer an agent/handler on the entry form
  const ckcAgentShowTypes: string[] = [
    Constants.show_type.conformation,
    Constants.show_type.obedience,
    Constants.show_type.rally_obedience,
    Constants.show_type.scent_detection,
  ];

  const ckcRulebookByShowType: { [showType: string]: string } = {
    [Constants.show_type.conformation]: "Conformation Show Rules & Regulations",
    [Constants.show_type.obedience]: "Obedience Trial Rules & Regulations",
    [Constants.show_type.rally_obedience]: "Rally Obedience Trial Rules & Regulations",
    [Constants.show_type.scent_detection]: "Scent Detection Trial Rules & Regulations",
  };

  // Names the CKC rulebook for each show type the exhibitor is entering (certification paragraph three)
  const getCkcRulebooks = () => {
    const enteredShows = shows.filter((s: IShowDogs) => (s.dogs || []).some((d) => (d.classes || []).length > 0));
    const sourceShows = enteredShows.length > 0 ? enteredShows : shows;
    const rulebooks = Array.from(new Set(
      sourceShows
        .map((s: IShowDogs) => ckcRulebookByShowType[s.show_type])
        .filter((rulebook): rulebook is string => !!rulebook)
    ));

    if (rulebooks.length === 0) {
      return ckcRulebookByShowType[Constants.show_type.conformation];
    }

    return rulebooks.join(" and ");
  };

  const getWaiverText = (sanctioningClub: string) => {
    switch (sanctioningClub) {
      case Constants.sanctioning_club.UKC:
        return <>
          <p>All events are held under the Official Rules and Regulations of the United Kennel Club. Absolutely no illegal drugs or firearms will be allowed on the grounds or in the buildings on the day of a UKC Licensed event. UKC, its agents and employees, and the host club assume no responsibility for any loss, damage, or injury sustained by spectators or by exhibitors and handlers, or to any of their dogs or property, and further assume no responsibility for injury to children not under the control of their parents or guardians. </p>
          <p>UKC and the host club are not responsible for loss, accidents or theft. By signing this form, I hereby agree to release, waive, discharge and covenant not to sue UKC, its judges, officers, employee and agents and the host club from liability arising from  any claim, action, or lawsuit, including the negligence of the UKC, its judges, officers, employee and agents, and further agree to indemnify and hold UKC, the host club and any approved UKC Judge harmless from any claims, actions or lawsuits, including attorneys' fees, resulting from my participation in this event, and any action, decision or judgment made by any UKC or host club representative or approved Judge under the official UKC Rules and Regulations governing this event.</p>
          <p>I acknowledge that the current Official UKC Rules and Regulations (Agility, Conformation, Dock Jumping, Drag Racing,  Junior Showmanship, Lure Coursing, Obedience, Rally Obedience, Weight Pull, Precision Coursing, Nosework) have been made available to me, and that I am familiar with their contents.</p>
          <p><span>My signature indicates that I understand and agree to the above and to abide by all of the current Official UKC Rules and Regulations.</span> I have read this waiver of liability, assumption of risk, and indemnity agreement, fully understand its terms, and understand that I am giving up substantial rights, including my right to sue. I acknowledge that I am signing the agreement freely and voluntarily and intend by my signature to be a complete and unconditional release of all liability to the greatest extent allowed by law.</p>
        </>;
      case Constants.sanctioning_club.ABI:
        return <>
          <p>I (we) acknowledge that the Rules and Regulations of All Breed Independent Dog Shows (ABIDS) have been made available to me (us) on the website, and that I am (we are) familiar with the contents. I (we) agree that All Breed Independent Dog Shows (ABIDS) has the right to refuse this entry for cause, which the club shall deem to be sufficient. In consideration of the acceptance of this entry and of the holding of the show  and the  opportunity to have the dog judged and to win prize, ribbons or trophies, I (we) agree to hold All Breed Independent Dog Shows, Inc., their members, directors, governors, officers, agents, superintendents. show secretary or ring stewards and the owner or lessor of the premises and any employees of the aforementioned parties, harmless from any claim for loss or injury which may be alleged to have been caused directly or indirectly to any person or thing by the act of a dog while in or upon the show premises or grounds or near any entrance thereto, and I (we) personally assume all responsibility and liability for any such claim, and I (we) further agree to hold the aforementioned parties harmless from any claim for loss of a dog by disappearance, theft, death, or otherwise, and from any claim for damage or injury to the dog, whether such loss, disappearance, theft damage or injury, be caused or alleged to be caused by the negligence of All Breed Independent Dog Shows (ABIDS) or any of the parties aforementioned, or by the negligence of any other person, or any other cause or causes.</p>
          <p>I (we) hereby assume the sole responsibility for and to indemnify and save the aforementioned parties harmless from any and all loss and expenses (including legal fees) by reason of the liability imposed by law upon any of the aforementioned parties for damage because of bodily injuries, including death at any time resulting there from, sustained by any person or persons, including myself (ourselves), or on account of damage to property, arising out of or in consequence of my (our) participation in this show, howsoever such injuries, death or damage to property may be caused and whether or not the same may have been caused or may be alleged to have been caused by negligence of the aforementioned parties or any of their employees or agents, or any other persons.</p>
          <p>Judges may not judge dogs owned or co- owned by them.</p>
          <p>Entry fees will not be refunded in the event that a dog is, disqualified, or barred from competition by action of the Show Chairman or Show Secretary. All Breed Independent Dog Shows (ABIDS) reserves the right to refuse entries. Poor sportsmanship will not be tolerated at shows. If entered/paid and you are not able to attend the show monies will not be refunded to you & monies may not be put towards another show. If because of riots, civil disturbances or other acts beyond control of the management it is impossible to open or to complete the show, no entry fees will be refunded. Payment must be done by electronic transfer to dogsatoxford@gmail.com (Oxford Dog Sports) prior to the start of judging.</p>
          <p>Best of Breed Class:  Best of Breed competition is only for dogs that are ABIDS & FCI Champions a copy of FCI Championship must accompany your Entry Form. If according to owners or agents records your dog has completed the requirements for ABIDS Championship but Championship has not yet been confirmed by ABIDS office yet your win may be forfeited if no ABIDS Championship is actually acquired.</p>
        </>;
      case Constants.sanctioning_club.AKC:
        return <>
          <p>I (we) agree that the club holding this event has the right to refuse this entry for cause which the club shall deem sufficient.</p>
          <p>In consideration of the acceptance of this entry and of the holding of this event and of the opportunity to have the dog judged and to win prizes, ribbons, or trophies, I (we) agree to hold the AKC, the event-giving club, their members, directors, governors, officers, agents, superintendents or event secretary and the owner and/or lessor of the premises and any provider of services that are necessary to hold this event and any employees or volunteers of the aforementioned parties, and any AKC approved judge, judging at this event, harmless from any claim for loss or injury which may be alleged to have been caused directly or indirectly to any person or thing by the act of this dog while in or about the event premises or grounds or near any entrance thereto, and I (we) personally assume all responsibility and liability for any such claim; and I (we) further agree to hold the aforementioned parties harmless from any claim for loss, injury or damage to this dog.</p>
          <p>Additionally, I (we) hereby assume the sole responsibility for and agree to indemnify, defend and save the aforementioned parties harmless from any and all loss and expense (including legal fees) by reason of the liability imposed by law upon any of the aforementioned parties for damage because of bodily injuries, including death at any time resulting therefrom, sustained by any person or persons, including myself (ourselves), or on account of damage to property, arising out of or in consequence of my (our) participation in this event, however such, injuries, death or property damage may be caused, and whether or not the same may have been caused or may be alleged to have been caused by the negligence of the aforementioned parties or any of their employees, agents, or any other persons.</p>
          <p>I (WE) AGREE THAT ANY CAUSE OF ACTION, CONTROVERSY OR CLAIM ARISING OUT OF OR RELATED TO THE ENTRY, EXHIBITION OR ATTENDANCE AT THE EVENT BETWEEN THE AKC AND THE EVENT-GIVING CLUB (UNLESS OTHERWISE STATED IN ITS PREMIUM LIST) AND MYSELF (OURSELVES) OR AS TO THE CONSTRUCTION, INTERPRETATION AND EFFECT OF THIS AGREEMENT SHALL BE SETTLED BY ARBITRATION PURSUANT TO THE APPLICABLE RULES OF THE AMERICAN ARBITRATION ASSOCIATION. HOWEVER, PRIOR TO ARBITRATION ALL APPLICABLE AKC BYLAWS, RULES, REGULATIONS AND PROCEDURES MUST FIRST BE FOLLOWED AS SET FORTH IN THE AKC CHARTER AND BYLAWS, RULES, REGULATIONS, PUBLISHED POLICIES AND GUIDELINES.</p>
        </>;
      case Constants.sanctioning_club.PWDCA:
        return <>
          <p><span>Please sign this Participation Agreement to complete your Entry, Match registration and/or Trial Volunteer signup form.</span>
          </p>
          <p><span><b>Conditions:</b></span><span>&nbsp;These trials are open to purebred Portuguese Water Dogs. I/ we understand that the dogs must be in good health, proper fitness and condition for the test they are about to enter and must be at least 6 months of age in order to compete in the water trials. I/we agree to these terms. I/we accept full responsibility for the care of my dog(s) during the water trial.</span>
          </p>
          <p><span><b>Special Rules:</b></span><span>&nbsp;The water trial is located within the confines of {event.address.line1}. I/we agree to become familiar with and adhere to all site rules and regulations including but not limited to the following:</span>
          </p>
          <ul>
            <li><span>Dogs must be on leash or confined to a crate at all times except when participating in practice sessions or when actually being judged.</span>
            </li>
            <li><span>If my dog soils the grounds, I am responsible for cleanup and disposal.</span>
            </li>
            <li><span>Nothing (dogs, banners, etc.) is to be attached to any tree in the park.</span>
            </li>
          </ul>
          <p><span><b>General Rules:</b></span><span>&nbsp;The Portuguese Water Dog Club of America, Inc., (hereinafter "PWDCA") rules, regulations, policies and guidelines for water trial participants (hereinafter "the rules") are in the current PWDCA Water Trial Manual available on the Web site, </span><span><a href="https://www.google.com/url?q=http://www.pwdca.org/&amp;sa=D&amp;source=editors&amp;ust=1741170302741104&amp;usg=AOvVaw3ScQD_jqXIDIVrhN2DqDjJ">http://www.pwdca.org/</a></span><span>. I/we have received the current Manual and/or have read the rules via the Web site and I/we agree to obey the rules.</span>
          </p>
          <p><span><b>Hold-harmless:</b></span><span>&nbsp;I/we agree to hold the PWDCA and the event-giving club, their members, directors, officers, agents, and judge(s) at this event, the owner and/or lessor of the premises, any provider of services for this event and any employees or volunteers of any of the aforementioned parties, harmless from any claim for loss, injury, or illness which may be alleged to have been caused directly or indirectly to any person, animal or thing by the act of my dog while in or upon the trial grounds or while entering or leaving same, and I/we further agree to hold the aforementioned parties harmless from any claim for loss, disappearance or theft of, or damage injury or illness to myself, or my dog, whether such loss, disappearance, theft, damage, injury or illness is caused or alleged to be caused by the negligence of the club or any other parties aforementioned, by the negligence of any other person, or by any other cause or causes.</span></p>

          <p><span><b>Indemnification:</b></span><span>&nbsp;I/we hereby assume the sole responsibility for and agree to indemnify and save the aforementioned parties harmless from any and all loss and expense (including legal fees) by reason of the liability imposed by law upon any of the aforementioned parties for damage because of bodily injuries, including death at any time resulting there from, sustained by any person or persons, including myself (ourselves) or on account of damage to property, arising out of or in consequence of my (our) participation in this water trial, howsoever such same may have been caused or may be alleged to have been caused by negligence of the aforementioned parties or any of their employees or agents, or any other persons.</span></p>

          <p><span><b>Arbitration:</b></span><span>&nbsp;Notwithstanding any of the above, in the event that I/we or my dog(s) suffer any damage, injury, illness, or loss (including the death of the dog(s) arising from the water trial), I/we agree that any claim arising out of or related to the entry, exhibition or attendance at the event or as to the construction, interpretation and effect of this agreement shall be settled by arbitration pursuant to the applicable rules of the American</span></p>
          <p><span><b>Arbitration Association:</b> I/we agree that any such arbitration will take place in {getArbitrationAddress()} within 60 days of the incident giving rise to the claim, or at such other time and place as the parties may agree. I/we agree to pay a prorated share of the costs of any such arbitration.</span></p>

          <p><span>I/we agree that these clubs have the right to refuse this entry or any entry for any cause which they deem to be sufficient.</span></p>
          <p><span>I hereby certify I am the actual owner of the dog or duly authorized agent of or handler for the actual owner whose name has been entered above. I have read and agree to be bound by the agreement above as well as the stipulations contained in the premium list plus any directive received from any official involved in staging the event. I certify and represent that the dog entered is over 6 months of age (at the time of this event) and is not a hazard to persons or other dogs.</span></p>
          <p><span>*If the handler and/or substitute steward named herein is under 18 years of age (must be at least 9 or older), this entry form must be signed and acknowledged by his/her legal guardian. Parent or legal guardian must accompany the minor and remain on site during the event.</span></p>
        </>;
      case Constants.sanctioning_club.ISWS:
        return <>
          <p><strong>CSWS/ISWS Event Waiver and Release of Liability</strong></p>
          <p>All events are held under the Official Rules and Regulations of the International Silken Windhound Society (ISWS). No alcohol, illegal drugs or firearms are permitted on the grounds or in the event area.</p>
          <p>By entering this event, I understand and agree that CSWS, ISWS, its officers, judges, volunteers, agents, and the hosting club are not responsible for any injury, loss, or damage to myself, my dog(s), my property, or any guests I bring. This includes any accidents, theft, or injuries that may occur at or during the event. I also take full responsibility for any children in my care.</p>
          <p>I hereby release and agree not to hold liable CSWS, ISWS, its judges, officers, volunteers, agents, and the host club for any claims, actions, or expenses (including attorney's fees) arising out of my participation in this event, including any decision made by ISWS representatives or judges.</p>
          <p>I acknowledge that I have read and understand the current ISWS Rules and Regulations relevant to this event, and agree to abide by them.</p>
          <p>By signing or submitting this form, I confirm that I understand this waiver and agree to it voluntarily.</p>
        </>;
      case Constants.sanctioning_club.CPE:
        return <>
          <p><strong>CPE Event Waiver and Release of Liability</strong></p>
          <p>All events are held under the Official Rules and Regulations of Canine Performance Events (CPE). No alcohol, illegal drugs or firearms are permitted on the grounds or in the event area.</p>
          <p>By entering this event, I understand and agree that CPE, its officers, judges, volunteers, agents, and the hosting club are not responsible for any injury, loss, or damage to myself, my dog(s), my property, or any guests I bring. This includes any accidents, theft, or injuries that may occur at or during the event. I also take full responsibility for any children in my care.</p>
          <p>I hereby release and agree not to hold liable CPE, its judges, officers, volunteers, agents, and the host club for any claims, actions, or expenses (including attorney's fees) arising out of my participation in this event, including any decision made by CPE representatives or judges.</p>
          <p>I acknowledge that I have read and understand the current CPE Rules and Regulations relevant to this event, and agree to abide by them. I certify that my dog is in good health and proper condition for the activities they will be participating in.</p>
          <p>By signing or submitting this form, I confirm that I understand this waiver and agree to it voluntarily, and that I am giving up substantial rights including my right to sue.</p>
        </>;
      case Constants.sanctioning_club.GCR:
        return <>
          <p><strong>GCR Event Waiver and Release of Liability</strong></p>
          <p>All events are held under the Official Rules and Regulations of Greyhound Club Racing (GCR). No alcohol, illegal drugs or firearms are permitted on the grounds or in the event area.</p>
          <p>By entering this event, I understand and agree that GCR, its officers, judges, volunteers, agents, and the hosting club are not responsible for any injury, loss, or damage to myself, my dog(s), my property, or any guests I bring. This includes any accidents, theft, or injuries that may occur at or during the event. I also take full responsibility for any children in my care.</p>
          <p>I hereby release and agree not to hold liable GCR, its judges, officers, volunteers, agents, and the host club for any claims, actions, or expenses (including attorney's fees) arising out of my participation in this event, including any decision made by GCR representatives or judges.</p>
          <p>I acknowledge that I have read and understand the current GCR Rules and Regulations relevant to this event, and agree to abide by them. I certify that my dog is in good health and proper condition for the racing activities they will be participating in.</p>
          <p>By signing or submitting this form, I confirm that I understand this waiver and agree to it voluntarily, and that I am giving up substantial rights including my right to sue.</p>
        </>;
      case Constants.sanctioning_club.GENERAL:
        return <>
          <p><strong>NOTE:</strong> This Waiver, Release of Liability, &amp; Assumption of Risk is intended to shift the risk associated with participating in an event to the participant/handler/owner. Do not sign it unless you are willing to assume all risks associated with your participation.</p>
          
          <p>By signing this document electronically or in hard copy, clicking the box to accept the terms, or otherwise indicating my agreement, on my own behalf and on behalf of any minor children of whom I am the parent or legal guardian (collectively, "Participant"), I acknowledge and accept this Waiver, Release of Liability, &amp; Assumption of Risk ("Waiver") in connection with Participant's participation in an event (the "Event") organized, sanctioned, produced, or sponsored through the Eager Dog platform.</p>
          
          <p>I represent that I have read this Waiver and understand that it is a release of claims and that I am waiving important legal rights. I agree to be fully bound by its terms. The event host, along with its officers, directors, agents, representatives, volunteers, suppliers, sponsors, and any landowners, together with Eager Dog Inc., shall be referred to collectively as the "Event Sponsor."</p>
          
          <p><strong>1. Acknowledgment of and Assumption of Risk</strong></p>
          
          <p>I acknowledge that participating in the Event may involve inherent risks, including the risk of serious personal injury, death, and/or property damage caused by, among other things:</p>
          <ul>
            <li>the unpredictable nature of canine behavior and the possibility of a dog acting in a way that may result in injury, death, or property damage;</li>
            <li>the potential for another participant to fail to control his or her dog or engage in other negligence; and</li>
            <li>natural hazards, including surface and subsurface conditions, and the possibility of exposure to challenging, treacherous, or unstable terrain and footing.</li>
          </ul>
          
          <p>I acknowledge that other risks may exist, and I am not relying on Event Sponsor to list all possible inherent risks or all risks of participating. I freely and voluntarily assume all risks associated with participation.</p>
          
          <p><strong>2. Waiver and Release of Liability</strong></p>
          
          <p>I hereby release Event Sponsor from all liability for injury, illness, death, or damage to person or property in connection with the Event, including those resulting from any negligence (whether through action or inaction) by Event Sponsor. I hereby waive all corresponding damages, claims, actions, and causes of action accrued as a result of Event Sponsor's negligence (whether through action or inaction).</p>
          
          <p><strong>3. Indemnification</strong></p>
          
          <p>I agree that I alone am responsible and liable for my own behavior and that of the dogs in my custody during the Event. I will carefully monitor and control such dogs at all times. Accordingly, I agree to defend and indemnify the Event Sponsor, and to hold the Event Sponsor harmless from and against all claims, damages, actions, costs, and expenses arising out of or in connection with the Event.</p>

          <p><strong>4. Media Release</strong></p>
          
          <p>I hereby give permission for the Event Sponsor to use Participant's name, likeness, and voice in all types of advertising and promotion. This includes the use and distribution (in any format now known or later developed) of any and all photographs, video, and audio recordings in which Participant's name, likeness, or voice may be reproduced.</p>

          <p><strong>5. Severability</strong></p>
          
          <p>This document is intended to be as broad and inclusive as applicable law permits. If any clause conflicts with applicable law, only that clause will be void while the remainder shall stay in full force and effect.</p>
        </>;
      case Constants.sanctioning_club.C_WAGS:
        return <>
          <p><strong>DISCLAIMER – MUST BE SENT WITH ENTRY</strong></p>
          
          <p>I/we acknowledge that I am/we are familiar with the current rules applying to Canine-Work And GameS Trials. I/we agree that the Host Club holding the Trial has the right to refuse entry for cause which the Host Club shall deem to be sufficient. Upon acceptance of this entry, I/we agree to hold Canine-Work And GameS, LLC and its members and officers, this Host Club, its members, directors, officers, agents, show secretary, show chairperson, show committee and the owner or lessor of the premises and any employees of the aforementioned parties, any sponsors of this event, harmless from any claim or loss or injury which may be alleged to have been caused directly or indirectly to any person or thing by the act of this dog or handler while in or upon the show trial premises or grounds or near any entrance thereto, and I/we further agree to hold the fore mentioned parties harmless from any claim or loss of this dog by disappearance, theft, death or otherwise, and from any claim for damage or injury to the dog, whether any claim for loss or damage or alleged to be caused by the negligence of the Host Club or any of the parties aforementioned, or by the negligence of any other person, or any other cause or causes. I/we hereby assume sole responsibility for and agree to indemnify and save the aforementioned parties harmless from any and all loss and expense (including legal fees) by reason of the liability imposed by law upon any of the aforementioned parties for damage because of bodily injuries, including death at any time resulting there from, sustained by any person or persons, including myself ourselves or on account of damage to property, arising out of or in consequence of my/our participation in this Trial, howsoever such injuries, death or damage to property may be caused and whether or not the same may have been caused or may be alleged to have been caused by negligence of the aforementioned parties or any of their employees or agents, or any other persons.</p>

          <p><strong>THIS DISCLAIMER MUST BE SIGNED AND INCLUDED FOR A VALID ENTRY</strong></p>
          
          <p>I/we agree to abide by the rules of C-WAGS currently in effect at the time of the trial. I/we testify that the dog entered is healthy and vaccinated or titered. I/we certify that the dog entered is not dangerous to any person or other dog. By signing below, I/we agree to the disclaimer on this entry form.</p>
        </>;
      case Constants.sanctioning_club.BHA:
        return <>
          <p><strong>OFFICIAL BARN HUNT WAIVER</strong></p>
          
          <p>I (we) agree that the Club holding this event has the right to refuse this entry for cause which the Club shall deem sufficient. In consideration of the acceptance of this entry and of the holding of this event, I (we) agree to hold the Barn Hunt Association, LLC, {event?.club_name || "the hosting club"}, their members, directors, governors, officers, agents, event chair and secretary, and the owner and/or lessor of the premises, any provider of services that are necessary to hold this event, any employees or volunteers of the aforementioned parties, and any BHA LLC approved judge, judging at this event, harmless from any claim for loss or injury which may be alleged to have been caused directly or indirectly to any person or thing by the act of any dog while in or about the event premises or grounds or near any entrance thereto, and I (we) personally assume all responsibility and liability for any such claim; and I (we) further agree to hold the aforementioned parties harmless from any claim for loss, injury or damage to or this dog.</p>
          
          <p>Additionally, I (we) hereby assume the sole responsibility for and agree to indemnify, defend and save the aforementioned parties harmless from any and all loss and expense (including legal fees) by reason of the liability imposed by law upon any of the aforementioned parties for damage because of bodily injuries, including death at any time resulting therefrom, sustained by any person or persons, including myself (ourselves), or on account of damage to property, arising out of or in consequence of my (our) participation in this event, however such, injuries, death or property damage may be caused, and whether or not the same may have been caused or may be alleged to have been caused by the negligence of the aforementioned parties or any of their employees, agents, or any other persons.</p>
          
          <p><strong>Jurisdiction:</strong> I/we understand that any action taken to enforce the provisions of this agreement shall be governed by and interpreted in accordance with the laws of the United States in the state of Kentucky. Each party irrevocably submits to the exclusive jurisdiction of, and venue in, any state or federal court located within Kentucky Circuit 42, Western District for the purposes of any Suit arising out of this agreement or any transaction contemplated by this agreement, and agrees to commence any such suit only in such courts. Any legal fees or court costs resulting from such action will be paid by the prevailing party.</p>
          
          <p><strong>Dog Health & Rules:</strong> I certify that my dog is in good health and proper condition for the activities they will be participating in during this Barn Hunt event. I acknowledge that I have read and understand the current Barn Hunt Rules and Regulations, and agree to abide by them.</p>
        </>;
      case Constants.sanctioning_club.ASFA:
        return <>
          <p><strong>ASFA Event Waiver and Release of Liability</strong></p>
          <p>All events are held under the Official Rules and Regulations of the American Sighthound Field Association (ASFA). No alcohol, illegal drugs, or firearms are permitted on the grounds or in the event area.</p>
          <p>By entering this event, I understand and agree that ASFA, its officers, judges, volunteers, agents, and the hosting club are not responsible for any injury, loss, or damage to myself, my dog(s), my property, or any guests I bring. This includes accidents, theft, or injuries that may occur at or during the event.</p>
          <p>I hereby release and agree not to hold liable ASFA, its judges, officers, volunteers, agents, and the host club for any claims, actions, or expenses (including attorney's fees) arising out of my participation in this event, including any decision made by ASFA representatives or judges.</p>
          <p>I acknowledge that I have read and understand the current ASFA Rules and Regulations relevant to this event, and agree to abide by them. I certify that my hound is in good health and proper condition for the activities it will be participating in.</p>
        </>;
      case Constants.sanctioning_club.CKC:
        return <>
          <p>I CERTIFY that I am the registered owner(s) of the dog or that I am the authorized agent of the actual owner(s) whose name(s) I have entered above and accept full responsibility for all statements made in this entry. In consideration of the acceptance of this entry, I (we) agree to be bound by the rules and regulations of The Canadian Kennel Club and by any additional rules and regulations appearing in the premium list.</p>
          <p>I acknowledge that The Canadian Kennel Club is not responsible to any member, person, partnership, club or corporation for any loss, damage or injuries arising at or by reason of any event held under its rules, and that as owner or authorized agent I will take all reasonable steps to ensure that the dog behaves properly at the event.</p>
          <p>I acknowledge that the current CKC {getCkcRulebooks()} have been made available to me, and that I am familiar with their contents.</p>
        </>;
      case Constants.sanctioning_club.FH:
        return <>
          <p><strong>4-H Rally Obedience Liability Waiver</strong></p>

          <p><strong>NOTE:</strong> This Waiver, Release of Liability, &amp; Assumption of Risk is intended to shift the risk associated with participating in a 4-H dog event to the participant. Do not sign it unless you are willing to assume all risks associated with your participation.</p>

          <p>By signing this document electronically or in hard copy, clicking the box to accept the terms, or otherwise indicating my agreement, on my own behalf and on behalf of any minor children of whom I am the parent or legal guardian (collectively, "Participant"), I acknowledge and accept this Waiver, Release of Liability, &amp; Assumption of Risk in connection with Participant's participation in this 4-H sanctioned Rally Obedience event organized through the Eager Dog platform.</p>

          <p>I acknowledge that participating in this event involves inherent risks, including the unpredictable nature of canine behavior, physical demands on handlers and dogs, and environmental hazards. I freely and voluntarily assume all risks associated with participation.</p>

          <p>I release the event host, the county 4-H program, Wisconsin 4-H, and Eager Dog Inc. from all liability for injury, illness, death, or damage to person or property in connection with this event. I agree to abide by all applicable 4-H rules and regulations, and I certify that my dog is in good health and proper condition to compete.</p>

          <p>I confirm that my dog is current on all required vaccinations.</p>

          <p><em>If the participant is a minor, this entry form must be signed by a parent or legal guardian, who agrees to accompany the minor and remain on site during the event.</em></p>
        </>;
    }

    return <></>;
  }

  const isJoinedShow = (_sanc: string, _type: string, dogId?: string) => {
    if (_sanc === Constants.sanctioning_club.UKC) {
      // For UKC Conformation, check if user wants joined behavior (checkbox checked)
      if (_type === Constants.show_type.conformation && dogId) {
        return useJoinedConformation[dogId] !== false; // Default to true (joined) if not set
      }
      
      let nonJoinedShowTypes: string[] = [
        Constants.show_type.nosework,
        Constants.show_type.precision_coursing,
        Constants.show_type.dock_jumping,
        Constants.show_type.rally_obedience,
        Constants.show_type.obedience,
        Constants.show_type.agility,
        // Weight Pull trials can each offer a different subset of surface
        // elements (Wheels Natural/Artificial, Snow, Rail) and a dog can be
        // entered differently per trial/day — "joined" mode forces one
        // combined selection to apply identically across every Weight Pull
        // trial the dog is entered in, which breaks entries for any trial
        // whose available surfaces/divisions differ from the others.
        Constants.show_type.weight_pull
      ];

      if (!nonJoinedShowTypes.includes(_type)) {
        return true;
      }
    }

    if (_sanc === Constants.sanctioning_club.AKC) {
      let nonJoinedShowTypes: string[] = [
        Constants.show_type.conformation,
        Constants.show_type.rally_obedience,
        Constants.show_type.obedience,
        Constants.show_type.nosework,
        Constants.show_type.fast_cat,
        Constants.show_type.cat,
        Constants.show_type.scentwork,
        Constants.show_type.fetch,
        Constants.show_type.farm_dog,
        Constants.show_type.att,
        Constants.show_type.canine_good_citizen,
        Constants.show_type.trick_dog,
        Constants.show_type.agility,
      ];

      if (!nonJoinedShowTypes.includes(_type)) {
        return true;
      }
    }

    if (_sanc === Constants.sanctioning_club.ABI) {
      let nonJoinedShowTypes:string[] = [
        Constants.show_type.urban_rat_race,
        Constants.show_type.country_rat_race,
        Constants.show_type.scent_dog_games,
        Constants.show_type.scentwork,
        Constants.show_type.conformation
      ];

      if (!nonJoinedShowTypes.includes(_type)) {
        return true;
      }
    }

    if (_sanc === Constants.sanctioning_club.PWDCA) {
      let nonJoinedShowTypes:string[] = [
        Constants.show_type.water_dog,
      ];

      if (!nonJoinedShowTypes.includes(_type)) {
        return true;
      }
    }

    if (_sanc === Constants.sanctioning_club.C_WAGS) {
      let nonJoinedShowTypes:string[] = [
        Constants.show_type.scent,
        Constants.show_type.obedience,
        Constants.show_type.rally_obedience,
        Constants.show_type.cwags_games,
      ];

      if (!nonJoinedShowTypes.includes(_type)) {
        return true;
      }
    }

    return false;
  }

  // NOTE: show type w/o any dog details to input (handler, levels etc.)
  const isNoQuestionsShow = (showType: string) => {
    return [
      Constants.show_type.farm_dog,
      Constants.show_type.att,
      Constants.show_type.dock_diving,
    ].includes(showType);
  }

  const handlerEnabled:string[] = [
    Constants.show_type.conformation,
    Constants.show_type.obedience,
    Constants.show_type.rally_obedience,
    Constants.show_type.fetch,
    Constants.show_type.fast_cat,
    Constants.show_type.water_dog,
    Constants.show_type.shed_dog,
    Constants.show_type.agility,
    Constants.show_type.barn_hunt,
  ];

  const getStepHint = () => {
    switch (currentStep) {
      case 0:
        if (!showStepHint) {
          return <>
            <p className="showInstructions" onClick={() => setShowStepHint(true)}>Show Instructions</p>
          </>;
        }
        return <>
          <div className="minimizeButton" onClick={() => setShowStepHint(false)}>−</div>
          <p>
            <strong>1.</strong> Click a dog's profile image to select or deselect them for the trial.<br/>
            <strong>2.</strong> Choose all the dogs you want to enter — you can register multiple dogs in a single transaction.<br/>
            <strong>3.</strong> Don't see your dog? Click Add Dog to add one before continuing.<br/>
            <small>Need a walkthrough? Open the <span onClick={() => { setJoyrideRun(true); }}>guided tour</span>.</small>
          </p>
        </>;
      case 1:
        return <></>;
      default:
        return <></>;
    }
  }

  const setFirstShowTime = (showType: string) => {
    let hasShowTypeShows = shows.filter((s: any) => { return s.show_type === showType; });

    if (hasShowTypeShows.length > 0) {
      let i = shows.indexOf(hasShowTypeShows[0]);
      setCurrentShowTime(i);
    }
  }

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, index, type } = data;

    if (type === EVENTS.TARGET_NOT_FOUND) {
      setCurrentShow(0);
      if (orderedShowTypes.length > 0) {
        setFirstShowTime(orderedShowTypes[0]);
      }
    } else if (type === EVENTS.TOUR_END) {
      setJoyrideRun(false);
    }

    if ((action === ACTIONS.NEXT || action === ACTIONS.PREV) && type === EVENTS.STEP_AFTER) {
      setJoyrideStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    } else if (action === ACTIONS.CLOSE) {
      setJoyrideRun(false);
    } else if (action === ACTIONS.START) {
      setJoyrideStepIndex(0);
    }
  };

  const getJudgeDislcaimer = (_show: IShow) => {
    let disclaimer:string = "";

    if (_show.breed_group_judges && _show.breed_group_judges.length > 0) {
      disclaimer += "Breed Group Judges: ";

      for (let i in _show.breed_group_judges) {
        let judge = _show.breed_group_judges[i];

        disclaimer += judge.judge_name + " (" + judge.breed_group + ")";

        if (Number(i) < _show.breed_group_judges.length - 1) {
          disclaimer += ", ";
        }
      }

      disclaimer += " ";
    }

    if (_show.breed_judges && _show.breed_judges.length > 0) {
      disclaimer += "Breed Specific Judges: ";

      for (let i in _show.breed_judges) {
        let judge = _show.breed_judges[i];

        disclaimer += judge.judge_name + " (" + judge.breed + ")";

        if (Number(i) < _show.breed_judges.length - 1) {
          disclaimer += ", ";
        }
      }
    }

    return disclaimer;
  }

  const getWaitlistStatus = (_showId: string, _dogId: string) => {
    for (let d in paymentSummary.details) {
      let _show:any = paymentSummary.details[d];

      if (_show.show_id === _showId) {
        for (let d in _show.dogs) {
          let _dog:any = _show.dogs[d];

          if (_dog.dog_id === _dogId) {
            for (let s in _dog.show_elements) {
              if (_dog.show_elements[s].waitlist === true) {
                return true;
              }
            }
          }
        }
      }
    }

    return false;
  }

  const getWaitlistText = (_showId: string, _dogId: string, _showType?: string) => {
    let _text:string = "";

    for (let d in paymentSummary.details) {
      let _show:any = paymentSummary.details[d];

      if (_show.show_id === _showId) {
        for (let d in _show.dogs) {
          let _dog:any = _show.dogs[d];

          if (_dog.dog_id === _dogId) {
            for (let s in _dog.show_elements) {
              if (_dog.show_elements[s].waitlist === true) {
                _text += _dog.show_elements[s].show_element;

                if (_dog.show_elements[s].level) {
                  _text += " - " + _dog.show_elements[s].level;
                }

                _text += ", ";
              }
            }
          }
        }
    }
    }

    _text = _text.slice(0, _text.length - 2);

    return _text;
  }

  const getWaitlistPositions = (_showId: string, _dogId: string) => {
    const positions: Array<{element: string, level?: string, position: number}> = [];

    for (let d in paymentSummary.details) {
      let _show:any = paymentSummary.details[d];

      if (_show.show_id === _showId) {
        for (let d in _show.dogs) {
          let _dog:any = _show.dogs[d];

          if (_dog.dog_id === _dogId) {
            for (let s in _dog.show_elements) {
              if (_dog.show_elements[s].waitlist === true && _dog.show_elements[s].waitlist_position) {
                positions.push({
                  element: _dog.show_elements[s].show_element,
                  level: _dog.show_elements[s].level,
                  position: _dog.show_elements[s].waitlist_position
                });
              }
            }
          }
        }
      }
    }

    return positions;
  }

  const getWaitlistedClassesForDog = (_showId: string, _dogId: string): Array<{show_element: string, level: string}> => {
    const waitlistedClasses: Array<{show_element: string, level: string}> = [];

    for (let d in paymentSummary.details) {
      let _show:any = paymentSummary.details[d];

      if (_show.show_id === _showId) {
        for (let d in _show.dogs) {
          let _dog:any = _show.dogs[d];

          if (_dog.dog_id === _dogId) {
            for (let s in _dog.show_elements) {
              if (_dog.show_elements[s].waitlist === true) {
                waitlistedClasses.push({
                  show_element: _dog.show_elements[s].show_element,
                  level: _dog.show_elements[s].level
                });
              }
            }
          }
        }
      }
    }

    return waitlistedClasses;
  }

  const getWaitlistMessage = (_showId: string, _dogId: string, dogName: string, showText: string) => {
    const positions = getWaitlistPositions(_showId, _dogId);

    if (positions.length === 0) {
      return `One or more show levels is currently at maximum capacity. ${dogName} will be placed on a waitlist for these entries (${showText}).`;
    }

    if (positions.length === 1) {
      return `One or more show levels is currently at maximum capacity. ${dogName} will be placed on a waitlist for these entries (${showText}) at position #${positions[0].position}.`;
    }

    // Multiple positions - show each class with its position
    const showElements = showText.split(', ');
    let positionText = '';

    showElements.forEach((element, index) => {
      const position = positions[index];
      if (position) {
        if (index > 0) positionText += ', ';
        positionText += `${element} at position #${position.position}`;
      }
    });

    return `One or more show levels is currently at maximum capacity. ${dogName} will be placed on a waitlist for these entries (${positionText}).`;
  }

  const getWaitlistLotteryMessage = (_showId: string, _dogId: string, dogName: string) => {
    const waitlistText = getWaitlistText(_showId, _dogId);
    const lotteryDate = moment(event.lottery_end_date).format("MMMM Do, YYYY");

    return `This show will be holding a random draw for registrations on ${lotteryDate}. ${dogName} will be placed on a waitlist for these entries (${waitlistText}).`;
  }

  const checkIfWaitlist = () => {
    if (paymentSummary) {
      for (let d in paymentSummary.details) {
        for (let dog in (paymentSummary.details[d] as ITransactionShowDetails).dogs) {
          let _dog:any = (paymentSummary.details[d] as ITransactionShowDetails).dogs[dog];
          for (let _se in _dog.show_elements) {
            if (_dog.show_elements[_se].waitlist && _dog.show_elements[_se].waitlist === true) {
              return "true";
            }
          }
        }
      }
    }

    return "false";
  }

  const registerPayClubDirectly = async () => {
    if (!event?._id || !regPayload) return;

    const validAddress = isValidAddress(userAddress);
    const validPhone = isValidPhone(userPhone);

    if (!validAddress.valid || !validPhone.valid) {
      toast.show({
        title: "Invalid Information",
        content: validAddress.message || validPhone.message || "Please enter a valid address and phone number to continue.",
        duration: 10000,
        type: "fail"
      });
      return;
    }

    setPayClubDirectlyLoading(true);

    try {
      await apiService.updateSelf({ address: userAddress, phone: userPhone });

      const registrationPayload: any = {
        registration_kind: Constants.registration_kind.owner,
        payment_method_type: Constants.payment_method_type.pay_club_directly,
        owner_id: user._id,
        dog_show_registrations: regPayload
      };

      if (currentDiscountCode) {
        registrationPayload.discount_code = currentDiscountCode;
      }

      await apiService.createRegistrationPaymentIntent(event._id, registrationPayload);

      navigate(getRedirectUrl(`/e/${eid}`, `dids=${getDogIds()}&redirect_status=succeeded&waitlist=${checkIfWaitlist()}&pay_club_directly=true`));
    } catch (e: any) {
      toast.show({
        title: "Event Register",
        content: e.response?.data?.message ?? "Something went wrong. Please try again later.",
        duration: 10000,
        errorDetails: e,
        type: "fail"
      });
    } finally {
      setPayClubDirectlyLoading(false);
    }
  };

  const registerForFree = () => {
    if (transactionId !== undefined && event?._id) {
      // Validate address and phone — required for all registrations (free or paid)
      const validAddress = isValidAddress(userAddress);
      const validPhone = isValidPhone(userPhone);

      if (!validAddress.valid || !validPhone.valid) {
        toast.show({
          title: "Invalid Information",
          content: validAddress.message || validPhone.message || "Please enter a valid address and phone number to continue.",
          duration: 10000,
          type: "fail"
        });
        return;
      }

      apiService.updateSelf({
        address: userAddress,
        phone: userPhone
      }).then(() => {
        apiService.processOwnerRegistrationTransaction(event?._id, transactionId, { is_waitlist: waitlistsPaymentElements.length > 0 }).then((response) => {
          const extra = chargeParams();
          navigate(getRedirectUrl(`/e/${eid}`, `dids=${getDogIds()}&redirect_status=succeeded&waitlist=${checkIfWaitlist()}&lottery=${lotteryDrawOpen ? "true" : "false"}${extra ? `&${extra}` : ""}`));
        }).catch((e: any) => {
          toast.show({
            title: "Event Register",
            content: e.response?.data?.message ? e.response?.data?.message : "Something went wrong. Please try again later.",
            duration: 10000,
            errorDetails: e,
            type: "fail"
          });
        });
      }).catch((e: any) => {
        toast.show({
          title: "Update Address",
          content: "Failed to update address information. Please try again.",
          duration: 10000,
          errorDetails: e,
          type: "fail"
        });
      });
    } else {
      toast.show({
        title: "Event Register",
        content: "Something went wrong. Please try again later.",
        duration: 10000,
        type: "fail"
      });
    }
  }

  const getPrecisionCourseName = (show: any) => {
    if (show.show_type === Constants.show_type.precision_coursing) {
      let hasDash = show.show_elements.filter((s: any) => { return s.level === Constants.dog_class_element_level.dash });
      let hasSteeplechase = show.show_elements.filter((s: any) => { return s.level === Constants.dog_class_element_level.steeplechase });

      if (hasDash.length !== 0 && hasSteeplechase.length === 0) {
        return " ("+ Constants.dog_class_element_level.dash +")";
      }

      if (hasDash.length === 0 && hasSteeplechase.length !== 0) {
        return " ("+ Constants.dog_class_element_level.steeplechase +")";
      }
    }

    return "";
  }

  // While anyone is pending on the waitlist, open spots are held for the list: new entries
  // join the waitlist instead of taking the spot, so "N entries available" would be misleading.
  const renderWaitlistHeld = (pending: number, waitlistSize?: number) => (
    <>
      <span className="capacityText">Waitlist only — {pending} ahead</span>
      {waitlistSize ? (
        <>
          {' '}
          <span className="waitlistText">Waitlist: {pending} of {waitlistSize}</span>
        </>
      ) : null}
    </>
  );

  const getWaitlistAvailabilityText = (show: IShowDogs) => {
    // Random draw events don't show capacity — positions aren't meaningful before the draw runs
    if (lotteryDrawOpen) {
      return null;
    }
    // UKC Conformation does not display entry availability counts
    if (event?.sanctioning_club === Constants.sanctioning_club.UKC) {
      return null;
    }

    if (show.waitlist_option === Constants.waitlist_options.entries_per_judge) {
      return getShowAvailabilityMessage(show);
    }

    const isHerding = [
      Constants.show_type.herding,
      Constants.show_type.pre_trial_herding,
      Constants.show_type.instinct_test_herding,
    ].includes(show.show_type);

    // Herding per-stock: display availability per stock type
    if (isHerding && show.waitlist_option === Constants.waitlist_options.per_level_limit && show.show_elements) {
      const uniqueSections = [...new Set(
        (show.show_elements as any[])
          .filter((se: any) => se.section && se.max_entries != null)
          .map((se: any) => se.section as string)
      )];

      if (uniqueSections.length === 0) return null;

      const stockTexts = uniqueSections.map((section) => {
        const seForSection = (show.show_elements as any[]).find(
          (se: any) => se.section === section && se.max_entries != null
        );
        if (!seForSection) return null;

        const maxEntries: number = seForSection.max_entries;
        const registered = (show.stock_score_counts || []).find(
          (sc) => sc.section === section
        )?.count || 0;
        const available = Math.max(0, maxEntries - registered);
        const waitlistEnabled: boolean = seForSection.waitlist_enabled;
        const waitlistSize: number = seForSection.waitlist_size || 0;
        const waitlistUsed = (show.stock_waitlist_counts || []).find(
          (wc) => wc.section === section
        )?.count || 0;

        return (
          <span key={section} className="stockAvailability">
            <span className="stockLabel">{section}:</span>
            {' '}
            {waitlistEnabled && waitlistUsed > 0 ? (
              renderWaitlistHeld(waitlistUsed, waitlistSize)
            ) : (
              <>
                <span className="capacityText">{available} {available === 1 ? 'entry' : 'entries'} available</span>
                {waitlistEnabled && waitlistSize > 0 && (
                  <>
                    {' '}
                    <span className="waitlistText">Waitlist: {waitlistUsed} of {waitlistSize}</span>
                  </>
                )}
              </>
            )}
          </span>
        );
      }).filter(Boolean);

      return stockTexts.length > 0 ? <span className="stockAvailabilityList">{stockTexts}</span> : null;
    }

    // Show availability text if there's a waitlist limit configured
    if (show.waitlist_limit && show.waitlist_limit > 0) {
      let registeredEntries = show.registered_entries || 0;

      // Skip showing hours-based waitlist messages as they require accurate per-class timing
      if (show.waitlist_option && [Constants.waitlist_options.daily_judge_time_limit, Constants.waitlist_options.daily_trial_time_limit].includes(show.waitlist_option)) {
        return null;
      }

      const canBypassLimits = isAdmin || (onBehalf !== undefined && onBehalfEmail !== undefined);
      const isEntryLimit = show.waitlist_option === Constants.waitlist_options.show_entry_limit
      const isDogLimit = show.waitlist_option === Constants.waitlist_options.show_dog_limit

      if (isEntryLimit || isDogLimit) {
        const maxEntries = show.waitlist_limit;
        if (isDogLimit) {
          registeredEntries = show.dogs_count || 0
        }
        const availableEntries = Math.max(0, maxEntries - registeredEntries);
        const waitlistUsed = show.waitlist_count || 0;

        if (show.waitlist_enabled && waitlistUsed > 0) {
          return renderWaitlistHeld(waitlistUsed, show.waitlist_size);
        }

        if (show.waitlist_enabled && show.waitlist_size) {
          return (
            <>
              <span className="capacityText">{availableEntries} {availableEntries === 1 ? 'entry' : 'entries'} available</span>
              {' '}
              <span className="waitlistText">Waitlist: {waitlistUsed} of {show.waitlist_size}</span>
            </>
          );
        } else {
          return <span className="capacityText">{availableEntries} {availableEntries === 1 ? 'entry' : 'entries'} available</span>;
        }
      }
    }

    return null;
  }

  const getJudgeEntryAvailabilityRows = (show: IShowDogs) => {
    const holdActive = isManualEntryHoldActive(show.manual_entry_limit_date);
    const limits = new Map(
      (show.judge_entry_limits || []).map((l) => [getJudgeEntryLimitKey(l.judge_name, l.judge_number), l])
    );
    const counts = new Map<string, { online: number; manual: number }>();
    for (const row of show.judge_entry_counts || []) {
      const key = getJudgeEntryLimitKey(row.judge_name, row.judge_number);
      const entry = counts.get(key) || { online: 0, manual: 0 };
      if (row.manual) entry.manual += row.count; else entry.online += row.count;
      counts.set(key, entry);
    }

    return getShowJudgesForEntryLimits(show).map((judge) => {
      const key = getJudgeEntryLimitKey(judge.judge_name, judge.judge_number);
      const limit = limits.get(key);
      const count = counts.get(key) || { online: 0, manual: 0 };
      const availability = limit
        ? getJudgeEntryAvailability({
            total_entries: limit.total_entries,
            manual_entries: limit.manual_entries,
            online_entries: count.online,
            manual_entry_count: count.manual,
            hold_active: holdActive,
          })
        : undefined;
      return { judge_name: judge.judge_name, judge_number: judge.judge_number, availability };
    });
  };

  const formatManualEntryLimitDate = (date?: Date | string) => {
    if (!date) return "";
    const tz = event?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    return momentTz(date).tz(tz).format("MMM D, h:mm A z");
  };

  // Check if a show is full (no available spots and no waitlist)
  const isShowFull = (show: IShowDogs) => {
    // Admins and on-behalf users can always register even when full
    const canBypassLimits = isAdmin || (onBehalf !== undefined && onBehalfEmail !== undefined);
    if (canBypassLimits) {
      return false;
    }

    if (show.waitlist_option === Constants.waitlist_options.entries_per_judge) {
      const waitlistOpen = !!show.waitlist_enabled && (show.waitlist_count || 0) < (show.waitlist_size || 0);
      const rows = getJudgeEntryAvailabilityRows(show);
      return !waitlistOpen && rows.every((r) => !r.availability || r.availability.online_available === 0);
    }
    
    if (show.waitlist_option === Constants.waitlist_options.show_entry_limit) {
      const maxEntries = show.waitlist_limit || 0;
      const registeredEntries = show.registered_entries || 0;
      const availableEntries = Math.max(0, maxEntries - registeredEntries);

      // Show is full if no available spots and no waitlist enabled
      return availableEntries === 0 && !show.waitlist_enabled;
    }

    if (show.waitlist_option === Constants.waitlist_options.daily_judge_mrv_limit) {
      const mrvLimit = show.waitlist_limit || 260;
      const currentMrv = (show as any).current_mrv || 0;
      return currentMrv >= mrvLimit && !show.waitlist_enabled;
    }

    return false;
  }

  // Get full availability message for a show
  const getShowAvailabilityMessage = (show: IShowDogs) => {
    // Random draw events don't show capacity — positions aren't meaningful before the draw runs
    if (lotteryDrawOpen) {
      return null;
    }
    // Skip showing hours-based waitlist messages as they require accurate per-class timing
    if (show.waitlist_option && [Constants.waitlist_options.daily_judge_time_limit, Constants.waitlist_options.daily_trial_time_limit].includes(show.waitlist_option)) {
      return null;
    }

    if (show.waitlist_option === Constants.waitlist_options.entries_per_judge) {
      // Per-judge and Detective availability is for club staff only, not exhibitors.
      const canBypassLimits = isAdmin || (onBehalf !== undefined && onBehalfEmail !== undefined);
      if (!canBypassLimits) return null;

      const rows = getJudgeEntryAvailabilityRows(show);
      if (rows.length === 0) return null;
      const releaseLabel = formatManualEntryLimitDate(show.manual_entry_limit_date);
      const pendingByJudge = new Map(
        (show.judge_waitlist_counts || []).map((w) => [getJudgeEntryLimitKey(w.judge_name, w.judge_number), w.count])
      );

      return (
        <span className="stockAvailabilityList">
          {rows.map((r) => {
            const pending = show.waitlist_enabled
              ? pendingByJudge.get(getJudgeEntryLimitKey(r.judge_name, r.judge_number)) || 0
              : 0;
            let text: string;
            if (pending > 0) {
              text = `waitlist only — ${pending} ahead`;
            } else if (!r.availability) {
              text = "not open yet";
            } else if (r.availability.online_available > 0) {
              text = `${r.availability.online_available} online ${r.availability.online_available === 1 ? "spot" : "spots"}`;
            } else if (r.availability.active_reserve > 0 && r.availability.total_available > 0) {
              text = `online full (held for mail-in until ${releaseLabel})`;
            } else {
              text = "full";
            }
            return (
              <span key={r.judge_name} className="stockAvailability">
                <span className="stockLabel">Judge {r.judge_name}:</span>
                {' '}
                <span className="capacityText">{text}</span>
              </span>
            );
          })}
          {show.detective_run_limit && show.detective_entry_counts && showOffersDetective(show.show_elements) ? (() => {
            const detective = getJudgeEntryAvailability({
              total_entries: show.detective_run_limit,
              manual_entries: show.detective_manual_entries,
              online_entries: show.detective_entry_counts.online,
              manual_entry_count: show.detective_entry_counts.manual,
              hold_active: isManualEntryHoldActive(show.manual_entry_limit_date),
            });
            let text: string;
            if (detective.online_available > 0) {
              text = `${detective.online_available} online ${detective.online_available === 1 ? "spot" : "spots"}`;
            } else if (detective.active_reserve > 0 && detective.total_available > 0) {
              text = `online full (held for mail-in until ${releaseLabel})`;
            } else {
              text = "full";
            }
            return (
              <span className="stockAvailability">
                <span className="stockLabel">Detective:</span>
                {' '}
                <span className="capacityText">{text}</span>
              </span>
            );
          })() : null}
          {show.waitlist_enabled && show.waitlist_size ? (
            <span className="stockAvailability">
              <span className="waitlistText">Waitlist: {show.waitlist_count || 0} of {show.waitlist_size}</span>
            </span>
          ) : null}
        </span>
      );
    }

    // Only show capacity if there's a limit configured
    if (!show.waitlist_limit || show.waitlist_limit <= 0) {
      return null;
    }

    const isEntryLimit = show.waitlist_option === Constants.waitlist_options.show_entry_limit;
    const isDogLimit = show.waitlist_option === Constants.waitlist_options.show_dog_limit;

    if (isEntryLimit || isDogLimit) {
      const maxEntries = show.waitlist_limit;
      const registeredEntries = isDogLimit ? (show.dogs_count || 0) : (show.registered_entries || 0);
      const availableEntries = Math.max(0, maxEntries - registeredEntries);
      const waitlistUsed = show.waitlist_count || 0;

      if (show.waitlist_enabled && waitlistUsed > 0) {
        return renderWaitlistHeld(waitlistUsed, show.waitlist_size);
      }

      // Add waitlist info if enabled
      if (show.waitlist_enabled && show.waitlist_size) {
        return (
          <>
            <span className="capacityText">{availableEntries} {availableEntries === 1 ? 'entry' : 'entries'} available</span>
            <span className="waitlistText">Waitlist: {waitlistUsed} of {show.waitlist_size}</span>
          </>
        );
      }

      return <span className="capacityText">{availableEntries} {availableEntries === 1 ? 'entry' : 'entries'} available</span>;
    }

    return null;
  }

  const getDogCallNameByClub = (club: string, dog?: IDogInfo) => {
    const clubRegistration = dog?.club_registrations?.find(cr => cr.sanctioning_club === club);

    return clubRegistration?.call_name ?? dog?.call_name;
  }

  return (
    <div className="EventRegister">
      {event && <Helmet>
        <title>{event?.title} Event - Register</title>
        <meta name="description" content={"Register for " + event?.title + " event on " + moment(event?.start_date).format('dddd MMMM Do, yyyy') + "."} />
        <meta name="robots" content="index, follow" />
      </Helmet>}
      <Joyride
        continuous={true}
        steps={joyrideSteps}
        callback={handleJoyrideCallback}
        stepIndex={joyrideStepIndex}
        run={joyrideRun}
        locale={{last: "Finish"}}
      />
      <div className={isPaymentStep() ? "registerWrap payNow" : "registerWrap"}>
        <div className={currentStep === 0 ? "registerInner noCart" : "registerInner"}>
          <div className="breadcrumb">{event?.title && <div className="path"><Link to={getBreadcrumbUrl()}>{event?.title}</Link> /</div>}<div className="current">Register</div></div>
          {onBehalf && onBehalfEmail && currentStep !== 4 && <div className="onBehalfMsg">
            <div className="msg">You're registering on behalf of <span>{onBehalfEmail}</span>.
              {(!isClaimed) && <div className="accountStatus"> This email doesn't have an active Eagerdog account, dogs on this account are managed by you.</div>}
              {(isClaimed) && <div className="accountStatus"> This email has an active Eagerdog account, displaying dogs created by both the user and you.</div>}
            </div>
          </div>}
          <div className="stepWrap">
            <ul className="steps">
              {steps.map((step:string, i:number) => {
                return(<li key={i} className={i === currentStep ? "active": i < currentStep ? "done" : ""}><span>{i === currentStep ? `Step ${i + 1}` : i + 1}</span><span>{step}</span></li>);
              })}
              <li className="actions">
                {(currentStep < steps.length - 1) && <button onClick={() => { next(); }}>Next</button>}
              </li>
            </ul>
            {currentStep === 0 && getStepHint() && <div className="stepHint">{getStepHint()}</div>}
            <div className="step">
              {loaded && currentStep === 0 && <div className="stepInner selectShows">
                {/* Date Received section for admin offline entries */}
                {user.user_type === Constants.user_type.club_manager && (
                  <div className="adminSection">
                    <DatePick
                      label="Date Received"
                      value={dateReceived}
                      onChange={(date: Date) => {
                        const today = new Date();
                        today.setHours(23, 59, 59, 999); // Set to end of today

                        if (date > today) {
                          toast.show({
                            title: "Invalid Date",
                            content: "Date received cannot be in the future",
                            duration: 5000,
                            type: "fail"
                          });
                          setDateReceived(new Date());
                        } else {
                          setDateReceived(date);
                        }
                      }}
                    />
                    <div className="description">
                      Select the date this registration was received for proper pricing calculation.
                    </div>
                  </div>
                )}

                {/* Show banner only once if any dogs need migration */}
                {hasAnyLegacyDogs() && <RegistrationUpdateBanner />}

                {orderedShowTypes.map((showType:string, showTypeIndex:number) => {
                  const isShowTypeExpanded = expandedShowTypes.has(showTypeIndex);
                  return(
                    <div key={showTypeIndex} className={isShowTypeExpanded ? "showType expanded" : "showType"}>
                      <div onClick={() => { 
                        const newExpanded = new Set(expandedShowTypes);
                        if (isShowTypeExpanded) {
                          newExpanded.delete(showTypeIndex);
                        } else {
                          newExpanded.add(showTypeIndex);
                        }
                        setExpandedShowTypes(newExpanded);
                        setFirstShowTime(showType);
                      }} className="title">
                        <div className="name">{showType}</div>
                        <div className="actions"><span className="hint">{isShowTypeExpanded ? "Collapse" : "Expand"}</span><div className="icon caretDown"></div></div>
                      </div>
                      <div className="showTimes">
                        {shows.map((show:IShowDogs, showIndex:number) => {
                          if (show.show_type === showType) {
                            const isShowTimeExpanded = expandedShowTimes.has(showIndex);
                            return(
                              <ShowTime
                                timezone={event?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
                                key={showIndex}
                                className={isShowTimeExpanded ? "expanded" : ""}
                                club={event.sanctioning_club}
                                show={show}
                                onToggleShow={() => { 
                                  const newExpanded = new Set(expandedShowTimes);
                                  if (isShowTimeExpanded) {
                                    newExpanded.delete(showIndex);
                                  } else {
                                    newExpanded.add(showIndex);
                                  }
                                  setExpandedShowTimes(newExpanded);
                                }}
                                onAddDog={() => { setCurrentShowTime(showIndex); setModalContent("dogForm"); }}
                                onEditDog={() => {
                                  handleDogUpdated();
                                }}
                                onDogClick={(dog: IDog) => { selectDog(showIndex, dog) }}
                                dogs={dogs}
                                onBehalf={onBehalf !== undefined && onBehalfEmail !== undefined}
                                isClaimed={isClaimed}
                                getWaitlistAvailabilityText={getWaitlistAvailabilityText}
                                isShowFull={isShowFull}
                                event={event}
                                isVolunteer={isVolunteer}
                                volunteerLateRegAllowed={volunteerLateRegAllowed}
                                volunteerEarlyRegAllowed={volunteerEarlyRegAllowed}
                                isClubManager={isAdmin}
                              />
                            );
                          } else {
                            return(null);
                          }
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>}
              {loaded && currentStep === 1 && <div className="stepInner dogDetails">
                {orderedShowTypes.map((showType:string, showTypeIndex:number) => {
                  if (showTypeHasDogs(showType) > 0) {
                    const isShowTypeExpanded = expandedShowTypes.has(showTypeIndex);
                    return(
                      <div key={showTypeIndex} className={isShowTypeExpanded ? "showType expanded" : "showType"}>
                        <div onClick={() => { 
                          const newExpanded = new Set(expandedShowTypes);
                          if (isShowTypeExpanded) {
                            newExpanded.delete(showTypeIndex);
                          } else {
                            newExpanded.add(showTypeIndex);
                          }
                          setExpandedShowTypes(newExpanded);
                        }} className="title">
                          <div className="name">{showType}</div>
                          <div className="actions"><span className="hint">{isShowTypeExpanded ? "Collapse" : "Expand"}</span><div className="icon caretDown"></div></div>
                        </div>

                        {event && dogs.map((dog:any, dogIndex:number) => {
                          const showIsJoined = isJoinedShow(event.sanctioning_club, showType, dog._id);
                          
                          if (dogInShowType(dog, showType) && showIsJoined) {
                            let _show = getJoinedShow(dog, showType);
                            // Get the dog from the FIRST show in the shows state array (not from the copied _show)
                            let firstShowOfType = shows.find((s:IShowDogs) => s.show_type === showType && s.dogs.some((d: IDogInfo) => d._id === dog._id));
                            let _dog = firstShowOfType?.dogs.find((d: IDog) => d._id === dog._id);

                            return(
                              <div key={dogIndex} className="show">
                                {/* Per-dog "Use same selections" checkbox */}
                                {event.sanctioning_club === Constants.sanctioning_club.UKC && showType === Constants.show_type.conformation && (
                                  <div className="conformationJoinedCheckbox" style={{margin: '15px 20px 0 20px', padding: '10px', backgroundColor: '#FFF3CD', borderLeft: '4px solid #FFA500'}}>
                                    <Checkbox 
                                      id={`useJoinedConformation-${dog._id}`}
                                      onChange={(e) => { 
                                        // Update only this specific dog
                                        const newState: Record<string, boolean> = {};
                                        newState[dog._id] = e.target.checked;
                                        setUseJoinedConformation(prev => ({ ...prev, ...newState }));
                                        
                                        // When unchecking "use same for all" for this dog, copy first show's data to all shows
                                        if (!e.target.checked) {
                                          setTimeout(() => {
                                            let _shows:IShowDogs[] = JSON.parse(JSON.stringify(shows));
                                            
                                            // Find all shows of this type that have this dog
                                            const showsOfType = _shows.filter((s:IShowDogs) => 
                                              s.show_type === showType && 
                                              s.dogs.some((d: IDogInfo) => d._id === dog._id)
                                            );
                                            
                                            if (showsOfType.length > 0) {
                                              // Get the classes from the first show
                                              const firstShow = showsOfType[0];
                                              const firstShowDog = firstShow.dogs.find((d: IDogInfo) => d._id === dog._id);
                                              const classesToCopy = firstShowDog?.classes || [];
                                              
                                              // Copy classes from first show to all other shows of this type
                                              
                                              // Copy to all other shows, filtering each show's classes
                                              // to only those offered by that specific show's show_elements.
                                              showsOfType.forEach((show: IShowDogs) => {
                                                const dogInShow = show.dogs.find((d: IDogInfo) => d._id === dog._id);
                                                if (dogInShow) {
                                                  const targetElements: IShowElement[] = show.show_elements || [];
                                                  const filteredClasses = targetElements.length > 0
                                                    ? classesToCopy.filter((dc: IDogClass) => {
                                                        if (dc.show_element === Constants.dog_class_element.conformation_non_licensed) {
                                                          return targetElements.some((se: IShowElement) =>
                                                            (se.show_element === dc.show_element && se.level === dc.level) ||
                                                            se.show_element === dc.level ||
                                                            se.level === dc.level
                                                          );
                                                        }
                                                        return targetElements.some((se: IShowElement) =>
                                                          se.show_element === dc.show_element && (!se.level || se.level === dc.level)
                                                        );
                                                      })
                                                    : JSON.parse(JSON.stringify(classesToCopy));
                                                  dogInShow.classes = filteredClasses;
                                                }
                                              });
                                            }
                                            
                                            setShows(_shows);
                                          }, 0);
                                        }
                                      }} 
                                      value={useJoinedConformation[dog._id] !== false} 
                                      label={`Use same selections for all shows for ${dog.call_name}`} 
                                    />
                                  </div>
                                )}
                                
                                <div className="dog">
                                  <div className="dogDetails">
                                    <div className="dogPortraitSection">
                                      <DogPortrait dog={dog} dogCallName={getDogCallNameByClub(event.sanctioning_club, _dog)} sanctioningClub={event.sanctioning_club} show={_show} isAdmin={true} />
                                    </div>
                                    <div className="dogInfoSection">
                                      <div className="dogWaitlistSection">
                                        {getWaitlistStatus(_show._id, dog._id) && !lotteryDrawOpen && <><div className="waitlistTag">Waitlisted</div></>}
                                        {lotteryDrawOpen && getWaitlistStatus(_show._id, dog._id) && <span className="lotteryTag">Random Draw</span>}
                                      </div>
                                      {(() => {
                                        const existingChanges = getExistingChangesForEvent(dog, event._id);
                                        const clubRegInfo = getClubRegistrationInfo(dog, event.sanctioning_club, existingChanges);
                                        return renderDogInfoByShowType(showType, dog, clubRegInfo, existingChanges);
                                      })()}
                                    </div>
                                  </div>
                                  <div className="dogShowForm">
                                    {/* Debug: event={!!event} sanctioning={event?.sanctioning_club} showType={showType} handlerEnabled={handlerEnabled.includes(showType)} _dog={!!_dog} */}
                                    {event && event.sanctioning_club === Constants.sanctioning_club.AKC && showType === Constants.show_type.conformation && _dog !== undefined &&
                                      <div className="earnTitleWrap">
                                        <Checkbox disabled={_dog.using_non_agent} id={"usingAgent"+dog.call_name+_show._id} onChange={(e) => { toggleUsingAgentJoined(shows, _dog, showType); }} value={_dog.using_agent ? _dog.using_agent : false} label={"I'm using an agent handler for " + dog.call_name} />
                                      </div>
                                    }
                                    {event && event.sanctioning_club === Constants.sanctioning_club.AKC && showType === Constants.show_type.conformation && _dog !== undefined &&
                                      <div className="earnTitleWrap">
                                        <Checkbox disabled={_dog.using_agent} id={"usingNonAgent"+dog.call_name+_show._id} onChange={(e) => { toggleUsingNonAgentJoined(shows, _dog, showType); }} value={_dog.using_non_agent ? _dog.using_non_agent : false} label={"I'm using a non-agent handler for " + dog.call_name} />
                                      </div>
                                    }
                                    {event && event.sanctioning_club === Constants.sanctioning_club.AKC && showType === Constants.show_type.conformation && _dog !== undefined &&
                                      <div className="earnTitleWrap">
                                        <Checkbox disabled={_dog.using_agent || _dog.using_non_agent} id={"competingOwnerHandler"+dog.call_name+_show._id} onChange={(e) => { toggleCompetingOwnerHandlerJoined(shows, _dog, showType); }} value={_dog.competing_owner_handler ? _dog.competing_owner_handler : false} label={"Are you participating in Owner/Handler"} />
                                      </div>
                                    }
                                    {event && [Constants.sanctioning_club.UKC, Constants.sanctioning_club.BHA].includes(event.sanctioning_club) && handlerEnabled.includes(showType) && _dog !== undefined &&
                                      <div className="earnTitleWrap">
                                        <Checkbox id={"usingAgent"+dog.call_name+_show._id} onChange={(e) => { toggleUsingAgentJoined(shows, _dog, showType); }} value={_dog.using_agent ? _dog.using_agent : false} label={"Someone other than the owner is handling " + dog.call_name} />
                                      </div>
                                    }
                                    {event && event.sanctioning_club === Constants.sanctioning_club.CKC && ckcAgentShowTypes.includes(showType) && _dog !== undefined &&
                                      <>
                                        <div className="earnTitleWrap">
                                          <Checkbox id={"usingAgent"+dog.call_name+_show._id} onChange={(e) => { toggleUsingAgentJoined(shows, _dog, showType); }} value={_dog.using_agent ? _dog.using_agent : false} label={"I'm using an agent handler for " + dog.call_name} />
                                        </div>
                                        {showType === Constants.show_type.conformation &&
                                          <div className="earnTitleWrap">
                                            <Checkbox disabled={_dog.using_agent} id={"competingOwnerHandler"+dog.call_name+_show._id} onChange={(e) => { toggleCompetingOwnerHandlerJoined(shows, _dog, showType); }} value={_dog.competing_owner_handler ? _dog.competing_owner_handler : false} label={"Canadian National Owner Handled Series"} />
                                          </div>
                                        }
                                      </>
                                    }
                                    {_dog && _dog.using_agent &&
                                      <>
                                        <div className="agent">
                                          <Input required onChange={(e) => { updateAgentJoined(shows, _dog, showType, { name: e.target.value }); }} type="text" label={[Constants.sanctioning_club.UKC, Constants.sanctioning_club.BHA].includes(event.sanctioning_club) ? "Handler Name" : "Agent/Handler Name"} defaultValue={_dog.agent_name} placeholder={[Constants.sanctioning_club.UKC, Constants.sanctioning_club.BHA].includes(event.sanctioning_club) ? "Enter handler name" : "Enter your agent handler name"} />
                                        </div>
                                        {![Constants.sanctioning_club.UKC, Constants.sanctioning_club.BHA, Constants.sanctioning_club.CKC].includes(event.sanctioning_club) &&
                                          <div className="agent">
                                            <Input required onChange={(e) => { updateAgentJoined(shows, _dog, showType, { number: e.target.value }); }} type="text" label="Agent/Handler Number" defaultValue={_dog.agent_number} placeholder="Enter your agent handler number" />
                                          </div>
                                        }
                                        {event.sanctioning_club === Constants.sanctioning_club.CKC && <>
                                          <div className="agent">
                                            <Input onChange={(e) => { updateAgentJoined(shows, _dog, showType, { address: { line1: e.target.value } }); }} type="text" label="Agent Address (optional)" defaultValue={_dog.agent_address?.line1} placeholder="Street address" />
                                          </div>
                                          <div className="agent">
                                            <Input onChange={(e) => { updateAgentJoined(shows, _dog, showType, { address: { city: e.target.value } }); }} type="text" label="Agent City" defaultValue={_dog.agent_address?.city} placeholder="City" />
                                          </div>
                                          <div className="agent">
                                            <Input onChange={(e) => { updateAgentJoined(shows, _dog, showType, { address: { state: e.target.value } }); }} type="text" label="Agent Province" defaultValue={_dog.agent_address?.state} placeholder="Province" />
                                          </div>
                                          <div className="agent">
                                            <Input onChange={(e) => { updateAgentJoined(shows, _dog, showType, { address: { postal_code: e.target.value } }); }} type="text" label="Agent Postal Code" defaultValue={_dog.agent_address?.postal_code} placeholder="Postal code" />
                                          </div>
                                        </>}
                                      </>
                                    }
                                    {_dog && _dog.using_non_agent &&
                                      <div className="agent">
                                        <Input required onChange={(e) => { updateAgentJoined(shows, _dog, showType, { name: e.target.value }); }} type="text" label="Non-agent Handler Name" defaultValue={_dog.agent_name} placeholder="Enter your non-agent handler name" />
                                      </div>
                                    }
                                    <FormBuilder
                                      key={`${dog._id}-${_show._id}-${dogUpdateTrigger}`}
                                      isJunior={Boolean(user.is_junior) || onBehalfJunior === "true"}
                                      isClubManager={isAdmin}
                                      event={event}
                                      show={_show}
                                      dog={dog}
                                      classes={_dog?.classes || []}
                                      onChange={(classes: IDogClass[]) => {
                                        updateDogInfoAllShowTypes(_show.show_type, classes, dog);
                                      }}
                                      userDogs={dogs.filter((d) => d._id !== dog._id)}
                                      showDogs={shows[currentShow]}
                                      disabled={isShowFull(_show)}
                                      onAddDog={() => {
                                        setUsingTeamDogs(true);
                                        setModalContent("dogForm");
                                      }}
                                      onEditDog={(dog) => {
                                        // Just refresh the dog data, don't re-select
                                        // The dog is already selected in step 2, and handleDogUpdated preserves class selections
                                        handleDogUpdated();
                                      }}
                                      onRefreshDogData={(updated) => {
                                        if (updated?._id) freshDogRef.current = updated;
                                        handleDogUpdated();
                                      }}
                                      refreshTimeslots={timeslotRefreshTrigger}
                                      onTimeslotsLoaded={(showId, slots) => {
                                        setShowTimeslotMap(prev => ({ ...prev, [showId]: slots.length }));
                                        setShowTimeslotsData(prev => ({ ...prev, [showId]: slots }));
                                      }}
                                    />
                                  {!lotteryDrawOpen && getWaitlistStatus(_show._id, dog._id) && <div className="waitlistWarning">{getWaitlistMessage(_show._id, dog._id, dog.call_name, getWaitlistText(_show._id, dog._id))}</div>}
                                  {lotteryDrawOpen && getWaitlistStatus(_show._id, dog._id) && <div className="waitlistWarning lottery">{getWaitlistLotteryMessage(_show._id, dog._id, dog.call_name)}</div>}
                                  </div>
                                </div>
                              </div>
                            );
                          } else {
                            return(null);
                          }
                        })}
                        {event && shows.map((show:IShowDogs, showIndex:number) => {
                          if (show.dogs.length > 0 && show.show_type === showType) {
                            // Render unjoined dogs in this show
                            const unjoinedDogs = show.dogs.filter(dog => !isJoinedShow(event.sanctioning_club, showType, dog._id));
                            
                            if (unjoinedDogs.length === 0) {
                              return null;
                            }
                            
                            const timeZoneString = event.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
                            return(
                              <div key={showIndex} className="show">
                                <div className="showHeader">
                                  <div className="showInfo">
                                    {show.custom_show_name && (
                                      <div className="showName">{show.custom_show_name}</div>
                                    )}
                                    <div className="time">
                                      {show.trailing_show ? (
                                        <>
                                          {moment(show.show_date).tz(timeZoneString).format("dddd MMMM Do, YYYY")}
                                          {"  "}
                                          <span className="followsText">(follows {show.previous_show_name})</span>
                                        </>
                                      ) : (
                                        moment(show.show_date).tz(timeZoneString).format("dddd MMMM Do, YYYY @ h:mm A zz")
                                      )}
                                    </div>
                                    <div className="showEventNumber">
                                      {event.sanctioning_club === Constants.sanctioning_club.AKC && show.event_number
                                        ? `${show.show_type} (Event #: ${show.event_number})`
                                        : !show.custom_show_name
                                          ? `${show.show_name}${getPrecisionCourseName(show)}`
                                          : null
                                      }
                                    </div>
                                  </div>
                                </div>
                                {getShowAvailabilityMessage(show) && <div className="waitlistAvailability">{getShowAvailabilityMessage(show)}</div>}
                                <div className="dogList">
                                  {unjoinedDogs.map((dog:IDogInfo, dogIndex:number) => {
                                    // Find the actual index in show.dogs
                                    const actualDogIndex = show.dogs.findIndex(d => d._id === dog._id);
                                    
                                    return(
                                      <React.Fragment key={dog._id}>
                                        {/* Per-dog "Use same selections" checkbox for unjoined dogs */}
                                        {event.sanctioning_club === Constants.sanctioning_club.UKC && showType === Constants.show_type.conformation && (
                                          <div className="conformationJoinedCheckbox" style={{margin: '15px 20px 0 20px', padding: '10px', backgroundColor: '#FFF3CD', borderLeft: '4px solid #FFA500'}}>
                                            <Checkbox 
                                              id={`useJoinedConformation-unjoined-${dog._id}-${show._id}`}
                                              onChange={(e) => { 
                                                // Update only this specific dog
                                                const newState: Record<string, boolean> = {};
                                                newState[dog._id] = e.target.checked;
                                                setUseJoinedConformation(prev => ({ ...prev, ...newState }));
                                              }} 
                                              value={useJoinedConformation[dog._id] !== false} 
                                              label={`Use same selections for all shows for ${dog.call_name}`} 
                                            />
                                          </div>
                                        )}
                                        
                                        <div className="dog">
                                      <div className="dogDetails">
                                        <div className="dogPortraitSection">
                                          <DogPortrait edit={true} dog={dog} dogCallName={getDogCallNameByClub(event.sanctioning_club, dog)} sanctioningClub={event.sanctioning_club} show={show} isAdmin={true} onEditDog={handleDogUpdated} />
                                        </div>
                                        <div className="dogInfoSection">
                                          <div className="dogWaitlistSection">
                                            {getWaitlistStatus(show._id, dog._id) && !lotteryDrawOpen && <><div className="waitlistTag">Waitlisted</div></>}
                                            {lotteryDrawOpen && getWaitlistStatus(show._id, dog._id) && <span className="lotteryTag">Random Draw</span>}
                                          </div>
                                          {(() => {
                                            const existingChanges = getExistingChangesForEvent(dog, event._id);
                                            const clubRegInfo = getClubRegistrationInfo(dog, event.sanctioning_club, existingChanges);
                                            return renderDogInfoByShowType(showType, dog, clubRegInfo, existingChanges);
                                          })()}
                                        </div>
                                      </div>
                                      <div className="dogShowForm">
                                        {(showType === Constants.show_type.fast_cat || showType === Constants.show_type.fast_dash || showType === Constants.show_type.cat) &&
                                          <div className="earnTitleWrap">
                                            <div className="earnTitleInner">
                                              <Checkbox id={"earnTitle"+dog.call_name+show._id} onChange={() => { toggleEarnTitle(shows, actualDogIndex, showIndex); }} value={dog.plan_to_earn_title ? dog.plan_to_earn_title : false} label={dog.call_name + " is Planning to Earn a Title"} />
                                            </div>
                                          </div>
                                        }
                                        {dog.plan_to_earn_title &&
                                          <Dropdown className="titleDrop" value={getTitle(dog, showType)} label={"Title"} onChange={(_e, value: IOption) => { updateTitle(shows, actualDogIndex, value, showIndex, undefined); }} options={getTitleOptions(showType)} placeholder="" />
                                        }
                                        {dog.plan_to_earn_title && getTitle(dog, showType).id === Constants.akc_fastcat_titles.fcat &&
                                          <div style={{ maxWidth: "300px", marginBottom: "20px" }}>
                                            <Input type="number" label="FCAT Number (1–100)" min={1} max={100} defaultValue={getFcatNumber(dog).toString()} onBlur={(e: any) => { updateFcatNumber(shows, actualDogIndex, parseInt(e.target.value, 10), showIndex); }} />
                                          </div>
                                        }
                                        {event && [Constants.sanctioning_club.AKC, Constants.sanctioning_club.PWDCA, Constants.sanctioning_club.BHA].includes(event.sanctioning_club) && handlerEnabled.includes(showType) && showType !== Constants.show_type.agility && !!dog &&
                                          <div className="earnTitleWrap">
                                            <Checkbox disabled={dog.using_non_agent} id={"usingAgent"+dog.call_name+show._id} onChange={(e) => { toggleUsingAgentSingle(showIndex, actualDogIndex); }} value={dog.using_agent ? dog.using_agent : false} label={"I'm using an agent handler for " + dog.call_name} />
                                          </div>
                                        }
                                        {dog && event?.sanctioning_club === Constants.sanctioning_club.AKC && [
                                          Constants.show_type.conformation,
                                          Constants.show_type.herding,
                                          Constants.show_type.pre_trial_herding,
                                          Constants.show_type.instinct_test_herding,
                                          Constants.show_type.agility
                                        ].includes(showType) &&
                                          <div className="earnTitleWrap">
                                            <Checkbox disabled={dog.using_agent} id={"usingNonAgent"+dog.call_name+show._id} onChange={() => { toggleUsingNonAgentSingle(showIndex, actualDogIndex); }} value={dog.using_non_agent ? dog.using_non_agent : false} label={"I'm using a non-agent handler for " + dog.call_name} />
                                          </div>
                                        }
                                        {dog && event?.sanctioning_club === Constants.sanctioning_club.AKC && showType === Constants.show_type.conformation &&
                                          <div className="earnTitleWrap">
                                            <Checkbox disabled={dog.using_agent || dog.using_non_agent} id={"competingOwnerHandler"+dog.call_name+show._id} onChange={() => { toggleCompetingOwnerHandlerSingle(showIndex, actualDogIndex); }} value={dog.competing_owner_handler ? dog.competing_owner_handler : false} label={"Are you participating in Owner/Handler"} />
                                          </div>
                                        }
                                        {event && [Constants.sanctioning_club.UKC, Constants.sanctioning_club.BHA, Constants.sanctioning_club.PWDCA].includes(event.sanctioning_club) && handlerEnabled.includes(showType) && dog !== undefined &&
                                          <div className="earnTitleWrap">
                                            <Checkbox id={"usingAgent"+dog.call_name+show._id} onChange={(e) => { toggleUsingAgentSingle(showIndex, actualDogIndex); }} value={dog.using_agent ? dog.using_agent : false} label={"Someone other than the owner is handling " + dog.call_name} />
                                          </div>
                                        }
                                        {dog && event?.sanctioning_club === Constants.sanctioning_club.CKC && ckcAgentShowTypes.includes(showType) &&
                                          <>
                                            <div className="earnTitleWrap">
                                              <Checkbox id={"usingAgent"+dog.call_name+show._id} onChange={(e) => { toggleUsingAgentSingle(showIndex, actualDogIndex); }} value={dog.using_agent ? dog.using_agent : false} label={"I'm using an agent handler for " + dog.call_name} />
                                            </div>
                                            {showType === Constants.show_type.conformation &&
                                              <div className="earnTitleWrap">
                                                <Checkbox disabled={dog.using_agent} id={"competingOwnerHandler"+dog.call_name+show._id} onChange={() => { toggleCompetingOwnerHandlerSingle(showIndex, actualDogIndex); }} value={dog.competing_owner_handler ? dog.competing_owner_handler : false} label={"Canadian National Owner Handled Series"} />
                                              </div>
                                            }
                                          </>
                                        }
                                        {dog && dog.using_agent &&
                                          <>
                                            <div className="agent">
                                              <Input required onChange={(e) => { updateAgentSingle(showIndex, actualDogIndex, { name: e.target.value }); }} type="text" label={[Constants.sanctioning_club.UKC, Constants.sanctioning_club.BHA].includes(event.sanctioning_club) ? "Handler Name" : "Agent/Handler Name"} defaultValue={dog.agent_name} placeholder={[Constants.sanctioning_club.UKC, Constants.sanctioning_club.BHA].includes(event.sanctioning_club) ? "Enter handler name" : "Enter your agent handler name"} />
                                            </div>
                                            {![Constants.sanctioning_club.UKC, Constants.sanctioning_club.BHA, Constants.sanctioning_club.CKC].includes(event.sanctioning_club) &&
                                              <div className="agent">
                                                <Input required onChange={(e) => { updateAgentSingle(showIndex, actualDogIndex, { number: e.target.value }); }} type="text" label="Agent/Handler Number" defaultValue={dog.agent_number} placeholder="Enter your agent handler number" />
                                              </div>
                                            }
                                            {event.sanctioning_club === Constants.sanctioning_club.CKC && <>
                                              <div className="agent">
                                                <Input onChange={(e) => { updateAgentSingle(showIndex, actualDogIndex, { address: { line1: e.target.value } }); }} type="text" label="Agent Address (optional)" defaultValue={dog.agent_address?.line1} placeholder="Street address" />
                                              </div>
                                              <div className="agent">
                                                <Input onChange={(e) => { updateAgentSingle(showIndex, actualDogIndex, { address: { city: e.target.value } }); }} type="text" label="Agent City" defaultValue={dog.agent_address?.city} placeholder="City" />
                                              </div>
                                              <div className="agent">
                                                <Input onChange={(e) => { updateAgentSingle(showIndex, actualDogIndex, { address: { state: e.target.value } }); }} type="text" label="Agent Province" defaultValue={dog.agent_address?.state} placeholder="Province" />
                                              </div>
                                              <div className="agent">
                                                <Input onChange={(e) => { updateAgentSingle(showIndex, actualDogIndex, { address: { postal_code: e.target.value } }); }} type="text" label="Agent Postal Code" defaultValue={dog.agent_address?.postal_code} placeholder="Postal code" />
                                              </div>
                                            </>}

                                            {/* NOTE: PWDCA Water dog specific agent inputs */}
                                            {event && event.sanctioning_club === Constants.sanctioning_club.PWDCA && dog && dog.using_agent && <>
                                              <div className="agent">
                                                <Input required onChange={(e) => { updateAgentJoined(shows, dog, showType, { email: e.target.value }); }} type="text" icon={emailIcon} label="Agent Email" placeholder="Agent Email" />
                                              </div>
                                              <div className="agent">
                                                <Phone required onChange={(value: string) => { updateAgentJoined(shows, dog, showType, { phone: value }); }} value={dog.agent_phone} label="Agent Phone Number" />
                                              </div>
                                            </>}
                                          </>
                                        }
                                        {dog && dog.using_non_agent &&
                                          <div className="agent">
                                            <Input required onChange={(e) => { updateAgentSingle(showIndex, actualDogIndex, { name: e.target.value }); }} type="text" label="Non-agent Handler Name" defaultValue={dog.agent_name} placeholder="Enter your non-agent handler name" />
                                          </div>
                                        }

                                        {/* NOTE: PWDCA Water dog substitute mandatory information */}
                                        {event && event.sanctioning_club === Constants.sanctioning_club.PWDCA && dog && <>
                                          <div className="substituteWrapper">
                                            <div className="substituteTitle">Substitute information</div>
                                            <div className="agent">
                                              <Input required onChange={(e) => { updateSubstituteJoined(shows, dog, showType, { name: e.target.value }); }} type="text" label="Substitue Steward Name" defaultValue={dog.substitute_name} placeholder="Substitue name" />
                                            </div>
                                            <div className="agent">
                                              <Input required onChange={(e) => { updateSubstituteJoined(shows, dog, showType, { email: e.target.value }); }} type="email" icon={emailIcon} label="Substitue Steward Email" defaultValue={dog.substitute_email} placeholder="Substitue Email" />
                                            </div>
                                            <div className="agent">
                                              <Phone required onChange={(value: string) => { updateSubstituteJoined(shows, dog, showType, { phone: value }); }} value={dog.substitute_phone} label="Substitue Steward Phone Number" />
                                            </div>
                                          </div>
                                        </>}

                                        {event && event.sanctioning_club === Constants.sanctioning_club.AKC && showType === Constants.show_type.agility && <div style={{ marginBottom: 30 }}>
                                            <Checkbox id={`dog-need-measure-${dog._id}-${show._id}`} label="Does this dog need to be measured at trials?" value={!!(dog?.needs_measurement ?? getClubRegistrationInfo(dog, Constants.sanctioning_club.AKC)?.needs_measurement)} onChange={() => { toggleMeasurementSingle(shows, dog, showType); }} />
                                        </div>}

                                        {event && event.sanctioning_club === Constants.sanctioning_club.C_WAGS && showType === Constants.show_type.scent && <div style={{ marginBottom: 30 }}>
                                            <Dropdown id={`dog-wheelchair-${dog._id}-${show._id}`} label="Handler using wheelchair" options={dogWheelchairOptions} value={dogWheelchairOptions.find((wo) => wo.id === dog.wheelchair_type) ?? dogWheelchairOptions[0]} onChange={(_e, value: IOption) => { updateDogWheelchairType(shows, dog, showType, value.id) }} placeholder="" />
                                        </div>}

                                        <FormBuilder
                                          key={`${dog._id}-${show._id}-${dogUpdateTrigger}`}
                                          isJunior={Boolean(user.is_junior) || onBehalfJunior === "true"}
                                          isClubManager={isAdmin}
                                          event={event ?? undefined}
                                          show={show}
                                          dog={dog}
                                          classes={dog.classes || []}
                                          onChange={(classes: IDogClass[]) => {
                                            updateDogInfo(showIndex, actualDogIndex, classes); }}
                                          userDogs={dogs.filter((d) => d._id !== dog._id)}
                                          showDogs={shows[currentShow]}
                                          disabled={isShowFull(show)}
                                          waitlistedClasses={getWaitlistedClassesForDog(show._id, dog._id)}
                                          onAddDog={() => {
                                            setUsingTeamDogs(true);
                                            setModalContent("dogForm");
                                          }}
                                          onEditDog={(dog) => {
                                            // Just refresh the dog data, don't re-select
                                            // The dog is already selected in step 2, and handleDogUpdated preserves class selections
                                            handleDogUpdated();
                                          }}
                                          onRefreshDogData={(updated) => {
                                            if (updated?._id) freshDogRef.current = updated;
                                            handleDogUpdated();
                                          }}
                                          refreshTimeslots={timeslotRefreshTrigger}
                                          onTimeslotsLoaded={(showId, slots) => {
                                            setShowTimeslotMap(prev => ({ ...prev, [showId]: slots.length }));
                                            setShowTimeslotsData(prev => ({ ...prev, [showId]: slots }));
                                          }}
                                        />
                                        {event && event.sanctioning_club === Constants.sanctioning_club.PWDCA && dog && !!dog.classes?.length &&
                                          [
                                            Constants.dog_class_element.working,
                                            Constants.dog_class_element.courier,
                                            Constants.dog_class_element.master
                                          ].includes(dog.classes?.[0]?.level) &&
                                          <Dropdown
                                            value={workingOrCourierOptions[0]}
                                            label="What leg are you working on?"
                                            onChange={(_e, option: IOption) => { updateDogLegInfo(shows, dog, showType, option.value); }}
                                            options={getWorkingLegOptions(dog.classes ?? [])}
                                            placeholder=""
                                          />
                                        }

                                      </div>
                                    </div>
                                      </React.Fragment>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          } else {
                            return(null);
                          }
                        })}
                      </div>
                    );
                  } else {
                    return(null);
                  }
                })}
              </div>}
              {loaded && currentStep === 2 && <div className="stepInner waiver">
                <div className="waiverInner">
                  <div className="waiverTitle">{getWaiverTitle(event ? event.sanctioning_club : "")}</div>
                  {getWaiverText(event ? event.sanctioning_club : "")}
                </div>
                <div className="waiverSign">
                  <div className="checkContainer">
                    <Checkbox onChange={(e) => { setAgreeToWaiver(!agreeToWaiver); }} value={agreeToWaiver} id="waiverSign" label={getSignoffText(event ? event.sanctioning_club : "")} />
                  </div>
                  <div className="signature"></div>
                </div>
              </div>}
              {loaded && currentStep === 3 && <div className="stepInner eventWaiver">
                <div className="eventWaiverInner">
                  <div className="eventWaiverTitle">Event Waiver</div>
                  <div dangerouslySetInnerHTML={{__html:event.waiver}} />
                </div>
                <div className="eventWaiverSign">
                  <div className="checkContainer">
                    <Checkbox onChange={(e) => { setAgreeToEventWaiver(!agreeToEventWaiver); }} value={agreeToEventWaiver} id="waiverSign" label={getSignoffTextEventWaiver()}/>
                  </div>
                  <div className="signature"></div>
                </div>
              </div>}
              {loaded && currentStep === 4 && <div className="stepInner addons">
                <div className="addonsDescription">
                  <p>These are optional <b>Add-ons</b> that can be purchased while registering for this event</p>
                </div>
                {addons.map((addon: IAddonUI, i: number) => {
                  const hasAvailabilityLimit =
                    addon.available_quantity !== undefined &&
                    addon.available_quantity !== null;
                  const remainingQuantity = hasAvailabilityLimit
                    ? Math.max(0, Number(addon.available_quantity) - (addon.purchased_quantity || 0))
                    : Number.MAX_SAFE_INTEGER;
                  const isSoldOut = hasAvailabilityLimit && remainingQuantity === 0;
                  return(
                    <div key={i} className={isSoldOut ? "addon unavailable" : "addon"}>
                      <div className="addonDetails">
                        {hasAvailabilityLimit && <div className="addonAvailability">
                          {isSoldOut ? (
                            <span className="unavailable">Sold Out</span>
                          ) : (
                            <>
                              <span className="available">{remainingQuantity}</span> <span className="unavailable">/ {addon.available_quantity} items available</span>
                            </>
                          )}
                        </div>}
                        <div className="addonTitle">{addon.title}<span className="addonFee">{formatter.format(addon.fee)}</span></div>
                        <div className="addonDescription">{addon.description ? addon.description : "No Description Available"}</div>
                      </div>
                      <div className="addonActions">
                        <div className="decrementAddon">
                          <Button
                            disabled={addon.amount === 0}
                            onClick={() => {
                              setAddons(addons.map((a) => {
                                if (a._id === addon._id) {
                                  return { ...a, amount: a.amount ? a.amount - 1 : 0 };
                                }
                                return a;
                              }));
                            }}
                          >-</Button>
                        </div>
                        <Input
                          type="number"
                          className="addonQuantity"
                          min={0}
                          max={hasAvailabilityLimit ? remainingQuantity : undefined}
                          value={addon.amount || 0}
                          onChange={(e) => {
                            let quantity = parseInt(e.target.value);

                            if (e.target.value.length === 0) {
                              quantity = 0;
                            }

                            if (hasAvailabilityLimit) {
                              quantity = Math.min(quantity, remainingQuantity);
                            }

                            if (quantity >= 0 && (!hasAvailabilityLimit || quantity <= remainingQuantity)) {
                              setAddons(addons.map((a) => {
                                if (a._id === addon._id) {
                                  return { ...a, amount: quantity };
                                }
                                return a;
                              }));
                            }
                          }}
                          label="Quantity"
                        />
                        <div className="incrementAddon">
                          <Button
                            disabled={hasAvailabilityLimit ? addon.amount === remainingQuantity : false}
                            onClick={() => {
                              setAddons(addons.map((a) => {
                                if (a._id === addon._id) {
                                  return { ...a, amount: a.amount ? a.amount + 1 : 1 };
                                }
                                return a;
                              }));
                            }}
                          >+</Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>}
              {loaded && isPaymentStep() && <div className="stepInner payment">
                <div className="paymentWrap">
                  <div className="title">Account Owner Information</div>
                  <div className="subtitle">This information will be used for entry forms unless another Owner has been specified on a dog's profile.</div>
                  <UserAddress
                    onBehalf={onBehalf !== undefined && onBehalfEmail !== undefined}
                    onBehalfUserId={onBehalf}
                    user={user}
                    onChange={(address: IAddress, phone: string) => {
                      setUserAddress(address);
                      setUserPhone(phone);
                    }
                  } />

                  {/* Mandate notice + card-on-file section for AKC waitlisted entries */}
                  {event?.sanctioning_club === Constants.sanctioning_club.AKC && waitlistsPaymentElements.length > 0 && onBehalf === undefined && <>
                    <MandateNotice />

                    {/* Card on file: show card details + edit */}
                    {cardOnFileMethods !== null && cardOnFileMethods.length > 0 && stripeCustomerId && (
                      <CardOnFile
                        paymentMethod={cardOnFileMethods[0]}
                        customerId={stripeCustomerId}
                        onCardUpdated={(methods) => {
                          setCardOnFileMethods(methods);
                          if (methods.length > 0) {
                            toast.show({ title: "Payment Method", content: "Card updated successfully", duration: 4000, type: "success" });
                          }
                        }}
                      />
                    )}

                    {/* No card yet — show add card form */}
                    {cardOnFileMethods !== null && cardOnFileMethods.length === 0 && stripeCustomerId && (
                      <div className="akcAddCardGate">
                        <div className="akcAddCardGate__title">Payment Method Required</div>
                        <p className="akcAddCardGate__subtitle">
                          {lotteryDrawOpen
                            ? "AKC rules require a card on file for random draw entries. Your card will not be charged automatically — you will only be charged if your entry is selected in the draw and you confirm payment."
                            : "AKC rules require a card on file for waitlisted entries. Your card will not be charged automatically — you will only be charged once you are moved off the waitlist."}
                        </p>
                        <Elements stripe={standaloneStripePromise}>
                          <AddUserPaymentMethod
                            customerId={stripeCustomerId}
                            hideTitle
                            onSave={async () => {
                              const methods = await apiService.getSelfPaymentMethods();
                              setCardOnFileMethods(methods);
                              if (methods.length > 0) {
                                toast.show({ title: "Payment Method", content: lotteryDrawOpen ? "Card saved. You won't be charged until after the random draw is run and you've confirmed your entries." : "Card saved. You won't be charged until you're off the waitlist and have confirmed the amount.", duration: 6000, type: "success" });
                                // Initial registration call may have failed due to missing card — retry now
                                if (!transactionId && !stripeOptions) {
                                  await initRegistrationPaymentIntent();
                                }
                              }
                            }}
                          />
                        </Elements>
                      </div>
                    )}
                  </>}

                  {/* All-waitlisted submit: no payment due now, just confirm registration */}
                  {onBehalf === undefined && onBehalfEmail === undefined && !lotteryDrawOpen &&
                   waitlistsPaymentElements.length > 0 && (paymentSummary?.total_amount ?? 0) === 0 &&
                   transactionId !== undefined && (
                    <div className="freeActions">
                      <Button onClick={() => askAboutPoints(paymentSummary.base_amount || 0, (paymentSummary.club_points_redeemed || 0) > 0, () => registerForFree())}>Submit Registration(s)</Button>
                    </div>
                  )}

                  {/* Payment method toggle — hidden when all entries are waitlisted (nothing due now) */}
                  {onBehalf === undefined && onBehalfEmail === undefined && !lotteryDrawOpen && !(waitlistsPaymentElements.length > 0 && (paymentSummary?.total_amount ?? 0) === 0) && (
                    <div className="paymentChoiceToggle">
                      <div className="paymentChoiceTitle">How would you like to pay?</div>
                      <div
                        className="paymentChoiceOption"
                        onClick={() => {
                          setPaymentChoice('card');
                          setStripeOptions(savedStripeOptions);
                          setTransactionId(savedTransactionId);
                          setPaymentIntentId(savedPaymentIntentId);
                          setIsPayClubDirectlyTransaction(false);
                          updatePaymentSummary(currentDiscountCode, addons, undefined, 'card');
                        }}
                      >
                        <div className={`icon ${paymentChoice === 'card' ? 'radioFilled' : 'radioEmpty'}`}></div>
                        <span>Credit Card <span className="paymentChoiceAmount">({perMethodTotals['card'] != null ? formatter.format(perMethodTotals['card']) : formatter.format(paymentSummary?.total_amount || 0)})</span></span>
                      </div>
                      {waitlistsPaymentElements.length === 0 && (
                        <div
                          className="paymentChoiceOption"
                          onClick={async () => {
                            setPaymentChoice('bank_account');
                            updatePaymentSummary(currentDiscountCode, addons, undefined, 'bank_account');
                            // Reuse the cached bank transaction/PI if we already created one
                            // this session to avoid creating duplicate pending transactions
                            if (savedBankTransactionId && savedBankStripeOptions) {
                              setStripeOptions(savedBankStripeOptions);
                              setTransactionId(savedBankTransactionId);
                            } else {
                              setStripeOptions(undefined);
                              setTransactionId(undefined);
                              // Pass type directly — paymentChoice state won't update before the callback runs
                              await initRegistrationPaymentIntent('bank_account');
                            }
                          }}
                        >
                          <div className={`icon ${paymentChoice === 'bank_account' ? 'radioFilled' : 'radioEmpty'}`}></div>
                          <span>Bank Account <span className="paymentChoiceAmount">({perMethodTotals['bank_account'] != null ? formatter.format(perMethodTotals['bank_account']) : '...'})</span></span>
                        </div>
                      )}
                      {isPayClubDirectlyEligible && (
                        <div
                          className="paymentChoiceOption"
                          onClick={() => {
                            setPaymentChoice('pay_club_directly');
                            setStripeOptions(undefined);
                            setTransactionId(undefined);
                            setIsPayClubDirectlyTransaction(false);
                          }}
                        >
                          <div className={`icon ${paymentChoice === 'pay_club_directly' ? 'radioFilled' : 'radioEmpty'}`}></div>
                          <span>Pay Club Directly <span className="paymentChoiceAmount">({formatter.format(paymentSummary?.base_amount || 0)})</span></span>
                          <div className="paymentChoiceHint">Pay the club directly (cash, cheque, or e-transfer). <strong>Your entry is not confirmed until the club confirms your payment.</strong></div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Pay Club Directly submit */}
                  {isPayClubDirectlyEligible && paymentChoice === 'pay_club_directly' && onBehalf === undefined && onBehalfEmail === undefined && !lotteryDrawOpen && (
                    <div className="freeActions">
                      <Button isLoading={payClubDirectlyLoading} onClick={() => askAboutPoints(paymentSummary.base_amount || 0, (paymentSummary.club_points_redeemed || 0) > 0, () => { void registerPayClubDirectly(); }, () => registerForFree())}>Submit Registration(s)</Button>
                    </div>
                  )}

                  {onBehalf === undefined && onBehalfEmail === undefined && (!isPayClubDirectlyEligible || paymentChoice === 'card' || paymentChoice === 'bank_account') && stripeOptions !== undefined && stripePromise !== undefined && !lotteryDrawOpen &&
                    /* For AKC waitlist, only show Checkout once a payment method is confirmed */
                    (event?.sanctioning_club !== Constants.sanctioning_club.AKC || waitlistsPaymentElements.length === 0 || (cardOnFileMethods !== null && cardOnFileMethods.length > 0)) &&
                    <Elements key={stripeOptions?.clientSecret} stripe={stripePromise} options={stripeOptions}>
                      <Checkout
                        redirect={Config.web.REACT_APP_WEB_URL + getRedirectUrl(`/e/${eid}`, `dids=${getDogIds()}&waitlist=${checkIfWaitlist()}`)}
                        userAddress={userAddress}
                        userPhone={userPhone}
                        chargeParams={chargeParams}
                        bindPay={(pay) => { payRef.current = pay; }}
                        pointsGuard={() => {
                          if (!pointsReminder.shouldAsk(paymentSummary.base_amount || 0, (paymentSummary.club_points_redeemed || 0) > 0)) {
                            return false;
                          }
                          pointsReminder.ask({
                            redeemableAmount: paymentSummary.base_amount || 0,
                            onApply: (points) => updatePaymentSummary(currentDiscountCode, addons, points),
                            onContinue: () => payRef.current(),
                            onFree: () => registerForFree(),
                          });
                          return true;
                        }}
                      />
                    </Elements>
                  }
                  {onBehalf === undefined && onBehalfEmail === undefined && (!isPayClubDirectlyEligible || paymentChoice === 'card' || paymentChoice === 'bank_account') && stripeOptions === undefined && transactionId !== undefined &&
                    !(waitlistsPaymentElements.length > 0 && (paymentSummary?.total_amount ?? 0) === 0) &&
                    /* For AKC waitlist (free checkout path), block submit until card confirmed */
                    (event?.sanctioning_club !== Constants.sanctioning_club.AKC || waitlistsPaymentElements.length === 0 || (cardOnFileMethods !== null && cardOnFileMethods.length > 0)) && <>
                    <div className="freeActions">
                      <Button onClick={() => askAboutPoints(paymentSummary.base_amount || 0, (paymentSummary.club_points_redeemed || 0) > 0, () => { lotteryDrawOpen ? toggleRandomDrawConfirm() : registerForFree(); })}>{lotteryDrawOpen && event.sanctioning_club === Constants.sanctioning_club.PWDCA ? "Register For Random Draw" : "Submit Registration(s)"} </Button>
                    </div>
                  </>}
                  {onBehalf !== undefined && onBehalfEmail !== undefined && (lotteryDrawOpen || (stripeOptions !== undefined && stripePromise !== undefined)) && <div className="behalfWrap">
                    {/*clubPaymentMethods.length > 0 && */<div className="clubHasCard">
                      <Dropdown value={onBehalfPaymentMethod} label={"Payment Method"} onChange={(e, value: IOption) => { setOnBehalfPaymentMethod(value); }} options={onBehalfPaymentMethods} placeholder="" />
                      {/*<div className="title">Payment Information</div>
                      <RadioList
                        list={clubPaymentMethods}
                        selected={selectedPaymentMethod}
                        onSelect={(index: number) => { setSelectedPaymentMethod(index); }}
                      />*/}
                      {onBehalfPaymentMethod.id === Constants.payment_method_type.cheque &&
                        <Input required label="Cheque Number" type="text" onChange={(e) => { e.stopPropagation(); setChequeNumber(e.target.value); }} defaultValue={chequeNumber} />
                      }
                      <div className="actions">
                        <Button isLoading={loadingBehalf} onClick={() => { registerOnBehalf(); }}>Register User</Button>
                      </div>
                    </div>}
                    {/*clubPaymentMethods.length === 0 && <>
                      <Elements stripe={stripePromise} options={stripeOptions}>
                        <Checkout
                          onBehalf={true}
                          onAddCard={() => {
                            if (event) {
                              getClubCards(event?.club_id);
                            }
                          }}
                          returnUrl={Config.web.REACT_APP_WEB_URL + getRedirectUrl(`/e/${eid}/register`)}
                          redirect={Config.web.REACT_APP_WEB_URL + getRedirectUrl(`/e/${eid}`, `dids=${getDogIds()}`)}
                        />
                      </Elements>
                    </>*/}
                  </div>}
                </div>
              </div>}
            </div>
            <div className={currentStep === 0 ? "actions": "actions two"}>
              {currentStep !== 0 && !isPaymentStep() && <button onClick={() => { previous(); }}>Previous</button>}
              {(currentStep < steps.length - 1) && <button className="joy" onClick={() => { next(); }}>Next</button>}
            </div>
          </div>
        </div>
        {currentStep !== 0 &&
          <Cart
            onApplyDiscount={isPaymentStep() ? (discount: string) => { updatePaymentSummary(discount)} : undefined }
            onRedeemPoints={isPaymentStep() ? (points: number) => { handleRedeemPoints(points)} : undefined }
            eventId={event?._id}
            clubId={event?.club_id}
            shows={shows}
            paymentSummary={paymentSummary}
            isLottery={lotteryDrawOpen}
            behalfUserId={onBehalf}
            sanctioningClub={event?.sanctioning_club}
            showFees={isPaymentStep() && (paymentChoice === 'card' || paymentChoice === 'bank_account') && event?.club?.platform_fees_by !== Constants.platform_fees_by.club}
            isFinalStep={isPaymentStep()}
            clubAbsorbsFees={event?.club?.platform_fees_by === Constants.platform_fees_by.club}
            allEntriesWaitlisted={isPaymentStep() && waitlistsPaymentElements.length > 0 && (paymentSummary?.total_amount ?? 0) === 0}
            previewPaymentSummary={previewPaymentSummary}
            feeOverrides={onBehalf !== undefined ? feeOverrides : undefined}
            originalPrices={onBehalf !== undefined ? originalPrices : undefined}
            onFeeOverride={onBehalf !== undefined ? (key: string, amountDollars: number, originalFee: number) => {
              setFeeOverrides(prev => ({ ...prev, [key]: amountDollars }));
              setOriginalPrices(prev => prev[key] !== undefined ? prev : { ...prev, [key]: originalFee });
            } : undefined}
            onClearOverride={onBehalf !== undefined ? (key: string) => {
              setFeeOverrides(prev => { const next = { ...prev }; delete next[key]; return next; });
              setOriginalPrices(prev => { const next = { ...prev }; delete next[key]; return next; });
            } : undefined}
            overrideNote={onBehalf !== undefined ? overrideNote : undefined}
            onOverrideNoteChange={onBehalf !== undefined ? setOverrideNote : undefined}
          />}
      </div>
      <Modal
        className={modalContent}
        isShown={isShown}
        hide={toggle}
        modalContent={getModalContent()}
        scrollToTopOnCloseRef={scrollToTopOnModalCloseRef}
      />
      <Modal
        className="randomDrawConfirm"
        isShown={isRandomDrawConfirmShown}
        hide={toggleRandomDrawConfirm}
        modalContent={
          <div className="randomDrawConfirmContent">
            <h2>How Random Draw Works</h2>
            <p>Your entries will be submitted and held until after the random draw is run — <strong>you won't be charged today</strong>.</p>
            <p>This protects you from paying processing and platform fees on entries that don't make it into the event.</p>
            <p>A valid payment method is required alongside your entries. Once the draw is run, you'll receive an email with the outcome of each entry. If your entry is selected, <strong>the payment method on file will be automatically charged</strong>. If the charge is unsuccessful, you'll receive a payment link to complete your registration.</p>
            <div className="randomDrawConfirmActions">
              <Button variant="secondary" onClick={toggleRandomDrawConfirm}>Cancel</Button>
              <Button onClick={() => { toggleRandomDrawConfirm(); registerForFree(); }}>Confirm & Submit</Button>
            </div>
          </div>
        }
      />
      {pointsReminder.modal}
      {missingFieldsRequest && (
        <MissingFieldsModal
          context={missingFieldsRequest.context}
          fields={missingFieldsRequest.fields}
          levels={missingFieldsRequest.levels}
          isAdmin={isAdmin}
          eventId={event?._id}
          onSaved={missingFieldsRequest.onSaved}
          onProgress={() => { handleDogUpdated(); }}
          onCancel={() => setMissingFieldsRequest(null)}
        />
      )}
    </div>
  );
};

export default EventRegister;